import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';

const I18nContext = createContext({
  lang: 'en',
  setLanguage: () => {},
  t: (key, fallback, vars) => fallback || key,
  getLocale: () => 'en-GB'
});

// Simple cookie helpers (aligned with app's existing pattern)
const setCookie = (name, value, days = 365) => {
  try {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
  } catch (_) {}
};

const getCookie = (name) => {
  try {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  } catch (_) {
    return null;
  }
};

const UI_LANGUAGE_COOKIE = 'uiLanguage';

const getBrowserLanguage = () => {
  try {
    const langs = (navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language || navigator.userLanguage]).map(l => String(l || '').toLowerCase());
    if (langs.some(l => l.startsWith('en'))) return 'en';
    if (langs.some(l => l.startsWith('de'))) return 'de';
    if (langs.some(l => l.startsWith('es'))) return 'es';
  } catch (_) {}
  return 'en';
};

const translations = {
  de: {
    'dayCalendar.title': 'Eventtag',
    'filters.keywordFilter': 'Schlagwort-Filter',
    'filters.keywordFilterPlaceholder': 'Ein Schlagwort pro Zeile eingeben...\nz.B.:\nBug\nFeature\nSupport',
    'filters.keywordFilterHelp': 'Tickets, die diese Schlagwörter im Titel, Beschreibung oder Kategorie enthalten, werden herausgefiltert. Ein Schlagwort pro Zeile eingeben.',
    'filters.activeFilters': 'Aktive Filter:',
    'filters.keywordsCount': '{{count}} Schlagwort(e) werden gefiltert',
    'filters.noneActive': 'Keine Filter aktiv',
    'filters.hideReadEmails': 'Gelesene E-Mails ausblenden',
    'filters.hideReadEmailsHelp': 'Wenn aktiviert, werden bereits gelesene E-Mails in der Liste nicht angezeigt.',
    'filters.onlyLastHours': 'Nur E-Mails aus den letzten Stunden anzeigen',
    'filters.numHours': 'Anzahl Stunden:',
    'filters.onlyLastHoursHelp': 'Zeigt nur E-Mails aus den letzten {{hours}} Stunden an.',
    'filters.keywordEmailHelp': 'E-Mails, die diese Schlagwörter im Absender oder Betreff enthalten, werden herausgefiltert. Ein Schlagwort pro Zeile eingeben.',
    'filters.hideReadEmailsActive': 'Gelesene E-Mails werden ausgeblendet',
    'filters.onlyLastHoursActive': 'Nur E-Mails aus den letzten {{hours}} Stunden',
    'projectWizard.step1.title': 'Neuen Vorgang erstellen',
    'projectWizard.step1.editTitle': 'Vorgang überarbeiten',
    'projectWizard.step2.title': 'Teilvorgänge definieren',
    'projectWizard.step3.title': 'Zeitblöcke definieren',
    'projectWizard.labels.projectName': 'Vorgangsname',
    'projectWizard.labels.projectDescription': 'Vorgangsbeschreibung',
    'projectWizard.labels.projectResponsible': 'Vorgangsverantwortlicher',
    'projectWizard.labels.subprojects': 'Teilvorgänge',
    'projectWizard.placeholders.projectName': 'Vorgangsname',
    'projectWizard.placeholders.description': 'Beschreibung',
    'projectWizard.placeholders.assignedTo': 'Zugewiesen an...',
    'projectWizard.options.none': 'Keiner',
    'projectWizard.options.no': 'Ohne',
    'projectWizard.options.with': 'Mit',
    'projectWizard.buttons.next': 'Weiter',
    'projectWizard.buttons.back': 'Zurück',
    'projectWizard.buttons.finish': 'Fertig',
    'projectWizard.buttons.create': 'Anlegen',
    'projectWizard.buttons.saveChanges': 'Änderungen speichern',
    'projectWizard.buttons.addSubproject': 'Weiterer Teilvorgang',
    'projectWizard.buttons.addAnotherTimeblock': 'Weiterer Zeitblock',
    'projectWizard.buttons.configureTimeblocks': 'Zeitblöcke konfigurieren',
    'projectWizard.buttons.deleteSubproject': 'Teilvorgang löschen',
    'projectWizard.buttons.deleteProject': 'Vorgang löschen',
    'projectWizard.buttons.addTimeblock': 'Zeitblock',
    'projectWizard.buttons.addTimeblockFor': 'Zeitblock für {{name}}',
    'projectWizard.subproject.title': 'Teilvorgang Titel',
    'projectWizard.subproject.namePlaceholder': 'Teilvorgangsname',
    'projectWizard.subproject.description': 'Teilvorgang Beschreibung',
    'projectWizard.subproject.responsibleRequired': 'Verantwortlicher (erforderlich)',
    'projectWizard.timeblock.namePlaceholder': 'ZEITBLOCKNAME',
    'projectWizard.timeblock.titleLabel': 'Zeitblock Titel',
    'projectWizard.timeblock.titlePlaceholder': 'Zeitblock Titel',
    'projectWizard.timeblock.descriptionLabel': 'Zeitblock Beschreibung',
    'projectWizard.timeblock.delete': 'Zeitblock löschen',
    'projectWizard.hints.titleSavedAs': 'Titel wird als "{{project}} - ZEITBLOCKNAME" gespeichert.',
    'projectWizard.validation.selectAssigneeForAllBlocks': 'Bitte wählen Sie für alle definierten Zeitblöcke ein "Zugewiesen an" aus.',
    'projectWizard.confirm.deleteSubprojectAndTickets': 'Diesen Teilvorgang und alle dazugehörigen Tickets endgültig löschen?',
    'projectWizard.confirm.deleteProject': 'Vorgang und alle zugehörigen Teilvorgänge sowie Tickets endgültig löschen?',
    'projectWizard.errors.deleteSubproject': 'Teilvorgang konnte nicht gelöscht werden.',
    'projectWizard.errors.deleteSubprojectPartial': 'Teilvorgang nicht gelöscht: {count} Ticket(s) konnten nicht entfernt werden.',
    'projectWizard.confirm.deleteTimeblock': 'Diesen Zeitblock löschen?',
    'projectWizard.info.subprojectsToggleLocked': 'Der Teilvorgang-Umschalter ist bei bestehenden Vorgängen gesperrt.',
    'projectWizard.templates.title': 'Bibliothek',
    'projectWizard.templates.loading': 'Lade Bibliothek...',
    'projectWizard.templates.none': 'Keine Einträge in der Bibliothek',
    'projectWizard.templates.searchPlaceholder': 'Bibliothek durchsuchen...',
    'projectWizard.turnus.count': 'x mal',
    'projectWizard.turnus.every': 'alle x',
    'projectWizard.turnus.days': 'Tage',
    'projectWizard.turnus.weeks': 'Wochen',
    'projectWizard.turnus.months': 'Monate',
    'projectWizard.turnus.years': 'Jahre',
    'projectWizard.turnus.hint': 'Angabe optional: z. B. "1 mal alle 1 Monate".',
    'projectWizard.turnus.heading': 'Turnus',
    'projectWizard.templates.apply': 'Anwenden',
    'projectWizard.templates.delete': 'Löschen',
    'projectWizard.templates.confirmDelete': 'Diese Vorlage wirklich löschen?',
    'projectWizard.templates.deleteError': 'Fehler beim Löschen der Vorlage. Details in der Konsole.',
    'projectWizard.templates.hasActiveProjects': 'Diese Vorlage kann nicht gelöscht werden, da {{count}} aktive Turnus-Projekte damit verknüpft sind.',
    'projectWizard.templates.skipTurnus': 'Aussetzen',
    'projectWizard.templates.skippedOnce': 'Zeitraum ausgesetzt',
    'projectWizard.templates.fulfilledThisPeriod': 'Bereits laut Turnus eingeplant',
    'projectWizard.templates.turnusSkippedOnce': 'Aktueller Turnus übersprungen',
    'projectWizard.templates.skipTurnusConfirm': 'Soll dieser Zeitraum ausgesetzt werden?',
    'projectWizard.templates.skipTurnusError': 'Fehler beim Aussetzen.',
    'projectWizard.templates.created': 'Vorlage wurde erstellt',
    'projectWizard.templates.deleted': 'Vorlage gelöscht',
    'projectWizard.templates.undo': 'Rückgängig',
    'projectWizard.templates.undoing': 'Wiederherstellen...',
    'projectWizard.templates.restored': 'Vorlage wiederhergestellt',
    'projectWizard.templates.restoreError': 'Fehler beim Wiederherstellen der Vorlage.',
    'projectWizard.templates.updated': 'Vorlage wurde aktualisiert',
    'projectWizard.templates.dueThisPeriod': 'Noch für diesen Zeitraum einzuplanen',
    'projectWizard.buttons.addToTemplates': 'Zur Bibliothek hinzufügen',
    'projectWizard.buttons.updateTemplate': 'Bibliotheks-Eintrag anpassen',
    'language.de': 'Deutsch',
    'language.en': 'Englisch',
    'language.es': 'Spanisch',

    'app.loading': 'Lade Anwendung...',

    'mindmap.completed': 'Abgeschlossen',
    'mindmap.subprojects': 'Teilvorgänge',
    'mindmap.timeblocks': 'Time blocks',
    'mindmap.project': 'Operation',

    'header.calendar': 'Kalender',
    'header.dashboard': 'Dashboard',
    'header.allTickets': 'Alle Tickets',
    'header.allCalendars': 'Alle Kalender',
    'header.projects': 'Vorgänge',
    'header.searchDev': 'Suche (in Entwicklung)',
    'search.title': 'KI-Suche',
    'search.semanticBadge': 'semantisch',
    // Frontend Phase 4 (Welle 4): frühere Baseline-Keys (i18n_known_missing) —
    // Werte = die bisherigen Inline-Fallbacks aus dem Code
    'admin.notifications.saved': 'Benachrichtigung gespeichert.',
    'admin.notifications.saveError': 'Fehler beim Speichern der Benachrichtigung.',
    'admin.releaseNote.saved': 'Releasenote gespeichert.',
    'admin.releaseNote.saveError': 'Fehler beim Speichern der Releasenote.',
    'admin.releaseNote.heading': 'Hinweis bei Login (einmalig)',
    'admin.releaseNote.desc': 'Steuert ausschließlich den Einmal-Hinweis, den jeder Benutzer nach dem Login einmal als Overlay sieht. Die Versions-Historie im „Was ist neu?"-Modal kommt dagegen aus dem CHANGELOG-Import beim Deploy und wird hier nicht gepflegt.',
    'admin.releaseNote.versionsHeading': 'Versions-Historie („Was ist neu?")',
    'admin.releaseNote.versionsHint': 'Wird beim Deploy aus dem CHANGELOG importiert und ist hier nur zur Ansicht — gepflegt wird sie nicht im Admin-Bereich.',
    'admin.releaseNote.enabled': 'Aktiviert',
    'admin.releaseNote.title': 'Titel',
    'admin.releaseNote.titlePlaceholder': 'z. B. Update v2.5.0',
    'admin.releaseNote.content': 'Inhalt',
    'admin.releaseNote.contentPlaceholder': 'Neue Funktionen, Änderungen, Bugfixes...',
    'calendar.convertToTicket': 'In Ticket umwandeln',
    'calendar.confirmDeleteEvent': 'Möchten Sie diesen Termin wirklich löschen?',
    'calendar.deletingEvent': 'Termin wird gelöscht...',
    'calendar.deleteError': 'Fehler beim Löschen des Termins.',
    'calendar.deleteEvent': 'Eintrag löschen',
    'calendar.dropChoice.title': 'Termin verschieben oder neuen erstellen?',
    'calendar.dropChoice.text': 'Sie haben einen bestehenden Termin bewegt. Wie möchten Sie fortfahren?',
    'calendar.dropChoice.move': 'Verschieben',
    'calendar.dropChoice.createAnother': 'Weiteren erstellen',
    'calendar.updating': 'Wird aktualisiert...',
    'calendar.creating': 'Erstelle...',
    'calendar.createError': 'Fehler beim Erstellen.',
    'common.yes': 'Ja',
    'common.clear': 'Leeren',
    'dashboard.showTodayCalendar': 'Kalenderansicht anzeigen',
    'dashboard.hideTodayCalendar': 'Kalenderansicht ausblenden',
    'dashboard.expandToCalendar': 'Zur Kalenderansicht wechseln',
    'dashboard.collapseCalendar': 'Zur Liste Heutige Termine wechseln',
    'dashboard.todayLabel': 'Heute',
    'dashboard.releaseNote.defaultTitle': 'Neue Funktionen & Änderungen',
    'email.minimize.title': 'Minimieren',
    'email.maximize.title': 'Maximieren',
    'event.addToTicket.modalTitle': 'Termin zu Ticket hinzufügen',
    'eventDetail.busy': 'Beschäftigt',
    'eventDetail.tentative': 'Vorläufig',
    'eventDetail.free': 'Frei',
    'eventDetail.oof': 'Abwesend',
    'eventDetail.appointment': 'Termin',
    'eventDetail.noSubject': '(Kein Betreff)',
    'eventDetail.header': 'Termin',
    'eventDetail.status': 'Status',
    'eventDetail.date': 'Datum',
    'eventDetail.period': 'Zeitraum',
    'eventDetail.location': 'Ort',
    'eventDetail.organizer': 'Organisator',
    'eventDetail.attendees': 'Teilnehmer',
    'eventDetail.joinMeeting': 'Besprechung beitreten',
    'eventDetail.teamsMarker': 'Teams-Besprechung',
    'global.searchInCalendar': 'Im Kalender suchen...',
    'global.prevResult': 'Vorheriges Ergebnis',
    'global.nextResult': 'Nächstes Ergebnis',
    'global.creating': 'Erstelle...',
    'header.aiSearch': 'KI-Suche',
    'header.loadingStatus': 'Lade-Status',
    'projects.heatmapLoading': 'Heatmap wird aktualisiert...',
    'projects.searchProjects': 'Vorgänge durchsuchen...',
    'projects.turnusConfigured': 'Turnus konfiguriert',
    'projects.completePrompt.defaultProjectName': 'Neuer Vorgang',
    'projects.completePrompt.defaultBlockBase': 'Ticket',
    'projects.completePrompt.convertTitle': 'Brauchst du noch einen Folgetermin?',
    'projects.completePrompt.convertMessage': 'Brauchst du noch einen Folgetermin? Oder ist wirklich alles zu dem Thema abgeschlossen?',
    'projects.completePrompt.title': 'Vorgang vollständig',
    'projects.completePrompt.message': 'Der letzte Zeitblock des Vorgangs wurde abgeschlossen. Soll der Vorgang (und seine Untervorgänge) auch als abgeschlossen markiert und archiviert werden?',
    'projects.completePrompt.project': 'Vorgang',
    'projects.completePrompt.subprojects': 'Teilvorgänge',
    'projects.completePrompt.keepOpen': 'Nein, ich brauche noch Zeit',
    'projects.completePrompt.confirmConvert': 'Bin fertig, abschließen',
    'projects.completePrompt.confirm': 'Ja, Vorgang archivieren',
    'projectWizard.confirm.deleteSubproject': 'Diesen Teilvorgang löschen?',
    'projectWizard.templates.createError': 'Fehler beim Speichern der Vorlage. Details in der Konsole.',
    'projectWizard.info.turnusLocked': 'Turnus kann in "Projekt überarbeiten" nicht geändert werden.',
    'projectWizard.templates.choiceTitle': 'Bibliothekseintrag anpassen oder neu erstellen?',
    'projectWizard.templates.choiceText': 'Sie haben eine Vorlage angewendet. Wie möchten Sie fortfahren?',
    'projectWizard.templates.updateExisting': 'Bestehenden Eintrag anpassen',
    'projectWizard.templates.createNew': 'Neue Vorlage erstellen',
    'projectWizard.timeblock.checklist': 'Checkliste',
    'search.send': 'Senden',
    'search.ai.answer': 'Vorschlag / Antwort',
    'search.ai.followup.placeholder': 'Rückfrage stellen…',
    'search.ai.debug': 'Debug (nur sichtbar hier)',
    'search.ai.input': 'Suchbegriff',
    'search.ai.usedQueries': 'Genutzte Suchpaare',
    'ticket.create.data': 'Neue Ticket-Daten:',
    'ticket.merge.error': 'Fehler beim Zusammenführen der Tickets.',
    'ticketDetail.aiSummary': 'KI-Zusammenfassung',
    'ticketDetail.aiSummaryError': 'Fehler beim Erstellen der KI-Zusammenfassung',
    'ticketDetail.aiDisabled': 'KI-Funktionen sind deaktiviert.',
    'ticketDetail.descSummarize': 'Beschreibung per KI zusammenfassen',
    'ticketDetail.descSummarizeTitle': 'Beschreibung zusammenfassen',
    'ticketDetail.descSummarizeHint': 'Vorschlag prüfen und bei Bedarf anpassen. Beim Übernehmen wird die bisherige Beschreibung automatisch als interner Kommentar gesichert.',
    'ticketDetail.descSummarizeLoading': 'Die KI fasst die Beschreibung zusammen…',
    'ticketDetail.descSummarizeApply': 'Übernehmen',
    'ticketDetail.descSummarizeApplying': 'Übernehme…',
    'ticketDetail.descSummarizeApplied': 'Beschreibung übernommen — das Original steht als interner Kommentar in der Historie.',
    'ticketDetail.descSummarizeError': 'Fehler beim Zusammenfassen der Beschreibung.',
    'ticketDetail.descSummarizeApplyError': 'Fehler beim Übernehmen der Beschreibung.',
    'ticketDetail.confirmDeleteAttachment': 'Diesen Anhang wirklich löschen?',
    'ticketDetail.deleteAttachment': 'Anhang löschen',
    'ticketDetail.deleteAttachmentError': 'Fehler beim Löschen des Anhangs',
    'ticketDetail.loadingAttachments': 'Lade Anhänge...',
    'ticketDetail.file': 'Datei',
    'ticketDetail.files': 'Dateien',
    'ticketDetail.prevImage': 'Vorheriges Bild',
    'ticketDetail.nextImage': 'Nächstes Bild',
    'ticketDetail.backToHistory': 'Zur Historie zurück',
    'ticketDetail.meeting': 'Meeting',
    'ticketDetail.joinMeeting': 'Meeting beitreten',
    'ticketDetail.markWaiting': 'Als wartend markieren',
    'ticketDetail.waitingError': 'Fehler beim Setzen auf Wartend',
    'ticketDetail.reopenTitle': 'Ticket wieder öffnen',
    'ticketDetail.reopenBlocked': 'Wiederöffnen blockiert: Im Vorgang existieren noch offene Tickets oder Zeitblöcke.',
    'ticketDetail.reopenError': 'Fehler beim Wiederöffnen des Tickets',
    'ticketDetail.notFound': 'Ticket nicht gefunden.',
    'ticketDetail.completeDate': 'Datum',
    'ticketDetail.completeStart': 'Startzeit',
    'ticketDetail.completeEnd': 'Endzeit',
    'ticketDetail.completeHint': 'Bitte geben Sie Datum, Start- und Endzeit an. Bei bereits geplanter Zeit sind die Felder vorbelegt.',
    'ticketDetail.completeWithoutTimes': 'Ohne Zeiten',
    'ticketDetail.completeMissingFields': 'Bitte Datum, Startzeit und Endzeit angeben.',
    'ticketDetail.completeInvalidFields': 'Ungültiges Datum oder Uhrzeit.',
    'ticketDetail.completeEndBeforeStart': 'Die Endzeit muss nach der Startzeit liegen.',
    'ticketDetail.completePlanningError': 'Fehler beim Aktualisieren/Erstellen des Kalendereintrags.',
    'ticketDetail.completeUnplanError': 'Fehler beim Entfernen des Kalendereintrags.',
    'ticketDetail.transferPlanningError': 'Fehler beim Aktualisieren/Erstellen des Kalendereintrags vor der Übergabe.',
    'ticketDetail.transferHint': 'Wenn Sie Zeiten ändern, wird Ihr Kalendereintrag vor der Übergabe aktualisiert/erstellt.',
    'ticketDetail.sourceAssignee': 'Quellperson',
    'ticketDetail.usedPastTimeQuestion': 'Geplante vergangene Zeit genutzt?',
    'ticketDetail.usedPastTimeHint': 'Bei Nein werden vergangene Kalendereinträge zu diesem Ticket aus Ihrem Kalender entfernt.',
    'ticketSidebar.pool': 'Pool',
    'ticketSidebar.poolTooltip': 'Nur Pool-Tickets anzeigen',
    'search.placeholder': 'Ganzer Satz für KI-Suche, Keywords für klassische Suche',
    'search.aiDisabled': 'Die KI-Suche ist deaktiviert. Ein Administrator kann sie im Admin-Panel (Tab „AI Integration") aktivieren.',
    'search.sections.withTicket': 'Gefundene Inhalte über klassische Keyword-Suche',
    'search.sections.emailsOnly': 'Gefundene Mails ohne Ticketzuordnung',
    'search.sections.teamsOnly': 'Gefundene Teams-Nachrichten ohne Ticketzuordnung',
    'search.loading': 'Suche…',
    'search.noResults': 'Keine Ergebnisse',
    'search.noAnswer': 'Keine Antwort erhalten.',
    'search.noContext': 'Für diesen Verlauf sind keine Rückfragen möglich.',
    'search.followupFailed': 'Rückfrage fehlgeschlagen.',
    'search.newSearch': 'Neue Suche',
    'search.history': 'Verläufe',
    'search.historyEmpty': 'Noch keine gespeicherten Verläufe.',
    'search.historyMessages': '{{count}} Nachrichten',
    'search.historyDelete': 'Verlauf löschen',
    'search.historyDeleteConfirm': 'Diesen Verlauf löschen?',
    'search.deleteFailed': 'Löschen fehlgeschlagen.',
    'search.loadFailed': 'Verlauf konnte nicht geladen werden.',
    'search.saveConversation': 'Verlauf speichern',
    'search.savedToast': 'Verlauf gespeichert.',
    'search.saveFailed': 'Speichern fehlgeschlagen.',
    'search.attachToTicket': 'An Ticket anhängen',
    'search.attachAction': 'Anhängen',
    'search.attachSelectTicket': 'Ticket wählen',
    'search.attachHint': 'Der Verlauf wird als interner Kommentar am gewählten Ticket gespeichert.',
    'search.attachedToast': 'Als interner Kommentar am Ticket gespeichert.',
    'search.attachFailed': 'Anhängen fehlgeschlagen (fehlende Berechtigung?).',
    'search.attachCommentTitle': 'KI-Suche – Verlauf',
    'search.roleQuestion': 'Frage',
    'search.roleAnswer': 'Antwort',
    'search.hits': 'Treffer',
    'search.emptyHint': 'Stelle eine Frage — die KI durchsucht Tickets, Kommentare und E-Mails.',
    'ticketDetail.aiSimilar': 'Ähnliche Vorfälle (KI-Suche)',
    'aiSimilar.title': 'Ähnliche Vorfälle',
    'aiSimilar.similarTickets': 'Gefundene Tickets',
    'aiSimilar.adopt': 'Ins Ticket übernehmen',
    'aiSimilar.adopted': 'Als interner Kommentar gespeichert.',
    'aiSimilar.adoptFailed': 'Übernehmen fehlgeschlagen.',
    'aiSimilar.commentTitle': 'KI-Suche: Ähnliche Vorfälle',
    'aiSimilar.failed': 'KI-Suche fehlgeschlagen.',
    'header.notifications': 'Benachrichtigungen',
    'header.activities': 'Aktivitäten',
    'header.noNewActivities': 'Keine neuen Aktivitäten',
    'header.activityNewTicket': 'Neues Ticket',
    'header.activityComment': 'Neuer Kommentar',
    'header.activityEmail': 'E-Mail eingegangen',
    'header.activityAttachment': 'Neuer Anhang',
    'header.activityGeneric': 'Neue Aktivität',
    'header.assignedToYou': 'Dir zugewiesen',
    'header.unassigned': 'Nicht zugewiesen',
    'header.user': 'Benutzer',
    'header.admin': 'Administration',
    'header.enable': 'Aktivieren',
    'header.enableNotifications': 'Desktop-Benachrichtigungen aktivieren',
    'header.enableNotificationsShort': 'Benachrichtigungen aktivieren',
    'header.notificationsBlocked': 'Benachrichtigungen im Browser erlaubt? (aktuell blockiert)',
    'header.logout': 'Abmelden',
    'header.options': 'Optionen',
    'header.customers': 'Kundenverwaltung',
    'customersPage.title': 'Kundenverwaltung',
    'customersPage.disabled': 'Das Kunden-Feature ist auf diesem System deaktiviert.',
    'customersPage.forbidden': 'Die Kundenverwaltung ist Managern vorbehalten.',
    'header.whatsNew': 'Was ist neu?',
    'header.menu': 'Menü',
    'ticketCard.justNow': 'Gerade eben',
    'ticketCard.minutesAgo': 'vor {{count}} Min.',
    'ticketCard.hoursAgo': 'vor {{count}} Std.',
    'ticketCard.daysAgo': 'vor {{count}} Tg.',
    'ticketCard.newActivity': 'Neue Aktivität',
    'ticketCard.projectTicket': 'Vorgangs-Ticket',
    'contextMenu.open': 'Öffnen',
    'contextMenu.findInCalendar': 'Im Kalender finden',
    'contextMenu.mergeInto': 'Zu einem Ticket hinzufügen',
    'contextMenu.assignToMe': 'Mir zuweisen',
    'contextMenu.unassign': 'Zuweisung entfernen',
    'contextMenu.assignError': 'Fehler beim Zuweisen des Tickets.',
    'contextMenu.unassignError': 'Fehler beim Entfernen der Zuweisung.',
    'contextMenu.moveNextWeek': 'Auf nächste Woche verschieben',
    'contextMenu.markBooked': 'Als gebucht markieren',
    'contextMenu.unmarkBooked': 'Nicht mehr gebucht',
    'contextMenu.bookedSet': 'Als gebucht markiert.',
    'contextMenu.bookedRemoved': 'Markierung „Gebucht“ entfernt.',
    'contextMenu.bookedError': '„Gebucht“ konnte nicht gespeichert werden.',
    'contextMenu.movingNextWeek': 'Wird auf nächste Woche verschoben...',
    'contextMenu.movedNextWeek': 'Verschoben auf {{date}}',
    'contextMenu.moveNextWeekError': 'Verschieben auf nächste Woche fehlgeschlagen.',
    'app.updateAvailable': 'Eine neue Version der Anwendung ist verfügbar.',
    'app.reloadNow': 'Jetzt aktualisieren',
    'app.offline': 'Offline — angezeigte Daten können veraltet sein.',
    'app.degradedStale': 'Keine Verbindung zum Server — angezeigt wird der Stand von {time}.',
    'app.degradedOffline': 'Keine Verbindung zum Server — einige Bereiche konnten nicht geladen werden.',
    'header.poolMonitor': 'Pool-Monitor',
    'pool.title': 'Pool-Monitor',
    'pool.loading': 'Lade Pool…',
    'pool.empty': 'Keine offenen Tickets im Pool. 🎉',
    'pool.lastUpdated': 'Stand',
    'pool.justNow': 'Gerade eben',
    'pool.minutesAgo': 'vor {{count}} Min.',
    'pool.hoursAgo': 'vor {{count}} Std.',
    'pool.daysAgo': 'vor {{count}} Tg.',
    'dashboard.expandPool': 'Pool als Monitor-Ansicht öffnen',
    'dashboard.field.attachments': 'Anhänge',
    'dashboard.addAttachment': 'Datei/Foto hinzufügen',
    'dashboard.removeAttachment': 'Anhang entfernen',
    'dashboard.attachmentUploadError': 'Ticket wurde erstellt, aber Anhänge konnten nicht hochgeladen werden.',
    'ticketDetail.ticketUpdateError': 'Fehler beim Aktualisieren des Tickets.',
    'header.myApiKeys': 'Meine API-Keys',
    'personalKeys.title': 'Meine API-Keys',
    'personalKeys.intro': 'Bearer-Keys für eigene Skripte und Agenten. Ein Key hat genau deine Benutzerrechte und wird nur einmal angezeigt.',
    'personalKeys.namePlaceholder': 'z. B. Kalender-Skript',
    'personalKeys.none': 'Noch keine eigenen API-Keys angelegt.',
    'personalKeys.loadError': 'Fehler beim Laden der API-Keys.',
    'personalKeys.createError': 'Fehler beim Erzeugen des API-Keys.',
    'releaseNotes.historyTitle': 'Was ist neu?',
    'releaseNotes.versionLabel': 'Version',
    'releaseNotes.current': 'aktuell',
    'releaseNotes.loadError': 'Versions-History konnte nicht geladen werden.',
    'releaseNotes.empty': 'Noch keine Releasenotes vorhanden.',
    'header.login': 'Anmelden',
    'header.setup': 'Setup',
    'header.projectPlanningDisabled': 'Im Vorgangs-Zeitplan deaktiviert',
    'header.backToProject': 'Zurück zum Vorgang',
    'header.projectPlanningFor': 'Zeitplanung für Vorgang',
    'projectWizard.subTimeblocks.heading': 'Zeitblöcke für {{name}}',
    'header.notice': 'Hinweis:',
    'header.dropHereNewTicket': 'Hier ablegen für neues Ticket',
    'header.unreadTitle': '{{count}} ungelesene Tickets/Aktivitäten',
    'header.notificationsEnabled': 'Benachrichtigungen aktiviert',
    'header.notificationsEnabledBody': 'Sie erhalten jetzt Desktop-Benachrichtigungen.',

    'ticketDetail.block': 'Zeitblock',
    'ticketDetail.ticket': 'Ticket',
    'ticketDetail.toProject': 'Zum Vorgang',
    'ticketDetail.projectLoadError': 'Der Vorgang konnte nicht geladen werden.',
    'ticketDetail.edit': 'Bearbeiten',
    'ticketDetail.transfer': 'Übergeben',
    'ticketDetail.transferTitle': 'Ticket übergeben',
    'ticketDetail.complete': 'Abschließen',
    'ticketDetail.completeTitle': 'Ticket abschließen',
    'ticketDetail.history': 'Historie',
    'ticketDetail.attach': 'Dateien anhängen',
    'ticketDetail.newEmail': 'Neue Mail-Konversation',
    'ticketDetail.newEmailError': 'Der Mail-Entwurf konnte nicht angelegt werden. Bitte erneut versuchen.',
    'ticketDetail.detachEmailTitle': 'Mail vom Ticket lösen',
    'ticketDetail.detachEmailConfirm': 'Die Mail „{{subject}}“ vom Ticket lösen? In Outlook bleibt sie unverändert, nur die Verknüpfung mit dem Ticket entfällt.',
    'ticketDetail.detachEmail': 'Vom Ticket lösen',
    'ticketDetail.detachEmailDone': 'Mail vom Ticket gelöst.',
    'ticketDetail.detachEmailGone': 'Die Mail hängt nicht mehr an diesem Ticket.',
    'ticketDetail.detachEmailAutomatic': 'Diese Mail wurde automatisch zugeordnet und lässt sich nicht lösen.',
    'ticketDetail.detachEmailForbidden': 'Keine Berechtigung, dieses Ticket zu ändern.',
    'ticketDetail.detachEmailError': 'Die Mail konnte nicht gelöst werden.',
    'ticketDetail.addComment': 'Kommentar hinzufügen',
    'ticketDetail.loadingHistory': 'Lade Historie...',
    'ticketDetail.noHistory': 'Keine Historie vorhanden',
    'ticketDetail.download': 'Herunterladen',
    'ticketDetail.cancel': 'Abbrechen',
    'ticketDetail.adding': 'Hinzufügen...',
    'ticketDetail.enterComment': 'Neuen Kommentar eingeben...',
    'ticketDetail.optionalComment': 'Optionalen Kommentar eingeben...',
    'ticketDetail.status': 'Status',
    'ticketDetail.priority': 'Priorität',
    'ticketDetail.plannedFor': 'Geplant für',
    'ticketDetail.created': 'Erstellt',
    'ticketDetail.createdBy': 'Erstellt von',
    'ticketDetail.closedBy': 'Abgeschlossen von',
    'ticketDetail.completeForeignHint': 'Dieses Ticket ist {{assignee}} zugewiesen — es wird trotzdem in deinem Namen abgeschlossen.',
    'ticketDetail.period': 'Zeitraum',
    'ticketDetail.filesDragOverlay': 'Dateien hierher ziehen, um sie anzuhängen',
    'ticketDetail.loadingPreview': '🔄 Lade Vorschau...',
    'ticketDetail.subject': 'Betreff',
    'ticketDetail.from': 'Von',
    'ticketDetail.to': 'An',
    'ticketDetail.received': 'Empfangen',
    'ticketDetail.teamsMessage': 'Teams-Nachricht',
    'ticketDetail.chat': 'Chat',
    'ticketDetail.sent': 'Gesendet',
    'ticketDetail.description': 'Beschreibung',
    'ticketDetail.system': 'System',
    'ticketDetail.unknown': 'Unbekannt',
    'ticketDetail.emailAdded': 'E-Mail hinzugefügt',
    'ticketDetail.teamsMessageAdded': 'Teams-Nachricht hinzugefügt',
    'ticketDetail.fileAdded': 'Datei hinzugefügt',
    'ticketDetail.comment': 'Kommentar',
    'ticketDetail.editTicketTitle': 'Ticket bearbeiten',
    'ticketDetail.outgoingEmail': 'Ausgehende E-Mail',
    'ticketDetail.incomingEmail': 'Eingehende E-Mail',
    'ticketDetail.unknownEmailDirection': 'Richtung unbekannt',
    'ticketDetail.directMessage': 'Direktnachricht',
    'ticketDetail.editCommentTitle': 'Kommentar bearbeiten',
    'ticketDetail.updateError': 'Fehler beim Aktualisieren des Kommentars',
    'ticketDetail.confirmDeleteComment': 'Diesen Kommentar wirklich löschen?',
    'ticketDetail.deleting': 'Lösche... ',
    'ticketDetail.deleteError': 'Fehler beim Löschen des Kommentars',
    'ticketDetail.projectGeneratedTitle': 'Vorgangsgenerierter Titel',
    'ticketDetail.fileTooLarge': 'Datei "{{name}}" ist zu groß. Maximum sind 10MB.',
    'ticketDetail.selectFilesLabel': 'Dateien auswählen (max. 10MB pro Datei)',
    'ticketDetail.selectedFiles': 'Ausgewählte Dateien:',
    'ticketDetail.optionalFilesDescription': 'Optionale Beschreibung für die Dateien...',
    'ticketDetail.uploading': 'Hochladen...',
    'ticketDetail.uploadFilesCount': '{{count}} Datei(en) hochladen',
    'ticketDetail.pasteFromClipboard': 'Aus Zwischenablage einfügen',
    'ticketDetail.clipboardNotSupported': 'Zwischenablage-Lesen wird von diesem Browser nicht unterstützt.',
    'ticketDetail.clipboardEmpty': 'Zwischenablage enthält keine unterstützten Inhalte.',
    'ticketDetail.clipboardReadError': 'Fehler beim Lesen aus der Zwischenablage.',
    'ticketDetail.pasteHint': 'Tipp: Inhalte können direkt mit Strg+V eingefügt werden.',
    'ticketDetail.transferAssignee': 'Zielperson',
    'ticketDetail.selectTargetPersonError': 'Bitte wählen Sie eine Zielperson aus.',
    'common.pleaseSelect': 'Bitte auswählen',
    'email.read': 'Gelesen',
    'email.unread': 'Ungelesen',
    'email.markAsUnread': 'Als ungelesen markieren',
    'email.markAsRead': 'Als gelesen markieren',
    'email.detailTitle': 'E-Mail Details',

    'common.sessionExpired': 'Sitzung abgelaufen. Bitte melden Sie sich erneut an.',
    'calendar.errorLoad': 'Fehler beim Laden der Termine. Bitte versuchen Sie es später erneut.',
    'projects.errorLoadUsers': 'Fehler beim Laden der Benutzer'
    , 'common.all': 'Alle'
    , 'common.none': 'Keine'
    , 'common.paused': '⚠️ Pausiert'
    , 'common.errors': '⚠️ {{count}} Fehler'
    , 'common.loadFailed': 'Konnte nicht geladen werden'
    , 'common.retry': 'Erneut versuchen'
    , 'teams.list.loading': 'Teams-Nachrichten werden geladen…'
    , 'teams.list.loadFailed': 'Teams-Nachrichten konnten nicht geladen werden'
    , 'common.noEntries': 'Noch keine Einträge'
    , 'common.linkedToTicket': 'Mit Ticket verknüpft'
    , 'ticketDetail.totalSize': 'Gesamtgröße:'
    // #329: Verwaltung — KI-Reiter, Vorlagen, Kalender-Marker, Protokoll
    , 'admin.templates.saveFailed': 'Fehler beim Speichern der Antwort-Vorlagen.'
    , 'admin.templates.autoReplyPlaceholder': 'Wir haben ein neues Ticket mit der Ticketnummer [ticketnr] erstellt und melden uns schnellstmöglich.'
    , 'admin.templates.discardChanges': 'Änderungen verwerfen'
    , 'admin.markers.deleteConfirm': 'Sind Sie sicher, dass Sie diesen Kalender-Marker löschen möchten?'
    , 'admin.markers.deleteFailed': 'Fehler beim Löschen des Kalender-Markers.'
    , 'admin.markers.createFailed': 'Fehler beim Erstellen des Kalender-Markers.'
    , 'admin.markers.updateFailed': 'Fehler beim Aktualisieren des Kalender-Markers.'
    , 'admin.logs.trigger': 'Auslöser'
    , 'admin.logs.empty': 'Keine Einträge'
    , 'admin.ai.saveFailed': 'Fehler beim Speichern der KI-Konfiguration.'
    , 'admin.ai.sectionDescription': 'Konfigurieren Sie die Verbindung zu einem KI-Anbieter (OpenAI oder OpenRouter). Die OpenAI-Basis-URL ist vorausgefüllt.'
    , 'admin.ai.defaultHint': 'Voreinstellung wie gewünscht, kann hier angepasst werden.'
    , 'admin.ai.summaryPromptHint': 'Vorgabetext für die Ticket-Zusammenfassung in der Detailansicht.'
    , 'admin.ai.model': 'Modell'
    , 'admin.ai.choosePlease': 'Bitte wählen…'
    , 'admin.ai.modelsHint': 'Modelle werden über den Verbindungscheck geladen.'
    , 'admin.ai.mailModeOff': 'Aus — Mail-Text wie bisher übernehmen'
    , 'admin.ai.mailModeShadow': 'Schatten — Vorschlag als interner Kommentar'
    , 'admin.ai.mailModeLive': 'Scharf — KI-Beschreibung und Priorität setzen'
    , 'admin.ai.mailModeHint': 'Aufbereitung eingehender support@-Mails. „Schatten\u201c verändert nichts am Ticket — der Vorschlag landet als interner Kommentar zur Bewertung.'
    , 'admin.ai.transcribeModel': 'Transkriptions-Modell (Anrufbeantworter)'
    , 'admin.ai.transcribeHint': 'Audiofähiges Modell für die Voicemail-Transkription. Leer = Standard.'
    , 'admin.ai.embeddingModel': 'Embedding-Modell'
    , 'admin.ai.embeddingProviderHint': 'Eigener Anbieter für /embeddings — nötig, wenn der Chat über OpenRouter läuft. Leer = Chat-Anbieter.'
    , 'admin.ai.dimensionsPlaceholder': 'Modell-Standard'
    , 'admin.ai.dimensionsHint': 'z. B. 1024 spart zwei Drittel Speicher bei text-embedding-3-large. Nach einer Änderung: Backfill!'
    , 'admin.ai.backfillRunning': 'Backfill läuft…'
    , 'admin.ai.backfillStart': 'Backfill starten'
    , 'admin.ai.lastCheck': 'Letzte Prüfung:'
    , 'admin.tabs.features': 'Features'
    , 'admin.ai.switchesInFeatures': 'An- und abgeschaltet werden die Funktionen im Reiter „Features“.'
    , 'admin.ai.disabledInFeatures': 'Im Reiter „Features“ abgeschaltet. Die Einstellungen lassen sich trotzdem vorbereiten.'
    , 'admin.ai.allDisabledInFeatures': 'Alle KI-Funktionen sind im Reiter „Features“ abgeschaltet.'
    , 'admin.features.title': 'Features'
    , 'admin.features.description': 'Alle Funktionen, die sich an- und abschalten lassen. Jeder Schalter speichert sofort. Die Einstellungen bleiben in ihren Reitern und werden dort ausgegraut, solange die Funktion aus ist.'
    , 'admin.features.loadError': 'Die Schalter konnten nicht geladen werden. Bitte „Aktualisieren“ versuchen.'
    , 'admin.features.saved': 'Gespeichert.'
    , 'admin.features.saveError': 'Der Schalter konnte nicht gespeichert werden.'
    , 'admin.features.aiNotReady': 'Es ist noch kein KI-Anbieter eingerichtet (Reiter „AI Integration“: Base URL, API Key, Modell). Bis dahin bleiben die KI-Funktionen wirkungslos, auch wenn sie hier an sind.'
    , 'admin.features.inAiTab': 'Einstellungen im Reiter „AI Integration“'
    , 'admin.features.inCustomersTab': 'Verwaltung im Reiter „Kunden“'
    , 'admin.features.searchAi': 'KI-Suche'
    , 'admin.features.searchAiDesc': 'KI-Antwort in der globalen Suche mit Folgefragen und „Ähnliche Tickets“ in der Detailansicht.'
    , 'admin.features.summaryAi': 'KI-Zusammenfassung'
    , 'admin.features.summaryAiDesc': 'Knöpfe „Zusammenfassen“ und „Beschreibung zusammenfassen“ am Ticket.'
    , 'admin.features.mailAi': 'KI-Ticketerstellung aus Mails'
    , 'admin.features.voicemailAi': 'KI-Voicemail-Tickets'
    , 'admin.features.customers': 'Kundenstamm'
    , 'admin.features.darstellung': 'Darstellung (Vorgabe)'
    , 'admin.features.darstellungDesc': 'Erscheinungsbild für alle, die unter dem Zahnrad keine eigene Darstellung gewählt haben.'
    , 'admin.server.darstellungSofort': 'Die Auswahl speichert sofort.'
    , 'darstellung.modern': 'Modern'
    , 'darstellung.klassisch': 'Klassisch'
    , 'einstellungen.titel': 'Einstellungen'
    , 'einstellungen.darstellung': 'Darstellung'
    , 'einstellungen.darstellungVorgabe': 'Vorgabe der Installation'
    , 'einstellungen.darstellungZurzeit': 'zurzeit {name}'
    , 'einstellungen.sprache': 'Sprache'
    , 'einstellungen.apiKeysText': 'Schlüssel für Programme und Agenten, die mit Ihren Rechten auf das Ticketsystem zugreifen.'
    , 'einstellungen.apiKeysVerwalten': 'API-Keys verwalten'
    , 'einstellungen.speichernFehler': 'Die Einstellung konnte nicht gespeichert werden.'
    , 'einstellungen.kopfleiste': 'Kopfleiste'
    , 'einstellungen.reiter': 'Reiter'
    , 'einstellungen.reiterFest': 'Startseite, immer sichtbar'
    , 'einstellungen.reiterAnzeige': 'Darstellung der Reiter'
    , 'einstellungen.symbolUndText': 'Symbol und Text'
    , 'einstellungen.nurSymbol': 'Nur Symbol'
    , 'einstellungen.kontaktsuche': 'Kontaktsuche in der Kopfleiste'
    , 'einstellungen.dashboard': 'Dashboard'
    , 'einstellungen.nachOben': '{{name}} nach oben'
    , 'einstellungen.nachUnten': '{{name}} nach unten'
    , 'einstellungen.letzterBereich': 'Mindestens ein Bereich bleibt sichtbar'
    , 'dashboard.pool': 'Pool'
    , 'dashboard.showBereich': '{{name}} einblenden'
    , 'dashboard.alleAusgeblendet': 'Alle Bereiche des Dashboards sind ausgeblendet.'
    , 'dashboard.einstellungenOeffnen': 'Einstellungen öffnen'
    , 'admin.features.lineLabel': 'Stand als Zeile (für das Deploy-Protokoll):'
    , 'admin.ai.title': 'AI Integration'
    , 'admin.ai.groupProvider': 'Anbieter-Verbindung'
    , 'admin.ai.groupProviderHint': 'Gilt für alle KI-Funktionen darunter.'
    , 'admin.ai.groupSearch': 'KI-Suche'
    , 'admin.ai.groupIndex': 'Suchindex (Embeddings)'
    , 'admin.ai.groupIndexHint': 'Grundlage für die Suchmodi „Hybrid“ und „Semantisch“.'
    , 'admin.ai.groupSummary': 'KI-Zusammenfassung'
    , 'admin.ai.groupVoicemail': 'Anrufbeantworter (Voicemail)'
    , 'admin.ai.baseUrl': 'API-Basis-URL'
    , 'admin.ai.baseUrlExamples': 'Beispiele: https://api.openai.com/v1, https://openrouter.ai/api/v1'
    , 'admin.ai.apiKey': 'API-Key'
    , 'admin.ai.apiKeyStored': 'Ein API-Key ist gespeichert.'
    , 'admin.ai.showKey': 'Anzeigen'
    , 'admin.ai.hideKey': 'Ausblenden'
    , 'admin.ai.debug': 'Debug'
    , 'admin.ai.testing': 'Teste…'
    , 'admin.ai.testConnection': 'Verbindung testen & Modelle laden'
    , 'admin.ai.retrievalMode': 'Suchmodus (KI-Suche)'
    , 'admin.ai.retrievalLexical': 'Lexikalisch — wie bisher (Optimizer + Textsuche)'
    , 'admin.ai.retrievalHybrid': 'Hybrid — Textsuche + semantisch (empfohlen nach Backfill)'
    , 'admin.ai.retrievalVector': 'Semantisch — nur Embedding-Index'
    , 'admin.ai.retrievalHint': 'Hybrid/Semantisch erst nach erfolgreichem Backfill aktivieren (Suchindex unten).'
    , 'admin.ai.searchPrompt': 'Such-Prompt (Vorgabe auf Englisch)'
    , 'admin.ai.perQueryFragmentCap': 'Fragmente pro Suchpaar'
    , 'admin.ai.globalFragmentCap': 'Globales Fragmentlimit'
    , 'admin.ai.contextCharCap': 'Kontext-Zeichenlimit'
    , 'admin.ai.fullTicketsCount': 'Anzahl Voll-Tickets'
    , 'admin.ai.defaultValue': 'Standard: {{wert}}'
    , 'admin.ai.embeddingModelHint': 'Leer = semantische Suche aus. Empfehlung: text-embedding-3-large.'
    , 'admin.ai.embeddingBaseUrl': 'Embedding-Basis-URL (optional)'
    , 'admin.ai.embeddingApiKey': 'Embedding-API-Key (optional)'
    , 'admin.ai.embeddingApiKeyPlaceholder': 'sk-… (leer = Chat-Key)'
    , 'admin.ai.embeddingApiKeyStored': 'Ein eigener Embedding-Key ist gespeichert.'
    , 'admin.ai.dimensions': 'Dimensionen (optional)'
    , 'admin.ai.indexStatus': 'Index-Status'
    , 'admin.ai.loadStatus': 'Status laden'
    , 'admin.ai.testEmbedding': 'Embedding testen'
    , 'admin.ai.chunksEmbedded': '{{eingebettet}}/{{gesamt}} Chunks eingebettet'
    , 'admin.ai.chunksPending': '({{anzahl}} wartend)'
    , 'admin.ai.backfillProgress': 'Backfill: {{phase}}, {{quellen}} Quellen, {{eingebettet}} eingebettet'
    , 'admin.ai.backfillLastError': 'letzter Fehler:'
    , 'admin.ai.summaryPrompt': 'Zusammenfassungs-Prompt (Vorgabe auf Englisch)'
    , 'admin.ai.voicemailToggleHint': 'Placetel-AB-Nachrichten transkribieren und als aufbereitetes Ticket anlegen. Bei Inaktiv entsteht ein normales Mail-Ticket.'
    , 'admin.ai.transcribePlaceholder': 'google/gemini-3.5-flash-lite (Standard)'
    , 'admin.ai.saveAllHint': 'Speichert alle Rahmen auf einmal.'
    // #329: Detailansicht einer Teams-Nachricht — vor Sprint 10 ohne jede Uebersetzung
    , 'teams.detail.title': 'Teams-Nachricht — Details'
    , 'teams.detail.from': 'Von:'
    , 'teams.detail.chat': 'Chat:'
    , 'teams.detail.sent': 'Gesendet:'
    , 'teams.detail.edited': 'Bearbeitet:'
    , 'teams.detail.replyTo': 'Antwort auf:'
    , 'teams.detail.messageRef': 'Nachricht #{{id}}'
    , 'teams.detail.contentHeading': 'Nachrichteninhalt'
    , 'teams.detail.frameTitle': 'Teams-Nachrichteninhalt'
    , 'teams.detail.subjectFallback': 'Teams-Nachricht'
    , 'teams.detail.noContent': 'Kein Inhalt verfügbar'
    , 'teams.detail.unknownFormat': 'Unbekanntes Nachrichtenformat'
    , 'teams.detail.unknownSender': 'Unbekannter Absender'
    , 'teams.chatType.oneOnOne': 'Direktnachricht'
    , 'teams.chatType.group': 'Gruppenchat'
    , 'teams.chatType.unknown': 'Unbekannt'
    , 'teams.importance.high': 'Hoch'
    , 'teams.importance.urgent': 'Dringend'
    , 'teams.importance.normal': 'Normal'
    , 'dashboard.loading': 'Lade Daten...'
    , 'dashboard.todayEvents': 'Heutige Termine'
    , 'mobile.settings': 'Einstellungen'
    , 'mobile.language': 'Sprache'
    , 'mobile.sendFeedback': 'Feedback senden'
    , 'mobile.confirmTakeOver': 'Dieses Ticket dir zuweisen?'
    , 'mobile.eventNotes': 'Notizen'
    , 'mobile.aiQuestion': 'Frage an die KI'
    , 'mobile.newSearch': 'Neue Suche'
    , 'mobile.aiSearching': 'Suche läuft…'
    , 'mobile.aiFoundTickets': 'Gefundene Tickets'
    , 'mobile.aiSearchError': 'Die Suche hat nicht geklappt. Bitte noch einmal versuchen.'
    , 'mobile.aiSearchDisabled': 'Die KI-Suche ist auf dieser Installation abgeschaltet.'
    , 'mobile.editComment': 'Kommentar bearbeiten'
    , 'mobile.deleteImage': 'Bild löschen'
    , 'mobile.imageDeleted': 'Bild gelöscht.'
    , 'mobile.image': 'Bild'
    , 'mobile.clearSearch': 'Suche leeren'
    , 'mobile.searchMine': 'Meine Tickets durchsuchen'
    , 'mobile.searchPool': 'Pool-Tickets durchsuchen'
    , 'mobile.tabToday': 'Heute'
    , 'mobile.tabMine': 'Meine'
    , 'mobile.tabPool': 'Pool'
    , 'mobile.tabsLabel': 'Bereiche'
    , 'mobile.refresh': 'Aktualisieren'
    , 'mobile.ticket': 'Ticket'
    , 'mobile.back': 'Zurück'
    , 'mobile.archived': 'Archiviert'
    , 'mobile.poolTicket': 'Pool-Ticket, niemandem zugewiesen'
    , 'mobile.actions': 'Aktionen'
    , 'mobile.takePhoto': 'Foto'
    , 'mobile.chooseFiles': 'Dateien'
    , 'mobile.uploading': 'Lädt hoch…'
    , 'mobile.takeOver': 'Übernehmen'
    , 'mobile.newComment': 'Kommentar'
    , 'mobile.commentPlaceholder': 'Was hast du gemacht oder festgestellt?'
    , 'mobile.send': 'Senden'
    , 'mobile.sending': 'Sendet…'
    , 'mobile.showMore': 'Mehr anzeigen'
    , 'mobile.showLess': 'Weniger anzeigen'
    , 'mobile.history': 'Verlauf'
    , 'mobile.historyEmpty': 'Noch keine Einträge.'
    , 'mobile.ticketForbidden': 'Dieses Ticket ist für dich nicht sichtbar.'
    , 'mobile.ticketLoadError': 'Das Ticket konnte nicht geladen werden.'
    , 'mobile.commentSaved': 'Kommentar gespeichert.'
    , 'mobile.commentError': 'Der Kommentar konnte nicht gespeichert werden.'
    , 'mobile.uploadTooMany': 'Höchstens {{count}} Dateien auf einmal.'
    , 'ticketCard.hasAttachments': 'Hat Anhänge'
    , 'mobile.openEmail': 'E-Mail öffnen: {{subject}}'
    , 'mobile.openAndReply': 'Öffnen und antworten'
    , 'email.reply.popupBlocked': 'Der Entwurf liegt in Outlook bereit, ließ sich hier aber nicht öffnen.'
    , 'ticketDetail.mailAttachments': 'Anhänge der E-Mail'
    , 'viewer.title': 'Anhang'
    , 'viewer.page': 'Seite {{n}} von {{total}}'
    , 'viewer.zoomIn': 'Vergrößern'
    , 'viewer.zoomOut': 'Verkleinern'
    , 'viewer.zoomReset': 'Einpassen'
    , 'viewer.delete': 'Anhang löschen'
    , 'viewer.loadError': 'Die Datei konnte nicht geladen werden.'
    , 'viewer.noPreview': 'Für diese Datei gibt es keine Vorschau.'
    , 'viewer.prev': 'Vorheriger Anhang'
    , 'viewer.next': 'Nächster Anhang'
    , 'ticketDetail.view': 'Ansehen'
    , 'mobile.uploadTooLarge': '„{{name}}“ ist größer als 10 MB.'
    , 'mobile.uploadDoneOne': 'Datei hochgeladen.'
    , 'mobile.uploadDoneMany': '{{count}} Dateien hochgeladen.'
    , 'mobile.uploadError': 'Das Hochladen ist fehlgeschlagen.'
    , 'mobile.takenOver': 'Das Ticket ist jetzt dir zugewiesen.'
    , 'mobile.attachmentOpenError': 'Die Datei konnte nicht geöffnet werden.'
    , 'mobile.installTitle': 'Als App installieren?'
    , 'mobile.installAndroidText': 'Tickets öffnet sich dann wie eine App vom Startbildschirm.'
    , 'mobile.installIosBefore': 'Tippe auf'
    , 'mobile.installIosShare': 'Teilen'
    , 'mobile.installIosAfter': 'und dann auf „Zum Home-Bildschirm“.'
    , 'mobile.installButton': 'Installieren'
    , 'mobile.installLater': 'Später'
    , 'mobile.installGotIt': 'Verstanden'
    , 'dashboard.noUpcomingEvents': 'Keine anstehenden Termine'
    , 'dashboard.myTickets': 'Meine'
    , 'dashboard.tickets': 'Tickets'
    , 'dashboard.emails': 'Mails'
    , 'dashboard.teams': 'Teams'
    , 'dashboard.hiddenSectionsLabel': 'Ausgeblendete Bereiche:'
    , 'dashboard.showEmails': 'E-Mails einblenden'
    , 'dashboard.showTeams': 'Teams einblenden'
    , 'dashboard.hideEmails': 'E-Mails ausblenden'
    , 'dashboard.hideTeams': 'Teams ausblenden'
    , 'dashboard.configureTicketFilter': 'Ticket Filter konfigurieren'
    , 'dashboard.configureEmailFilter': 'E-Mail Filter konfigurieren'
    , 'dashboard.configureEmailSection': 'E-Mail Sektion konfigurieren'
    , 'filters.additionalMailboxes': 'Zusätzliche Postfächer'
    , 'filters.additionalMailboxPlaceholder': 'E-Mail-Adresse eingeben...'
    , 'filters.addMailbox': 'Postfach hinzufügen'
    , 'filters.removeMailbox': 'Postfach entfernen'
    , 'filters.additionalMailboxesHelp': 'Fügen Sie Postfächer hinzu, auf die Sie Vollzugriff haben. E-Mails daraus werden in der Sektion mit angezeigt.'
    , 'filters.additionalMailboxesActive': '{{count}} zusätzliches/zusätzliche Postfach/Postfächer'
    , 'dashboard.configureTeamsFilter': 'Teams-Nachrichten Filter konfigurieren'
    , 'dashboard.freeTime': 'Freie Zeit'
    , 'dashboard.searchTicketsPlaceholder': 'Tickets durchsuchen...'
    , 'dashboard.searchMailsPlaceholder': 'Mails durchsuchen...'
    , 'dashboard.searching': 'Suche…'
    , 'dashboard.retry': 'Erneut versuchen'
    , 'dashboard.connecting': 'Verbinde...'
    , 'dashboard.serviceIssues': 'Service-Verbindungsprobleme'
    , 'dashboard.autoUpdatePausedFor': 'Automatische Aktualisierung pausiert für:'
    , 'dashboard.sessionMayHaveExpired': 'Ihre Sitzung ist möglicherweise abgelaufen.'
    , 'dashboard.createTicketHeader': 'Neues Ticket erstellen'
    , 'dashboard.dragDropHint': 'Dateien per Drag & Drop hierher ziehen'
    , 'dashboard.attachmentsSuffix': 'Anhänge'
    , 'dashboard.field.title': 'Titel'
    , 'dashboard.field.description': 'Beschreibung'
    , 'dashboard.field.priority': 'Priorität'
    , 'dashboard.field.assignedTo': 'Zugewiesen an'
    , 'dashboard.field.category': 'Kategorie'
    , 'dashboard.placeholder.title': 'Ticket-Titel eingeben...'
    , 'dashboard.placeholder.description': 'Ticket-Beschreibung eingeben...'
    , 'dashboard.placeholder.category': 'z.B. Support, Bug, Feature'
    , 'dashboard.unassigned': 'Nicht zugewiesen'
    , 'dashboard.cancel': 'Abbrechen'
    , 'dashboard.createTicket': 'Ticket erstellen'
    , 'dashboard.enterTitle': 'Bitte geben Sie einen Titel ein.'
    , 'dashboard.createTicketError': 'Fehler beim Erstellen des Tickets.'
    , 'dashboard.saveEmailFilterError': 'Fehler beim Speichern der Filter-Einstellungen.'
    , 'dashboard.saveTeamsFilterError': 'Fehler beim Speichern der Teams-Filter-Einstellungen.'
    , 'dashboard.saveTicketFilterError': 'Fehler beim Speichern der Ticket-Filter-Einstellungen.'
    , 'dashboard.errorLoadData': 'Fehler beim Laden der Daten. Bitte versuchen Sie es später erneut.'
    , 'dashboard.refreshError': 'Fehler beim Aktualisieren der Daten'
    , 'calendar.loading': 'Lade Termine...'
    , 'calendar.previousWeek': 'Vorherige Woche'
    , 'calendar.today': 'Heute'
    , 'calendar.nextWeek': 'Nächste Woche'
    , 'calendar.backToWeekTooltip': 'Zur Wochenansicht zurückkehren'
    , 'calendar.show14DaysTooltip': '14 Tage anzeigen'
    , 'calendar.week': 'Woche'
    , 'calendar.14days': '14 Tage'
    , 'calendar.updateError': 'Fehler beim Aktualisieren der Termine'
    , 'calendar.planNewTicketError': 'Fehler beim Planen des neu erstellten Tickets'
    , 'calendar.createTicketError': 'Fehler beim Erstellen des Tickets.'
    , 'calendar.removePlanningError': 'Fehler beim Entfernen des Tickets aus der Planung'
    , 'calendar.changeDurationError': 'Fehler beim Ändern der Ticket-Dauer'
    , 'calendar.placeTicketError': 'Fehler beim Platzieren des Tickets'
    , 'calendar.newTicket': 'Neues Ticket erstellen'
    , 'global.loading': 'Lade Kalender...'
    , 'global.errorLoad': 'Fehler beim Laden der Kalenderdaten. Bitte versuchen Sie es später erneut.'
    , 'global.refreshError': 'Fehler beim Aktualisieren des globalen Kalenders'
    , 'global.permissionDenied': '⚠️ Keine Berechtigung: Sie können keine Kalendereinträge für {{userEmail}} erstellen. Benötigen erweiterte Kalenderberechtigungen.'
    , 'global.userNotFound': '⚠️ Benutzer {{userEmail}} wurde nicht gefunden oder der Kalender ist nicht verfügbar.'
    , 'global.moveTicketError': 'Fehler beim Verschieben des Tickets: {{details}}'
    , 'global.planNewTicketError': 'Fehler beim Planen des neu erstellten Tickets'
    , 'global.createTicketError': 'Fehler beim Erstellen des Tickets.'
    , 'global.removePlanningError': 'Fehler beim Entfernen des Tickets aus der Planung'
    , 'global.changeDurationError': 'Fehler beim Ändern der Ticket-Dauer'
    , 'global.placeTicketError': 'Fehler beim Platzieren des Tickets'
    , 'global.users': 'Benutzer'
    , 'global.onlyTodayTooltip': 'Nur aktueller Tag anzeigen'
    , 'global.weekTooltip': 'Aktuelle Woche (Mo–Fr) anzeigen'
    , 'global.weekendTooltip': 'Aktuelle Woche inkl. Wochenende (Mo–So) anzeigen'
    , 'common.noTicketsFound': 'Keine Tickets gefunden'
    , 'common.noOpenTickets': 'Keine offenen Tickets'
    , 'common.tryOtherSearchTerms': 'Versuchen Sie andere Suchbegriffe'
    , 'ticket.status.open': 'Offen'
    , 'ticket.status.in_progress': 'In Bearbeitung'
    , 'ticket.status.waiting': 'Wartend'
    , 'ticket.status.assigned': 'Zugewiesen'
    , 'ticket.status.completed': 'Abgeschlossen'
    , 'ticket.priority.critical': 'Kritisch'
    , 'ticket.priority.high': 'Hoch'
    , 'ticket.priority.normal': 'Normal'
    , 'ticket.priority.low': 'Niedrig'
    , 'ticket.overdueTitle': 'Ticket ist überfällig'
    , 'ticketSidebar.toPlan': 'Zu planende Tickets'
    , 'ticketSidebar.mine': 'Meine'
    , 'ticketSidebar.mineAndOpen': 'Meine+Pool'
    , 'ticketSidebar.onlyMineTooltip': 'Nur mir zugewiesene Tickets anzeigen'
    , 'ticketSidebar.mineAndOpenTooltip': 'Mir zugewiesene und Pool-Tickets anzeigen'
    , 'ticketSidebar.searchPlaceholder': 'Tickets durchsuchen...'
    , 'ticketSidebar.seqBadge': 'Nummer im Vorgang'
    , 'ticketSidebar.loadingTickets': 'Lade Tickets...'
    , 'ticketSidebar.noPlanTickets': 'Keine zu planenden Tickets vorhanden'
    , 'ticketSidebar.tryAnotherSearch': 'Versuchen Sie einen anderen Suchbegriff'
    , 'ticketSidebar.expand': 'Sidebar ausklappen'
    , 'ticketSidebar.collapse': 'Sidebar einklappen'
    , 'ticketSidebar.newTicket': 'Neues Ticket erstellen'
    , 'ticketView.byUserHeader': 'Tickets - Übersicht nach Benutzern'
    , 'ticketView.includeCompleted': 'Auch Abgeschlossene'
    , 'global.noUsersFound': 'Keine Benutzer gefunden'
    , 'global.checkConsoleOrContactAdmin': 'Bitte prüfen Sie die Browser-Konsole für Details oder wenden Sie sich an den Administrator.'
    , 'global.time': 'Zeit'
    , 'global.pleaseWait': 'Warten Sie:'
    , 'global.showEarlyAppointments': 'Frühe Termine anzeigen'
    , 'global.expandTo1am': 'Bis 1 Uhr erweitern'
    , 'global.showLateAppointments': 'Späte Termine anzeigen'
    , 'global.expandTo11pm': 'Bis 23 Uhr erweitern'
    , 'global.noSubject': '(Kein Betreff)'
    , 'common.newActivity': 'Neue Aktivität'
    , 'global.view.dayLabel': 'TAG'
    , 'global.view.weekLabel': 'WOCHE'
    , 'global.view.weekendLabel': 'WOCHE+WE'
    , 'global.openShort': 'Pool'
    , 'global.removeFromPlanning': 'Aus Planung entfernen'
    , 'global.ticketLabel': 'Ticket:'
    , 'global.tooltipResizeHint': 'Tipp: Ziehen Sie an den Rändern, um die Dauer zu ändern'
    , 'global.movingTicket': 'Ticket wird verschoben...'
    , 'global.removingFromPlanning': 'Ticket wird aus Planung entfernt...'
    , 'global.changingTicketDuration': 'Ticket-Dauer wird geändert...'
    , 'global.at': 'um'
    , 'projects.newProject': 'Neuen Vorgang anlegen'
    , 'projects.addProjectShort': '+ Vorgang'
    , 'projects.dueTemplatesCount': '{{count}} Bibliothekseinträge fällig'
    , 'projects.selectProject': 'Vorgang auswählen'
    , 'projects.edit': 'Bearbeiten'
    , 'projects.planning': 'Zeitplanung'
    , 'projects.planningNeeded': 'Zeitplanung nötig'
    , 'projects.subprojects': 'Teilvorgänge'
    , 'projects.lastPlannedAt': 'Zuletzt eingeplant'
    , 'projects.neverPlanned': 'Noch nie eingeplant'
    , 'projects.lastPlannedTooltip': 'eingeplant am'
    , 'projects.lastPlannedBy': 'von'
    , 'projects.searchSubprojects': 'Teilvorgänge durchsuchen...'
    , 'projects.timeblocks': 'Zeitblöcke'
    , 'projects.searchTimeblocks': 'Zeitblöcke durchsuchen...'
    , 'projects.completed': 'Abgeschlossen'
    , 'projects.prevMonth': 'Vorheriger Monat'
    , 'projects.thisMonth': 'Dieser Monat'
    , 'projects.nextMonth': 'Nächster Monat'
    , 'projects.users': 'Benutzer'
    , 'projects.selectUsers': 'Benutzer auswählen'
    , 'projects.selectAll': 'Alle auswählen'
    , 'projects.deselectAll': 'Alle abwählen'
    , 'projects.weekend': 'Wochenende'
    , 'projects.dayHoursTooltip': 'Tag {{day}}: {{hours}} Std.'
    , 'projects.planPrompt.title': 'Zeitblöcke jetzt einplanen?'
    , 'projects.planPrompt.message': 'Möchten Sie die Zeitblöcke für "{{name}}" jetzt in der Kalenderansicht einplanen?'
    , 'projects.planPrompt.goToPlanning': 'Ja, zur Zeitplanung'
    , 'common.no': 'Nein'
    , 'common.active': 'Aktiv'
    , 'common.inactive': 'Inaktiv'
    , 'admin.title': 'Administration'
    , 'admin.refresh': 'Aktualisieren'
    , 'admin.loading': 'Lade Admin-Daten...'
    , 'admin.tabs.users': 'Benutzerverwaltung'
    , 'admin.tabs.server': 'Servereinstellungen'
    , 'admin.tabs.mailbox': 'Postfach-Überwachung'
    , 'admin.tabs.markers': 'Kalender Marker'
    , 'admin.tabs.notifications': 'Benachrichtigungen'
    , 'admin.tabs.customers': 'Kunden'
    , 'admin.customers.intro': 'Kundenstamm für die Zuordnung von Tickets. Eingehende Mails werden über die E-Mail-Domäne automatisch dem Kunden zugeordnet; die Kurzform wird in Tickets angezeigt.'
    , 'admin.customers.name': 'Kundenname'
    , 'admin.customers.namePlaceholder': 'z. B. Krebs Consulting & IT-Services Co KG'
    , 'admin.customers.shortName': 'Kurzform (Anzeige in Tickets)'
    , 'admin.customers.shortNamePlaceholder': 'z. B. Krebs IT'
    , 'admin.customers.domains': 'E-Mail-Domänen (Komma-getrennt)'
    , 'admin.customers.domainsPlaceholder': 'krebsit.de, krebs-itservices.de'
    , 'admin.customers.phone1': 'Telefon 1'
    , 'admin.customers.phone2': 'Telefon 2'
    , 'admin.customers.phone3': 'Telefon 3'
    , 'admin.customers.notes': 'Notizen'
    , 'admin.customers.active': 'Aktiv'
    , 'admin.customers.save': 'Speichern'
    , 'admin.customers.add': 'Kunde anlegen'
    , 'admin.customers.cancelEdit': 'Abbrechen'
    , 'admin.customers.shortNameCol': 'Kurzform'
    , 'admin.customers.domainsCol': 'Domänen'
    , 'admin.customers.phoneCol': 'Telefon'
    , 'admin.customers.activeCol': 'Aktiv'
    , 'admin.customers.edit': 'Bearbeiten'
    , 'admin.customers.none': 'Noch keine Kunden angelegt.'
    , 'admin.customers.loadError': 'Fehler beim Laden der Kunden.'
    , 'admin.customers.saveError': 'Fehler beim Speichern des Kunden.'
    , 'admin.customers.searchPlaceholder': 'Suchen: Name, Kurzform, Domäne, Telefon, Ansprechpartner…'
    , 'admin.customers.searchLabel': 'Kunden durchsuchen'
    , 'admin.customers.archiveAction': 'Archivieren'
    , 'admin.customers.reactivateAction': 'Reaktivieren'
    , 'admin.customers.archived': 'Kunde archiviert.'
    , 'admin.customers.reactivated': 'Kunde reaktiviert.'
    , 'admin.customers.deleteTitle': 'Kunde löschen'
    , 'admin.customers.deleteMessage': 'Kunde „{{name}}" endgültig löschen? Das geht nur, solange keine Tickets, Vorgänge, Ansprechpartner oder Domänen darauf verweisen.'
    , 'admin.customers.deleted': 'Kunde gelöscht.'
    , 'admin.customers.deleteRefs': 'Kunde wird noch verwendet ({{refs}}) — bitte stattdessen archivieren.'
    , 'admin.customers.deleteError': 'Fehler beim Löschen des Kunden.'
    , 'admin.customers.refsTickets': 'Tickets'
    , 'admin.customers.refsArchived': 'Archiv-Tickets'
    , 'admin.customers.refsProjects': 'Vorgänge'
    , 'admin.customers.refsContacts': 'Ansprechpartner'
    , 'admin.customers.refsDomains': 'Domänen'
    , 'admin.customers.similarTitle': 'Mögliche Dublette'
    , 'admin.customers.similarMessage': 'Ähnliche Kunden gefunden: {{names}}. Trotzdem anlegen?'
    , 'admin.customers.similarConfirm': 'Trotzdem anlegen'
    , 'admin.customers.saved': 'Kunde gespeichert.'
    , 'admin.contacts.title': 'Ansprechpartner'
    , 'admin.contacts.countCol': 'Ansprechpartner'
    , 'admin.contacts.manage': 'Ansprechpartner verwalten'
    , 'admin.contacts.name': 'Name'
    , 'admin.contacts.phoneMobile': 'Mobil'
    , 'admin.contacts.phoneLandline': 'Festnetz'
    , 'admin.contacts.email': 'E-Mail'
    , 'admin.contacts.notes': 'Notizen'
    , 'admin.contacts.active': 'Aktiv'
    , 'admin.contacts.add': 'Anlegen'
    , 'admin.contacts.save': 'Speichern'
    , 'admin.contacts.cancelEdit': 'Abbrechen'
    , 'admin.contacts.none': 'Noch keine Ansprechpartner hinterlegt.'
    , 'admin.contacts.saved': 'Ansprechpartner gespeichert.'
    , 'admin.contacts.saveError': 'Fehler beim Speichern des Ansprechpartners.'
    , 'admin.contacts.archived': 'Ansprechpartner archiviert.'
    , 'admin.contacts.reactivated': 'Ansprechpartner reaktiviert.'
    , 'admin.contacts.deleteTitle': 'Ansprechpartner löschen'
    , 'admin.contacts.deleteMessage': 'Ansprechpartner „{{name}}" endgültig löschen? Das geht nur, solange keine Tickets darauf verweisen.'
    , 'admin.contacts.deleted': 'Ansprechpartner gelöscht.'
    , 'admin.contacts.deleteRefs': 'Ansprechpartner wird noch verwendet ({{refs}}) — bitte stattdessen deaktivieren.'
    , 'admin.contacts.deleteError': 'Fehler beim Löschen des Ansprechpartners.'
    , 'customerField.contact': 'Ansprechpartner'
    , 'customerField.contactSelect': 'Ansprechpartner wählen…'
    , 'customerField.contactNone': '— kein Ansprechpartner —'
    , 'customerField.contactNoCustomer': 'Erst Kunde wählen'
    , 'customerField.createNewContact': 'Neuen Ansprechpartner anlegen'
    , 'customerField.createContactTitle': 'Neuen Ansprechpartner anlegen'
    , 'customerField.createContactHint': 'E-Mail und Notizen können später in der Kundenverwaltung ergänzt werden.'
    , 'customerField.createContactError': 'Ansprechpartner konnte nicht angelegt werden.'
    , 'customerField.numberCopied': 'Nummer kopiert.'
    , 'customerField.copyNumber': 'Nummer kopieren'
    , 'customerField.callTitle': 'Anrufen — Rechtsklick kopiert die Nummer'
    , 'ticketDetail.customer': 'Kunde'
    , 'admin.tabs.apiKeys': 'API-Keys'
    , 'admin.apiKeys.intro': 'Bearer-Keys für Maschinen-Zugriffe (KI-Agenten, Integrationen). Der Key agiert mit den Rechten des zugeordneten Benutzers und wird nur einmal angezeigt.'
    , 'admin.apiKeys.reachNotice': 'Ein Key liest alles, was der zugeordnete Benutzer lesen darf — auch ticket-verknüpfte E-Mail- und Teams-Inhalte. Laufzeit ist Pflicht: Abgelaufene Keys werden nicht verlängert, sondern neu ausgestellt (Rotation).'
    , 'admin.apiKeys.createdFor': 'Neuer Key für'
    , 'admin.apiKeys.copy': 'Kopieren'
    , 'admin.apiKeys.showOnce': 'Der Key wird nur dieses eine Mal angezeigt — jetzt sicher ablegen!'
    , 'admin.apiKeys.name': 'Name'
    , 'admin.apiKeys.namePlaceholder': 'z. B. Placetel-Agent'
    , 'admin.apiKeys.lifetime': 'Laufzeit'
    , 'admin.apiKeys.days': 'Tage'
    , 'admin.apiKeys.user': 'Agiert als Benutzer'
    , 'admin.apiKeys.userSelf': 'Ich selbst'
    , 'admin.apiKeys.creating': 'Erzeuge…'
    , 'admin.apiKeys.create': 'Key erzeugen'
    , 'admin.apiKeys.prefix': 'Key'
    , 'admin.apiKeys.expires': 'Läuft ab'
    , 'admin.apiKeys.lastUsed': 'Zuletzt benutzt'
    , 'admin.apiKeys.status': 'Status'
    , 'admin.apiKeys.statusActive': 'aktiv'
    , 'admin.apiKeys.statusExpired': 'abgelaufen'
    , 'admin.apiKeys.statusRevoked': 'widerrufen'
    , 'admin.apiKeys.revokedBy': 'Widerrufen von'
    , 'admin.apiKeys.revoke': 'Widerrufen'
    , 'admin.apiKeys.revokeConfirm': 'Diesen API-Key wirklich widerrufen? Anwendungen, die ihn benutzen, verlieren sofort den Zugriff.'
    , 'admin.apiKeys.revoked': 'API-Key widerrufen.'
    , 'admin.apiKeys.none': 'Noch keine API-Keys angelegt.'
    , 'admin.apiKeys.loadError': 'Fehler beim Laden der API-Keys.'
    , 'admin.apiKeys.createError': 'Fehler beim Erzeugen des API-Keys.'
    , 'admin.apiKeys.revokeError': 'Fehler beim Widerrufen des API-Keys.'
    , 'admin.notifications.heading': 'Benachrichtigungen'
    , 'admin.notifications.desc': 'Steuern Sie eine globale Hinweisleiste ganz oben auf der Webseite. Diese erscheint in Orange mit dem folgenden Text.'
    , 'admin.notifications.barLabel': 'Globale Hinweisleiste'
    , 'admin.notifications.enabled': 'Aktiviert'
    , 'admin.notifications.message': 'Nachricht'
    , 'admin.notifications.placeholder': 'Text für die Hinweisleiste...'
    , 'admin.notifications.save': 'Speichern'
    , 'common.cancel': 'Abbrechen'
    , 'common.close': 'Schließen'
    , 'common.ok': 'OK'
    , 'common.loading': 'Lade...'
    , 'confirm.title': 'Bestätigung'
    , 'terminAbsage.title': 'Termin mit Teilnehmern'
    , 'terminAbsage.message': 'Zu diesem Ticket gibt es einen Termin mit Teilnehmern: {{liste}}. Wenn Sie die Zuweisung entfernen, wird der Termin gelöscht, und die Teilnehmer bekommen eine Absage.'
    , 'terminAbsage.confirm': 'Trotzdem entfernen'
    , 'feedback.buttonTitle': 'Feedback geben (Fehler oder Wunsch melden)'
    , 'feedback.title': 'Feedback geben'
    , 'feedback.type': 'Art der Rückmeldung'
    , 'feedback.typeBug': 'Fehler'
    , 'feedback.typeWish': 'Wunsch'
    , 'feedback.message': 'Beschreibung'
    , 'feedback.messagePlaceholder': 'Was ist passiert bzw. was wünschst du dir? Je konkreter, desto besser.'
    , 'feedback.screenshot': 'Screenshot (optional)'
    , 'feedback.contextHint': 'Mitgeschickt wird automatisch: aktuelle Ansicht ({{url}}), Browser und Zeitpunkt.'
    , 'feedback.send': 'Senden'
    , 'feedback.sending': 'Sende…'
    , 'feedback.thanks': 'Danke! Deine Rückmeldung ist angekommen.'
    , 'feedback.error': 'Feedback konnte nicht gesendet werden.'
    , 'admin.tabs.feedback': 'Feedback'
    , 'admin.feedback.title': 'Feedback'
    , 'admin.feedback.intro': 'Rückmeldungen aus dem Feedback-Button im Header. Nach Übernahme in ein Issue den Status auf „uebernommen" setzen und die Issue-Nummer eintragen.'
    , 'admin.feedback.filter': 'Status-Filter'
    , 'admin.feedback.empty': 'Keine Einträge für diesen Filter.'
    , 'admin.feedback.date': 'Datum'
    , 'admin.feedback.type': 'Typ'
    , 'admin.feedback.reporter': 'Melder'
    , 'admin.feedback.message': 'Nachricht'
    , 'admin.feedback.screenshot': 'Screenshot'
    , 'admin.feedback.status': 'Status'
    , 'admin.feedback.issue': 'Issue #'
    , 'admin.feedback.view': 'ansehen'
    , 'admin.feedback.notePlaceholder': 'Triage-Notiz…'
    , 'admin.feedback.loadError': 'Fehler beim Laden des Feedbacks.'
    , 'admin.feedback.updated': 'Feedback aktualisiert.'
    , 'admin.feedback.updateError': 'Fehler beim Aktualisieren des Feedbacks.'
    , 'admin.feedback.actions': 'Aktionen'
    , 'admin.feedback.deleteTitle': 'Feedback löschen?'
    , 'admin.feedback.deleteConfirm': 'Diesen Eintrag endgültig löschen? Das kann nicht rückgängig gemacht werden.'
    , 'admin.feedback.deleted': 'Feedback gelöscht.'
    , 'admin.feedback.deleteError': 'Fehler beim Löschen des Feedbacks.'
    , 'ticketDetail.customerNone': '— kein Kunde —'
    , 'ticketDetail.customerSelect': 'Kunde wählen…'
    , 'ticketDetail.customerEdit': 'Kunde zuordnen'
    , 'customerField.createNew': 'Neuen Kunden anlegen'
    , 'customerField.createTitle': 'Neuen Kunden anlegen'
    , 'customerField.createConfirm': 'Anlegen'
    , 'customerField.createError': 'Kunde konnte nicht angelegt werden.'
    , 'customerField.createHint': 'Domänen, Telefonnummern und weitere Daten können später in der Kundenverwaltung ergänzt werden.'
    , 'ui.noResults': 'Keine Treffer'
    , 'admin.server.customersFeatureDesc': 'Blendet das Kundenfeld in Tickets, den Admin-Tab „Kunden" und die automatische Kundenzuordnung im Mail-Eingang komplett aus — für Installationen ohne Kundenbezug. Bestandsdaten bleiben erhalten.'
    , 'common.save': 'Speichern'
    , 'common.saving': 'Speichere...'
    , 'common.never': 'Nie'
    , 'admin.users.email': 'E-Mail'
    , 'admin.users.name': 'Name'
    , 'admin.users.lastLogin': 'Letzter Login'
    , 'admin.users.admin': 'Admin'
    , 'admin.users.manager': 'Manager'
    , 'admin.users.ticketworker': 'Ticketworker'
    , 'admin.users.readonly': 'Readonly'
    , 'admin.users.licensed': 'Lizenziert'
    , 'admin.users.active': 'Aktiv'
    , 'admin.users.adminTooltip': 'Zugang zum Admin-Bereich (rein technisch: Benutzer, Postfächer, KI, API-Keys). Gibt KEINE Ticketrechte — dafür zusätzlich Ticketworker/Manager setzen.'
    , 'admin.users.managerTooltip': 'Wie Ticketworker, zusätzlich: fremde Tickets bearbeiten, abschließen und mergen, Tickets löschen, alle Kommentare bearbeiten, „Alle Tickets"/„Alle Kalender", andere einplanen, Kundenverwaltung.'
    , 'admin.users.ticketworkerTooltip': 'Darf eigene Tickets (zugewiesene und selbst erstellte) bearbeiten und abschließen, Pool-Tickets übernehmen, Tickets übergeben und sich selbst einplanen. Ohne dieses Häkchen: nur lesen.'
    , 'admin.users.readonlyTooltip': 'Nur-Lese-Konto: überstimmt ALLE anderen Rollen — sehen ja, ändern nein (auch als Manager). Feedback geben und eigene Ansichts-Einstellungen bleiben möglich.'
    , 'admin.users.licensedTooltip': 'Lizenzplatz: taucht in der Zuweisen-Auswahl der Tickets auf. Keine weiteren Auswirkungen.'
    , 'admin.users.activeTooltip': 'Darf sich anmelden. Deaktiviert = Anmeldung und alle API-Zugriffe blockiert.'
    , 'admin.users.none': 'Keine Benutzer gefunden.'
    , 'admin.users.systemAdminTooltip': 'System-Administrator-Rechte können nicht entfernt werden'
    , 'admin.users.systemAdminBadge': 'System-Administrator (nicht entfernbar)'
    , 'admin.error.permission': 'Keine Berechtigung für den Admin-Bereich.'
    , 'admin.error.load': 'Fehler beim Laden der Admin-Daten.'
    , 'admin.users.roleUpdated': 'Benutzerrolle erfolgreich aktualisiert.'
    , 'admin.users.roleUpdateError': 'Fehler beim Aktualisieren der Benutzerrolle.'
    , 'admin.server.allowedEmails': 'Erlaubte E-Mail-Adressen'
    , 'admin.server.desc': 'Beschränken Sie den Zugang auf bestimmte E-Mail-Adressen oder Domains. Leer lassen für alle O365-Benutzer.'
    , 'admin.server.allowedPlaceholder': 'Beispiele:\nuser1@example.com, user2@example.com\n*@example.com\nadmin@company.de, *@company.de'
    , 'admin.server.show': 'Anzeigen'
    , 'admin.server.hide': 'Ausblenden'
    , 'admin.server.formatting': 'Formatierung:'
    , 'admin.server.formatEmail': 'user@example.com - Exakte E-Mail-Adresse'
    , 'admin.server.formatDomain': '*@example.com - Alle Adressen der Domain'
    , 'admin.server.formatMultiple': 'Mehrere Adressen mit Komma trennen'
    , 'admin.server.allowedSaved': 'Erlaubte E-Mail-Adressen erfolgreich gespeichert.'
    , 'admin.server.allowedSaveError': 'Fehler beim Speichern der erlaubten E-Mail-Adressen.'
    , 'admin.server.allowedLoadError': 'Die erlaubten E-Mail-Adressen konnten nicht geladen werden. Speichern ist gesperrt, damit die bestehende Liste nicht überschrieben wird. Bitte die Seite neu laden.'
    , 'admin.server.unsaved': 'Ungespeicherte Änderungen'
    , 'admin.mailbox.heading': 'Postfach-Überwachung'
    , 'admin.mailbox.desc': 'Konfigurieren Sie E-Mail-Postfächer für die automatische Ticket-Erstellung und -Zuordnung.'
    , 'admin.mailbox.noneTitle': 'Noch keine Postfächer konfiguriert.'
    , 'admin.mailbox.noneDesc': 'Klicken Sie auf "Postfach hinzufügen", um die automatische E-Mail-Verarbeitung zu aktivieren.'
    , 'admin.mailbox.col.address': 'Postfach'
    , 'admin.mailbox.col.status': 'Status'
    , 'admin.mailbox.col.interval': 'Intervall'
    , 'admin.mailbox.col.successFolder': 'Erfolg-Ordner'
    , 'admin.mailbox.col.errorFolder': 'Fehler-Ordner'
    , 'admin.mailbox.col.lastProcessed': 'Letzte Verarbeitung'
    , 'admin.mailbox.col.actions': 'Aktionen'
    , 'admin.mailbox.processNowTooltip': 'Jetzt verarbeiten'
    , 'admin.mailbox.deleteTooltip': 'Postfach-Überwachung löschen'
    , 'admin.mailbox.addButton': 'Postfach hinzufügen'
    , 'admin.mailbox.addHeading': 'Neues Postfach hinzufügen'
    , 'admin.mailbox.addrLabel': 'Postfach-Adresse *'
    , 'admin.mailbox.intervalLabel': 'Prüfintervall (Sekunden)'
    , 'admin.mailbox.successFolderLabel': 'Erfolg-Ordner'
    , 'admin.mailbox.errorFolderLabel': 'Fehler-Ordner'
    , 'admin.mailbox.addSubmit': 'Hinzufügen'
    , 'admin.mailbox.added': 'Postfach-Überwachung wurde erfolgreich hinzugefügt.'
    , 'admin.mailbox.addError': 'Fehler beim Hinzufügen der Postfach-Überwachung.'
    , 'admin.mailbox.updated': 'Postfach-Einstellungen wurden erfolgreich aktualisiert.'
    , 'admin.mailbox.updateError': 'Fehler beim Aktualisieren der Postfach-Einstellungen.'
    , 'admin.mailbox.confirmDelete': 'Möchten Sie diese Postfach-Überwachung wirklich löschen?'
    , 'admin.mailbox.deleted': 'Postfach-Überwachung wurde erfolgreich gelöscht.'
    , 'admin.mailbox.deleteError': 'Fehler beim Löschen der Postfach-Überwachung.'
    , 'admin.mailbox.processStarted': 'Manuelle Verarbeitung gestartet'
    , 'admin.mailbox.processError': 'Fehler bei der manuellen Verarbeitung'
    , 'admin.markers.heading': 'Kalender Marker Konfiguration'
    , 'admin.markers.desc': 'Verwalten Sie die verfügbaren Kalender Marker für Tickets. Diese Marker können von allen Benutzern verwendet werden.'
    , 'admin.markers.current': 'Aktuelle Marker'
    , 'admin.markers.none': 'Noch keine Kalender Marker konfiguriert.'
    , 'admin.markers.col.value': 'Wert'
    , 'admin.markers.col.label': 'Label'
    , 'admin.markers.col.icon': 'Symbol'
    , 'admin.markers.col.color': 'Farbe'
    , 'admin.markers.col.status': 'Status'
    , 'admin.markers.col.sort': 'Sortierung'
    , 'admin.markers.col.actions': 'Aktionen'
    , 'admin.markers.addButton': 'Marker hinzufügen'
    , 'admin.markers.addHeading': 'Neuen Kalender Marker hinzufügen'
    , 'admin.markers.valueLabel': 'Wert (eindeutig)'
    , 'admin.markers.valuePlaceholder': 'z.B. extern, intern'
    , 'admin.markers.labelLabel': 'Label'
    , 'admin.markers.labelPlaceholder': 'z.B. Extern, Intern'
    , 'admin.markers.iconLabel': 'Symbol (Emoji)'
    , 'admin.markers.colorLabel': 'Farbe'
    , 'admin.markers.sortLabel': 'Sortierung'
    , 'admin.markers.create': 'Marker erstellen'
    , 'admin.markers.cancel': 'Abbrechen'
    , 'admin.markers.editHeading': 'Marker bearbeiten'
    , 'admin.markers.valueReadonly': 'Wert (nicht änderbar)'
    , 'common.edit': 'Bearbeiten'
    , 'common.delete': 'Löschen'
    , 'common.hide': 'Ausblenden'
    , 'common.show': 'Anzeigen'
    , 'common.unknown': 'Unbekannt'
    , 'common.notAvailable': 'Nicht verfügbar'
    , 'common.opening': 'Öffne…'
    , 'common.creating': 'Erstelle...'
    , 'common.add': 'Hinzufügen'
    , 'common.adding': 'Füge hinzu...'
    , 'common.back': 'Zurück'
    , 'common.unassigned': 'Nicht zugewiesen'
    , 'email.detail.title': 'E-Mail Details'
    , 'email.detail.content': 'Inhalt'
    , 'email.detail.imagesLoading': ' (Bilder werden geladen...)'
    , 'email.detail.from': 'Von:'
    , 'email.detail.to': 'An:'
    , 'email.detail.received': 'Empfangen:'
    , 'email.subject.none': '(Kein Betreff)'
    , 'email.flagged': 'Markiert'
    , 'email.importance.high': 'Hoch'
    , 'email.importance.normal': 'Normal'
    , 'email.importance.low': 'Niedrig'
    , 'email.body.empty': 'Kein Inhalt verfügbar'
    , 'email.body.frameTitle': 'E-Mail-Inhalt'
    , 'email.body.processing': 'Inhalt wird verarbeitet...'
    , 'email.body.imageLoadFailed': 'Bild konnte nicht geladen werden'
    , 'email.body.unknownFormat': 'Unbekanntes E-Mail-Format'
    , 'email.body.imageNotAvailable': '[Bild nicht verfügbar]'
    , 'email.attachments.title': 'Anhänge'
    , 'email.attachments.unknownSize': 'Unbekannte Größe'
    , 'email.attachments.inlineImages': 'eingebettete Bilder'
    , 'email.addToTicket.title': 'E-Mail zu Ticket hinzufügen'
    , 'email.addToTicket.modalTitle': 'E-Mail zu Ticket hinzufügen'
    , 'email.addToTicket.button': 'Zu einem Ticket hinzufügen'
    , 'email.reply.title': 'Auf E-Mail antworten'
    , 'email.reply.button': 'Antworten'
    , 'email.reply.createTitle': 'E-Mail-Antwort erstellen'
    , 'email.reply.ticketDecision': 'Diese E-Mail ist noch nicht mit einem Ticket verknüpft. Möchten Sie ein neues Ticket erstellen oder ohne Ticket antworten?'
    , 'email.reply.withoutTicket': 'Ohne Ticket antworten'
    , 'email.reply.createAndRespond': 'Ticket erstellen & antworten'
    , 'email.reply.sendFromFallback': 'Keine Senden-Als Rechte an {{mailbox}}, sende aus eigenem Konto.'
    , 'email.preview.title': 'E-Mail-Vorschau:'
    , 'email.preview.from': 'Von:'
    , 'email.preview.subject': 'Betreff:'
    , 'email.preview.preview': 'Vorschau:'
    , 'email.createFromEmail.title': 'Neues Ticket aus E-Mail erstellen'
    , 'teams.addToTicket.modalTitle': 'Teams-Nachricht zu Ticket hinzufügen'
    , 'ticket.create.new': 'Neues Ticket erstellen:'
    , 'ticket.create.submit': 'Ticket erstellen'
    , 'ticket.create.button': 'Neues Ticket erstellen'
    , 'ticket.addToExisting': 'Zu vorhandenem Ticket hinzufügen:'
    , 'ticket.filter.placeholder': 'Nach Ticketnummer oder Titel suchen...'
    , 'ticket.filter.placeholderCustomer': 'Nach Ticketnummer, Titel, Kunde oder Ansprechpartner suchen...'
    , 'contactSearch.placeholder': 'Kontaktsuche…'
    , 'contactSearch.label': 'Kontaktsuche: Kunden und Ansprechpartner'
    , 'contactSearch.title': 'Kontaktsuche'
    , 'contactSearch.results': 'Treffer der Kontaktsuche'
    , 'contactSearch.minChars': 'Mindestens zwei Zeichen eingeben.'
    , 'contactSearch.loading': 'Kontakte werden geladen…'
    , 'contactSearch.none': 'Keine Treffer.'
    , 'contactSearch.moreContacts': '+{{count}} weitere Ansprechpartner'
    , 'contactSearch.moreCustomers': '{{count}} weitere Treffer — Suche verfeinern'
    , 'contactSearch.openAdmin': 'In der Kundenverwaltung öffnen'
    , 'ticket.loading': 'Lade Tickets...'
    , 'ticket.noneOpen': 'Keine offenen Tickets gefunden.'
    , 'ticket.noneMatchFilter': 'Keine Tickets gefunden, die dem Filter entsprechen.'
    , 'form.title': 'Titel'
    , 'form.description': 'Beschreibung'
    , 'form.priority': 'Priorität'
    , 'form.assignedTo': 'Zugewiesen an'
    , 'form.category': 'Kategorie'
    , 'priority.low': 'Niedrig'
    , 'priority.normal': 'Normal'
    , 'priority.high': 'Hoch'
    , 'priority.critical': 'Kritisch'
    , 'welcome.title': 'Willkommen bei KayCee Ticket'
    , 'welcome.lead': 'Greifen Sie sicher auf Ihre Microsoft 365 E-Mails und Kalender zu. Dieses Interface bietet Ihnen eine moderne und benutzerfreundliche Oberfläche für Ihre Office 365-Daten.'
    , 'welcome.button.setupStart': '⚙️ Setup starten'
    , 'welcome.button.signInM365': 'Mit Microsoft 365 anmelden'
    , 'welcome.loadingStatus': 'Lade Status...'
    , 'welcome.feature.email.title': 'E-Mail-Zugriff'
    , 'welcome.feature.email.desc': 'Lesen Sie Ihre E-Mails direkt über die Microsoft Graph API'
    , 'welcome.feature.calendar.title': 'Kalender-Integration'
    , 'welcome.feature.calendar.desc': 'Überblick über Ihre anstehenden Termine und Ereignisse'
    , 'welcome.feature.oauth.title': 'Sichere OAuth-Authentifizierung'
    , 'welcome.feature.oauth.desc': 'Ihre Daten bleiben sicher durch Microsofts OAuth 2.0'
    , 'welcome.feature.ui.title': 'Moderne Benutzeroberfläche'
    , 'welcome.feature.ui.desc': 'Responsive Design für Desktop und Mobile'
    , 'welcome.setupNotice.title': '⚙️ Ersteinrichtung erforderlich'
    , 'welcome.setupNotice.desc': 'Diese Anwendung muss zunächst mit Ihrem Microsoft 365 Tenant konfiguriert werden. Klicken Sie auf "Setup starten" für eine interaktive Anleitung.'
    , 'setup.success': 'Setup erfolgreich abgeschlossen! Sie können sich jetzt anmelden.'
    , 'setup.errorUnknown': 'Unbekannter Fehler beim Setup-Test'
    , 'setup.testFailed': 'Setup-Test fehlgeschlagen: {{message}}'
    , 'email.list.noEmails': 'Keine E-Mails gefunden'
    , 'email.list.noneMatchFilters': 'Keine E-Mails entsprechen den aktiven Filtern'
    , 'email.list.adjustFilters': 'Entfernen Sie Filter oder passen Sie die Filterkriterien an'
    , 'teamsView.loadError': 'Fehler beim Laden der Teams-Nachrichten'
    , 'teamsView.noContent': 'Nachricht ohne Inhalt'
    , 'teamsView.unknownSender': 'Unbekannter Absender'
    , 'teamsView.loading': 'Teams-Nachrichten werden geladen...'
    , 'teamsView.title': 'Teams-Nachrichten'
    , 'teamsView.empty': 'Keine Teams-Nachrichten'
    , 'teamsView.emptyHint': 'Es wurden keine aktuellen Teams-Nachrichten gefunden.'
    , 'teamsView.count': '{{count}} Nachrichten (letzte 30)'
    , 'teamsView.justNow': 'gerade eben'
    , 'teamsView.inChat': 'in {{topic}}'
    , 'email.errors.draftCreate': 'Fehler beim Erstellen des E-Mail-Entwurfs.'
    , 'email.errors.noneSelected': 'Fehler: Keine E-Mail ausgewählt'
    , 'email.errors.replyCreate': 'Fehler beim Erstellen der E-Mail-Antwort: {{details}}'
    , 'email.reply.createErrorGeneric': 'Fehler beim Erstellen der E-Mail-Antwort.'
    , 'email.errors.addToTicket': 'Fehler beim Hinzufügen der E-Mail zum Ticket: {{details}}'
    , 'ticket.createErrorDetails': 'Fehler beim Erstellen des Tickets: {{details}}'
    , 'email.filter.invalidAddress': 'Bitte geben Sie eine gültige E-Mail-Adresse ein.'
    , 'teams.errors.noneSelected': 'Fehler: Keine Teams-Nachricht ausgewählt'
    , 'teams.errors.addToTicket': 'Fehler beim Hinzufügen der Teams-Nachricht zum Ticket: {{details}}'
    , 'eventToTicket.invalidEvent': 'Fehler: Ungültiger Termin.'
    , 'eventToTicket.createPlanError': 'Fehler beim Erstellen/Planen des Tickets.'
    , 'projectWizard.saveSubprojectsError': 'Fehler beim Speichern der Teilvorgänge oder Zeitblöcke. Details in der Konsole.'
    , 'setup.failed': 'Setup fehlgeschlagen: {{details}}'
    , 'setup.configError': 'Fehler bei der Konfiguration: {{details}}'
    , 'common.unknownError': 'Unbekannter Fehler'
    , 'app.sessionExpiredSso': 'Ihre Sitzung ist abgelaufen. Sie werden zur Anmeldung weitergeleitet.'
    , 'app.sessionExpiredManual': 'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.'
    , 'app.loginNow': 'Jetzt anmelden'
    , 'setup.subtitle': 'Konfigurieren Sie Ihre Microsoft 365 Integration'
    , 'setup.clickOn': 'Klicken Sie auf'
    , 'setup.goTo': 'Gehen Sie zu'
    , 'setup.choose': 'Wählen Sie'
    , 'setup.important': 'Wichtig:'
    , 'setup.new': 'NEU:'
    , 'setup.nextStep': 'Weiter zu Schritt {{n}}'
    , 'common.optional': 'Optional'
    , 'common.errorPrefix': 'Fehler:'
    , 'setup.step1.title': 'Schritt 1: Azure App Registration erstellen'
    , 'setup.step1.intro': 'Erstellen Sie eine neue App-Registrierung in Azure:'
    , 'setup.step1.goToPortal': 'Gehen Sie zum'
    , 'setup.step1.fillIn': 'Füllen Sie folgende Informationen aus:'
    , 'setup.step2.title': 'Schritt 2: Client Secret erstellen'
    , 'setup.step2.intro': 'Erstellen Sie ein Client Secret für Ihre App:'
    , 'setup.step2.description': 'Geben Sie eine Beschreibung ein (z.B. "O365 Interface Secret")'
    , 'setup.step2.expiry': 'Wählen Sie eine Gültigkeitsdauer (empfohlen: 24 Monate)'
    , 'setup.step2.copyNow': 'Kopieren Sie das Secret sofort - es wird nur einmal angezeigt!'
    , 'setup.step3.title': 'Schritt 3: API-Berechtigungen konfigurieren'
    , 'setup.step3.intro': 'Fügen Sie die erforderlichen API-Berechtigungen hinzu:'
    , 'setup.step3.addFollowing': 'Fügen Sie folgende Berechtigungen hinzu:'
    , 'setup.perm.userRead': 'Benutzerprofil lesen'
    , 'setup.perm.mailRead': 'E-Mails lesen'
    , 'setup.perm.mailReadWrite': 'E-Mails bearbeiten (für Gelesen/Ungelesen Status)'
    , 'setup.perm.mailSend': 'E-Mails versenden'
    , 'setup.perm.calRead': 'Kalender lesen'
    , 'setup.perm.calReadWrite': 'Kalendereinträge erstellen/bearbeiten (eigener Kalender)'
    , 'setup.perm.calShared': 'Kalendereinträge in geteilten/delegierten Kalendern'
    , 'setup.step3.forAllPermissions': 'für alle Berechtigungen'
    , 'setup.step3.adminConsentRequired': 'Die Administratoreinwilligung ist erforderlich für den ordnungsgemäßen Betrieb!'
    , 'setup.step3.newFeatures': 'Neue Features verfügbar:'
    , 'setup.step3.featureMarkRead': 'E-Mails als gelesen/ungelesen markieren'
    , 'setup.step3.featureSend': 'E-Mails direkt aus der Anwendung senden'
    , 'setup.step4.title': 'Schritt 4: Konfiguration eingeben'
    , 'setup.step4.clientIdHelp': 'Finden Sie diese auf der Overview-Seite Ihrer App'
    , 'setup.step4.clientIdPlaceholder': 'z.B. 12345678-1234-1234-1234-123456789012'
    , 'setup.step4.secretHelp': 'Das Secret, das Sie gerade erstellt haben'
    , 'setup.step4.secretPlaceholder': 'Ihr Client Secret'
    , 'setup.step4.tenantHelp': 'Leer lassen für Multi-Tenant oder spezifische Tenant-ID eingeben'
    , 'setup.step4.tenantPlaceholder': 'common (für Multi-Tenant)'
    , 'setup.step4.sessionHelp': 'Automatisch generiert oder eigenes eingeben'
    , 'setup.step4.sessionPlaceholder': 'Automatisch generiert'
    , 'setup.step4.generate': 'Generieren'
    , 'setup.step4.allowedEmails': 'Erlaubte E-Mail-Adressen'
    , 'setup.step4.allowedEmailsHelp': 'Beschränken Sie den Zugang auf bestimmte E-Mail-Adressen oder Domains. Leer lassen für alle O365-Benutzer.'
    , 'setup.step4.allowedEmailsPlaceholder': 'Beispiele:\nuser1@example.com, user2@example.com\n*@example.com\nadmin@company.de, *@company.de'
    , 'setup.step4.formatting': 'Formatierung:'
    , 'setup.step4.fmtExact': 'Exakte E-Mail-Adresse'
    , 'setup.step4.fmtDomain': 'Alle Adressen der Domain'
    , 'setup.step4.fmtComma': 'Mehrere Adressen mit Komma trennen'
    , 'setup.step4.saving': 'Konfiguriere...'
    , 'setup.step4.save': 'Konfiguration speichern'
    , 'setup.step5.title': 'Setup abgeschlossen!'
    , 'setup.step5.saved': 'Die Konfiguration wurde erfolgreich gespeichert!'
    , 'setup.step5.testing': 'Teste die Konfiguration...'
    , 'setup.step5.testOk': 'Test erfolgreich!'
    , 'setup.step5.worksFine': 'Die Konfiguration funktioniert korrekt.'
    , 'setup.step5.finish': 'Setup beenden'
    , 'setup.step5.testFailed': 'Test fehlgeschlagen'
    , 'setup.step5.retest': 'Erneut testen'
    , 'setup.step5.autoTest': 'Die Konfiguration wird automatisch getestet...'
    , 'setup.step5.manualTest': 'Manuell testen'
    , 'setup.info.permissions': 'Benötigte Berechtigungen:'
    , 'setup.info.userRead': 'Zum Lesen der Benutzerinformationen'
    , 'setup.info.mailRead': 'Zum Lesen der E-Mails'
    , 'setup.info.mailReadWrite': 'Zum Bearbeiten von E-Mails (Gelesen/Ungelesen)'
    , 'setup.info.mailSend': 'Zum Versenden von E-Mails'
    , 'setup.info.calRead': 'Zum Lesen der Kalender-Termine'
    , 'setup.info.calReadWrite': 'Zum Erstellen/Bearbeiten von Kalendereinträgen (eigener Kalender)'
    , 'setup.info.calShared': 'Zum Erstellen von Kalendereinträgen in geteilten Kalendern'
    , 'setup.info.access': 'Zugriffskontrolle:'
    , 'setup.info.accessRestrict': 'Beschränkung auf bestimmte E-Mail-Adressen möglich'
    , 'setup.info.accessWildcard': 'Wildcard-Unterstützung für ganze Domains (*@domain.com)'
    , 'setup.info.accessFlexible': 'Flexible Konfiguration über Setup-Prozess'
    , 'setup.info.accessEmpty': 'Leer lassen für alle O365-Benutzer'
    , 'common.accessDenied': 'Zugriff verweigert'
    , 'app.offlineQueueSentOne': 'Ihr offline geschriebener Kommentar wurde nachgesendet.'
    , 'app.offlineQueueSentMany': '{{count}} offline geschriebene Kommentare wurden nachgesendet.'
    , 'app.offlineQueueDropped': 'Ein offline geschriebener Kommentar konnte nicht nachgesendet werden und wurde verworfen.'
    , 'ticketDetail.commentQueued': 'Kein Netz — der Kommentar ist gespeichert und wird gesendet, sobald die Verbindung zurück ist.'
  },
  en: {
    'dayCalendar.title': 'Event day',
    'filters.keywordFilter': 'Keyword filter',
    'filters.keywordFilterPlaceholder': 'Enter one keyword per line...\nE.g.:\nBug\nFeature\nSupport',
    'filters.keywordFilterHelp': 'Tickets containing these keywords in title, description or category will be filtered out. Enter one keyword per line.',
    'filters.activeFilters': 'Active filters:',
    'filters.keywordsCount': '{{count}} keyword(s) filtered',
    'filters.noneActive': 'No filters active',
    'filters.hideReadEmails': 'Hide read emails',
    'filters.hideReadEmailsHelp': 'If enabled, emails already read will not be shown in the list.',
    'filters.onlyLastHours': 'Show only emails from the last hours',
    'filters.numHours': 'Number of hours:',
    'filters.onlyLastHoursHelp': 'Shows only emails from the last {{hours}} hours.',
    'filters.keywordEmailHelp': 'Emails containing these keywords in sender or subject will be filtered out. Enter one keyword per line.',
    'filters.hideReadEmailsActive': 'Read emails are hidden',
    'filters.onlyLastHoursActive': 'Only emails from the last {{hours}} hours',
    'projectWizard.step1.title': 'Create new operation',
    'projectWizard.step1.editTitle': 'Edit operation',
    'projectWizard.step2.title': 'Define sub-operations',
    'projectWizard.step3.title': 'Define time blocks',
    'projectWizard.labels.projectName': 'Operation name',
    'projectWizard.labels.projectDescription': 'Operation description',
    'projectWizard.labels.projectResponsible': 'Operation owner',
    'projectWizard.labels.subprojects': 'Sub-operations',
    'projectWizard.placeholders.projectName': 'Operation name',
    'projectWizard.placeholders.description': 'Description',
    'projectWizard.placeholders.assignedTo': 'Assigned to...',
    'projectWizard.options.none': 'None',
    'projectWizard.options.no': 'No',
    'projectWizard.options.with': 'Yes',
    'projectWizard.buttons.next': 'Next',
    'projectWizard.buttons.back': 'Back',
    'projectWizard.buttons.finish': 'Finish',
    'projectWizard.buttons.create': 'Create',
    'projectWizard.buttons.saveChanges': 'Save changes',
    'projectWizard.buttons.addSubproject': 'Add sub-operation',
    'projectWizard.buttons.addAnotherTimeblock': 'Another time block',
    'projectWizard.buttons.configureTimeblocks': 'Configure time blocks',
    'projectWizard.buttons.deleteSubproject': 'Delete sub-operation',
    'projectWizard.buttons.deleteProject': 'Delete operation',
    'projectWizard.buttons.addTimeblock': 'Time block',
    'projectWizard.buttons.addTimeblockFor': 'Time block for {{name}}',
    'projectWizard.subproject.title': 'Sub-operation title',
    'projectWizard.subproject.namePlaceholder': 'Sub-operation name',
    'projectWizard.subproject.description': 'Sub-operation description',
    'projectWizard.subproject.responsibleRequired': 'Responsible (required)',
    'projectWizard.timeblock.namePlaceholder': 'TIME BLOCK NAME',
    'projectWizard.timeblock.titleLabel': 'Time block title',
    'projectWizard.timeblock.titlePlaceholder': 'Time block title',
    'projectWizard.timeblock.descriptionLabel': 'Time block description',
    'projectWizard.timeblock.delete': 'Delete time block',
    'projectWizard.hints.titleSavedAs': 'Title will be saved as "{{project}} - TIME BLOCK NAME".',
    'projectWizard.validation.selectAssigneeForAllBlocks': 'Please select an assignee for all defined time blocks.',
    'projectWizard.confirm.deleteSubprojectAndTickets': 'Delete this sub-operation and all related tickets permanently?',
    'projectWizard.confirm.deleteProject': 'Delete the operation including all sub-operations and tickets permanently?',
    'projectWizard.errors.deleteSubproject': 'Sub-operation could not be deleted.',
    'projectWizard.errors.deleteSubprojectPartial': 'Sub-operation not deleted: {count} ticket(s) could not be removed.',
    'projectWizard.confirm.deleteTimeblock': 'Delete this time block?',
    'projectWizard.info.subprojectsToggleLocked': 'The sub-operations toggle is locked for existing operations.',
    'projectWizard.subTimeblocks.heading': 'Time blocks for {{name}}',
    'projectWizard.templates.title': 'Library',
    'projectWizard.templates.loading': 'Loading library...',
    'projectWizard.templates.none': 'Library is empty',
    'projectWizard.templates.searchPlaceholder': 'Search library...',
    'projectWizard.turnus.count': 'x times',
    'projectWizard.turnus.every': 'every x',
    'projectWizard.turnus.days': 'days',
    'projectWizard.turnus.weeks': 'weeks',
    'projectWizard.turnus.months': 'months',
    'projectWizard.turnus.years': 'years',
    'projectWizard.turnus.hint': 'Optional: e.g. "3 times every 2 weeks".',
    'projectWizard.turnus.heading': 'Recurrence',
    'projectWizard.templates.apply': 'Apply',
    'projectWizard.templates.delete': 'Delete',
    'projectWizard.templates.confirmDelete': 'Do you really want to delete this template?',
    'projectWizard.templates.deleteError': 'Failed to delete template. See console for details.',
    'projectWizard.templates.hasActiveProjects': 'This template cannot be deleted because {{count}} active recurrence operations are linked to it.',
    'projectWizard.templates.skipTurnus': 'Skip this cycle',
    'projectWizard.templates.skippedOnce': 'Cycle skipped once',
    'projectWizard.templates.fulfilledThisPeriod': 'Already scheduled by recurrence',
    'projectWizard.templates.turnusSkippedOnce': 'Current recurrence skipped',
    'projectWizard.templates.skipTurnusConfirm': 'Skip the recurrence once?',
    'projectWizard.templates.skipTurnusError': 'Failed to skip recurrence.',
    'projectWizard.templates.created': 'Template was created',
    'projectWizard.templates.deleted': 'Template deleted',
    'projectWizard.templates.undo': 'Undo',
    'projectWizard.templates.undoing': 'Restoring...',
    'projectWizard.templates.restored': 'Template restored',
    'projectWizard.templates.restoreError': 'Failed to restore template.',
    'projectWizard.templates.updated': 'Template updated',
    'projectWizard.templates.dueThisPeriod': 'To be scheduled this period',
    'projectWizard.buttons.addToTemplates': 'Add to library',
    'projectWizard.buttons.updateTemplate': 'Update library entry',
    'language.de': 'German',
    'language.en': 'English',
    'language.es': 'Spanish',

    'app.loading': 'Loading application...',
    'common.loading': 'Loading...',

    'mindmap.completed': 'Completed',
    'mindmap.subprojects': 'Sub-operations',
    'mindmap.timeblocks': 'Time blocks',
    'mindmap.project': 'Operation',

    'header.calendar': 'Calendar',
    'header.dashboard': 'Dashboard',
    'header.allTickets': 'All tickets',
    'header.allCalendars': 'All calendars',
    'header.projects': 'Operations',
    'header.searchDev': 'Search (in development)',
    'search.title': 'AI Search',
    'search.semanticBadge': 'semantic',
    // Frontend Phase 4 (Welle 4): frühere Baseline-Keys (i18n_known_missing)
    'admin.notifications.saved': 'Notification saved.',
    'admin.notifications.saveError': 'Failed to save the notification.',
    'admin.releaseNote.saved': 'Release note saved.',
    'admin.releaseNote.saveError': 'Failed to save the release note.',
    'admin.releaseNote.heading': 'Login notice (one-time)',
    'admin.releaseNote.desc': 'Controls only the one-time notice each user sees as an overlay after logging in. The version history in the "What\'s new?" modal comes from the CHANGELOG import at deploy time and is not maintained here.',
    'admin.releaseNote.versionsHeading': 'Version history ("What\'s new?")',
    'admin.releaseNote.versionsHint': 'Imported from the CHANGELOG at deploy time; shown here for reference only — it is not maintained in the admin area.',
    'admin.releaseNote.enabled': 'Enabled',
    'admin.releaseNote.title': 'Title',
    'admin.releaseNote.titlePlaceholder': 'e.g. Update v2.5.0',
    'admin.releaseNote.content': 'Content',
    'admin.releaseNote.contentPlaceholder': 'New features, changes, bug fixes...',
    'calendar.convertToTicket': 'Convert to ticket',
    'calendar.confirmDeleteEvent': 'Do you really want to delete this event?',
    'calendar.deletingEvent': 'Deleting event...',
    'calendar.deleteError': 'Failed to delete the event.',
    'calendar.deleteEvent': 'Delete entry',
    'calendar.dropChoice.title': 'Move event or create a new one?',
    'calendar.dropChoice.text': 'You moved an existing event. How would you like to proceed?',
    'calendar.dropChoice.move': 'Move',
    'calendar.dropChoice.createAnother': 'Create another',
    'calendar.updating': 'Updating...',
    'calendar.creating': 'Creating...',
    'calendar.createError': 'Failed to create.',
    'common.yes': 'Yes',
    'common.clear': 'Clear',
    'dashboard.showTodayCalendar': 'Show calendar view',
    'dashboard.hideTodayCalendar': 'Hide calendar view',
    'dashboard.expandToCalendar': 'Switch to calendar view',
    'dashboard.collapseCalendar': "Switch to today's appointments list",
    'dashboard.todayLabel': 'Today',
    'dashboard.releaseNote.defaultTitle': 'New features & changes',
    'email.minimize.title': 'Minimize',
    'email.maximize.title': 'Maximize',
    'event.addToTicket.modalTitle': 'Add event to ticket',
    'eventDetail.busy': 'Busy',
    'eventDetail.tentative': 'Tentative',
    'eventDetail.free': 'Free',
    'eventDetail.oof': 'Out of office',
    'eventDetail.appointment': 'Appointment',
    'eventDetail.noSubject': '(No subject)',
    'eventDetail.header': 'Appointment',
    'eventDetail.status': 'Status',
    'eventDetail.date': 'Date',
    'eventDetail.period': 'Time range',
    'eventDetail.location': 'Location',
    'eventDetail.organizer': 'Organizer',
    'eventDetail.attendees': 'Attendees',
    'eventDetail.joinMeeting': 'Join meeting',
    'eventDetail.teamsMarker': 'Teams meeting',
    'global.searchInCalendar': 'Search in calendar...',
    'global.prevResult': 'Previous result',
    'global.nextResult': 'Next result',
    'global.creating': 'Creating...',
    'header.aiSearch': 'AI Search',
    'header.loadingStatus': 'Loading status',
    'projects.heatmapLoading': 'Updating heatmap...',
    'projects.searchProjects': 'Search operations...',
    'projects.turnusConfigured': 'Recurrence configured',
    'projects.completePrompt.defaultProjectName': 'New operation',
    'projects.completePrompt.defaultBlockBase': 'Ticket',
    'projects.completePrompt.convertTitle': 'Do you need a follow-up appointment?',
    'projects.completePrompt.convertMessage': 'Do you need a follow-up appointment? Or is this topic really finished?',
    'projects.completePrompt.title': 'Operation complete',
    'projects.completePrompt.message': 'The last time block of this operation has been completed. Should the operation (and its sub-operations) also be marked as completed and archived?',
    'projects.completePrompt.project': 'Operation',
    'projects.completePrompt.subprojects': 'Sub-operations',
    'projects.completePrompt.keepOpen': 'No, I still need time',
    'projects.completePrompt.confirmConvert': "I'm done, close it",
    'projects.completePrompt.confirm': 'Yes, archive the operation',
    'projectWizard.confirm.deleteSubproject': 'Delete this sub-operation?',
    'projectWizard.templates.createError': 'Failed to save the template. Details in the console.',
    'projectWizard.info.turnusLocked': 'Recurrence cannot be changed in "Edit operation".',
    'projectWizard.templates.choiceTitle': 'Update library item or create a new one?',
    'projectWizard.templates.choiceText': 'You applied a template. How would you like to proceed?',
    'projectWizard.templates.updateExisting': 'Update existing item',
    'projectWizard.templates.createNew': 'Create new template',
    'projectWizard.timeblock.checklist': 'Checklist',
    'search.send': 'Send',
    'search.ai.answer': 'Suggestion / answer',
    'search.ai.followup.placeholder': 'Ask a follow-up…',
    'search.ai.debug': 'Debug (only visible here)',
    'search.ai.input': 'Search term',
    'search.ai.usedQueries': 'Search pairs used',
    'ticket.create.data': 'New ticket data:',
    'ticket.merge.error': 'Failed to merge the tickets.',
    'ticketDetail.aiSummary': 'AI summary',
    'ticketDetail.aiSummaryError': 'Failed to create the AI summary',
    'ticketDetail.aiDisabled': 'AI features are disabled.',
    'ticketDetail.descSummarize': 'Summarize description with AI',
    'ticketDetail.descSummarizeTitle': 'Summarize description',
    'ticketDetail.descSummarizeHint': 'Review the suggestion and adjust it if needed. When applied, the previous description is automatically saved as an internal comment.',
    'ticketDetail.descSummarizeLoading': 'The AI is summarizing the description…',
    'ticketDetail.descSummarizeApply': 'Apply',
    'ticketDetail.descSummarizeApplying': 'Applying…',
    'ticketDetail.descSummarizeApplied': 'Description applied — the original is saved as an internal comment in the history.',
    'ticketDetail.descSummarizeError': 'Error summarizing the description.',
    'ticketDetail.descSummarizeApplyError': 'Error applying the description.',
    'ticketDetail.confirmDeleteAttachment': 'Really delete this attachment?',
    'ticketDetail.deleteAttachment': 'Delete attachment',
    'ticketDetail.deleteAttachmentError': 'Failed to delete the attachment',
    'ticketDetail.loadingAttachments': 'Loading attachments...',
    'ticketDetail.file': 'file',
    'ticketDetail.files': 'files',
    'ticketDetail.prevImage': 'Previous image',
    'ticketDetail.nextImage': 'Next image',
    'ticketDetail.backToHistory': 'Back to history',
    'ticketDetail.meeting': 'Meeting',
    'ticketDetail.joinMeeting': 'Join meeting',
    'ticketDetail.markWaiting': 'Mark as waiting',
    'ticketDetail.waitingError': 'Failed to set to waiting',
    'ticketDetail.reopenTitle': 'Reopen ticket',
    'ticketDetail.reopenBlocked': 'Reopen blocked: the operation still has open tickets or time blocks.',
    'ticketDetail.reopenError': 'Failed to reopen the ticket',
    'ticketDetail.notFound': 'Ticket not found.',
    'ticketDetail.completeDate': 'Date',
    'ticketDetail.completeStart': 'Start time',
    'ticketDetail.completeEnd': 'End time',
    'ticketDetail.completeHint': 'Please enter date, start and end time. If time was already planned, the fields are prefilled.',
    'ticketDetail.completeWithoutTimes': 'Without times',
    'ticketDetail.completeMissingFields': 'Please enter date, start time and end time.',
    'ticketDetail.completeInvalidFields': 'Invalid date or time.',
    'ticketDetail.completeEndBeforeStart': 'The end time must be after the start time.',
    'ticketDetail.completePlanningError': 'Failed to update/create the calendar entry.',
    'ticketDetail.completeUnplanError': 'Failed to remove the calendar entry.',
    'ticketDetail.transferPlanningError': 'Failed to update/create the calendar entry before the handover.',
    'ticketDetail.transferHint': 'If you change the times, your calendar entry is updated/created before the handover.',
    'ticketDetail.sourceAssignee': 'Source person',
    'ticketDetail.usedPastTimeQuestion': 'Was the planned past time used?',
    'ticketDetail.usedPastTimeHint': 'If no, past calendar entries for this ticket are removed from your calendar.',
    'ticketSidebar.pool': 'Pool',
    'ticketSidebar.poolTooltip': 'Show pool tickets only',
    'search.placeholder': 'Full sentence for AI search, keywords for classic search',
    'search.aiDisabled': 'AI search is disabled. An administrator can enable it in the admin panel ("AI Integration" tab).',
    'search.sections.withTicket': 'Found content via classic keyword search',
    'search.sections.emailsOnly': 'Found emails without ticket link',
    'search.sections.teamsOnly': 'Found Teams messages without ticket link',
    'search.loading': 'Searching…',
    'search.noResults': 'No results',
    'search.noAnswer': 'No answer received.',
    'search.noContext': 'Follow-up questions are not available for this conversation.',
    'search.followupFailed': 'Follow-up failed.',
    'search.newSearch': 'New search',
    'search.history': 'History',
    'search.historyEmpty': 'No saved conversations yet.',
    'search.historyMessages': '{{count}} messages',
    'search.historyDelete': 'Delete conversation',
    'search.historyDeleteConfirm': 'Delete this conversation?',
    'search.deleteFailed': 'Delete failed.',
    'search.loadFailed': 'Could not load conversation.',
    'search.saveConversation': 'Save conversation',
    'search.savedToast': 'Conversation saved.',
    'search.saveFailed': 'Saving failed.',
    'search.attachToTicket': 'Attach to ticket',
    'search.attachAction': 'Attach',
    'search.attachSelectTicket': 'Select ticket',
    'search.attachHint': 'The conversation will be saved as an internal comment on the selected ticket.',
    'search.attachedToast': 'Saved as an internal comment on the ticket.',
    'search.attachFailed': 'Attach failed (missing permission?).',
    'search.attachCommentTitle': 'AI search – conversation',
    'search.roleQuestion': 'Question',
    'search.roleAnswer': 'Answer',
    'search.hits': 'Results',
    'search.emptyHint': 'Ask a question — the AI searches tickets, comments and emails.',
    'ticketDetail.aiSimilar': 'Similar incidents (AI search)',
    'aiSimilar.title': 'Similar incidents',
    'aiSimilar.similarTickets': 'Matching tickets',
    'aiSimilar.adopt': 'Adopt into ticket',
    'aiSimilar.adopted': 'Saved as an internal comment.',
    'aiSimilar.adoptFailed': 'Adopting failed.',
    'aiSimilar.commentTitle': 'AI search: similar incidents',
    'aiSimilar.failed': 'AI search failed.',
    'header.notifications': 'Notifications',
    'header.activities': 'Activities',
    'header.noNewActivities': 'No new activities',
    'header.activityNewTicket': 'New ticket',
    'header.activityComment': 'New comment',
    'header.activityEmail': 'Email received',
    'header.activityAttachment': 'New attachment',
    'header.activityGeneric': 'New activity',
    'header.assignedToYou': 'Assigned to you',
    'header.unassigned': 'Unassigned',
    'header.user': 'User',
    'header.admin': 'Administration',
    'header.enable': 'Enable',
    'header.enableNotifications': 'Enable desktop notifications',
    'header.enableNotificationsShort': 'Enable notifications',
    'header.notificationsBlocked': 'Allow notifications in browser? (currently blocked)',
    'header.logout': 'Sign out',
    'header.options': 'Options',
    'header.customers': 'Customer management',
    'customersPage.title': 'Customer management',
    'customersPage.disabled': 'The customer feature is disabled on this system.',
    'customersPage.forbidden': 'Customer management is reserved for managers.',
    'header.whatsNew': "What's new?",
    'header.menu': 'Menu',
    'ticketCard.justNow': 'Just now',
    'ticketCard.minutesAgo': '{{count}} min ago',
    'ticketCard.hoursAgo': '{{count}} h ago',
    'ticketCard.daysAgo': '{{count}} d ago',
    'ticketCard.newActivity': 'New activity',
    'ticketCard.projectTicket': 'Project ticket',
    'contextMenu.open': 'Open',
    'contextMenu.findInCalendar': 'Find in calendar',
    'contextMenu.mergeInto': 'Add to another ticket',
    'contextMenu.assignToMe': 'Assign to me',
    'contextMenu.unassign': 'Remove assignment',
    'contextMenu.assignError': 'Error assigning the ticket.',
    'contextMenu.unassignError': 'Error removing the assignment.',
    'contextMenu.moveNextWeek': 'Move to next week',
    'contextMenu.markBooked': 'Mark as booked',
    'contextMenu.unmarkBooked': 'No longer booked',
    'contextMenu.bookedSet': 'Marked as booked.',
    'contextMenu.bookedRemoved': '“Booked” mark removed.',
    'contextMenu.bookedError': '“Booked” could not be saved.',
    'contextMenu.movingNextWeek': 'Moving to next week...',
    'contextMenu.movedNextWeek': 'Moved to {{date}}',
    'contextMenu.moveNextWeekError': 'Moving to next week failed.',
    'app.updateAvailable': 'A new version of the application is available.',
    'app.reloadNow': 'Update now',
    'app.offline': 'Offline — the data shown may be outdated.',
    'app.degradedStale': 'No connection to the server — showing data as of {time}.',
    'app.degradedOffline': 'No connection to the server — some areas could not be loaded.',
    'header.poolMonitor': 'Pool monitor',
    'pool.title': 'Pool monitor',
    'pool.loading': 'Loading pool…',
    'pool.empty': 'No open tickets in the pool. 🎉',
    'pool.lastUpdated': 'As of',
    'pool.justNow': 'Just now',
    'pool.minutesAgo': '{{count}} min ago',
    'pool.hoursAgo': '{{count}} h ago',
    'pool.daysAgo': '{{count}} d ago',
    'dashboard.expandPool': 'Open pool as monitor view',
    'dashboard.field.attachments': 'Attachments',
    'dashboard.addAttachment': 'Add file/photo',
    'dashboard.removeAttachment': 'Remove attachment',
    'dashboard.attachmentUploadError': 'The ticket was created, but the attachments could not be uploaded.',
    'ticketDetail.ticketUpdateError': 'Error updating the ticket.',
    'header.myApiKeys': 'My API keys',
    'personalKeys.title': 'My API keys',
    'personalKeys.intro': 'Bearer keys for your own scripts and agents. A key carries exactly your user permissions and is shown only once.',
    'personalKeys.namePlaceholder': 'e.g. calendar script',
    'personalKeys.none': 'No personal API keys yet.',
    'personalKeys.loadError': 'Failed to load API keys.',
    'personalKeys.createError': 'Failed to create the API key.',
    'releaseNotes.historyTitle': "What's new?",
    'releaseNotes.versionLabel': 'Version',
    'releaseNotes.current': 'current',
    'releaseNotes.loadError': 'Could not load the version history.',
    'releaseNotes.empty': 'No release notes yet.',
    'header.login': 'Sign in',
    'header.setup': 'Setup',
    'header.projectPlanningDisabled': 'Disabled in operation planning',
    'header.backToProject': 'Back to operation',
    'header.projectPlanningFor': 'Planning for operation',
    'header.notice': 'Notice:',
    'header.dropHereNewTicket': 'Drop here to create new ticket',
    'header.unreadTitle': '{{count}} unread tickets/activities',
    'header.notificationsEnabled': 'Notifications enabled',
    'header.notificationsEnabledBody': 'You will now receive desktop notifications.',

    'ticketDetail.block': 'Time block',
    'ticketDetail.ticket': 'Ticket',
    'ticketDetail.toProject': 'Go to operation',
    'ticketDetail.projectLoadError': 'The operation could not be loaded.',
    'ticketDetail.edit': 'Edit',
    'ticketDetail.transfer': 'Transfer',
    'ticketDetail.transferTitle': 'Transfer ticket',
    'ticketDetail.complete': 'Complete',
    'ticketDetail.completeTitle': 'Complete ticket',
    'ticketDetail.history': 'History',
    'ticketDetail.attach': 'Attach files',
    'ticketDetail.newEmail': 'New email thread',
    'ticketDetail.newEmailError': 'The email draft could not be created. Please try again.',
    'ticketDetail.detachEmailTitle': 'Detach email from ticket',
    'ticketDetail.detachEmailConfirm': 'Detach the email "{{subject}}" from this ticket? It stays unchanged in Outlook; only the link to the ticket is removed.',
    'ticketDetail.detachEmail': 'Detach from ticket',
    'ticketDetail.detachEmailDone': 'Email detached from ticket.',
    'ticketDetail.detachEmailGone': 'This email is no longer linked to this ticket.',
    'ticketDetail.detachEmailAutomatic': 'This email was assigned automatically and cannot be detached.',
    'ticketDetail.detachEmailForbidden': 'You are not allowed to change this ticket.',
    'ticketDetail.detachEmailError': 'The email could not be detached.',
    'ticketDetail.addComment': 'Add comment',
    'ticketDetail.loadingHistory': 'Loading history...',
    'ticketDetail.noHistory': 'No history',
    'ticketDetail.download': 'Download',
    'ticketDetail.cancel': 'Cancel',
    'ticketDetail.adding': 'Adding...',
    'ticketDetail.enterComment': 'Enter new comment...',
    'ticketDetail.optionalComment': 'Enter optional comment...',
    'ticketDetail.status': 'Status',
    'ticketDetail.priority': 'Priority',
    'ticketDetail.plannedFor': 'Planned for',
    'ticketDetail.created': 'Created',
    'ticketDetail.createdBy': 'Created by',
    'ticketDetail.closedBy': 'Closed by',
    'ticketDetail.completeForeignHint': 'This ticket is assigned to {{assignee}} — it will still be closed in your name.',
    'ticketDetail.period': 'Period',
    'ticketDetail.filesDragOverlay': 'Drag files here to attach',
    'ticketDetail.loadingPreview': '🔄 Loading preview...',
    'ticketDetail.subject': 'Subject',
    'ticketDetail.from': 'From',
    'ticketDetail.to': 'To',
    'ticketDetail.received': 'Received',
    'ticketDetail.teamsMessage': 'Teams message',
    'ticketDetail.chat': 'Chat',
    'ticketDetail.sent': 'Sent',
    'ticketDetail.description': 'Description',
    'ticketDetail.system': 'System',
    'ticketDetail.unknown': 'Unknown',
    'ticketDetail.emailAdded': 'Email added',
    'ticketDetail.teamsMessageAdded': 'Teams message added',
    'ticketDetail.fileAdded': 'File added',
    'ticketDetail.comment': 'Comment',
    'ticketDetail.editTicketTitle': 'Edit ticket',
    'ticketDetail.outgoingEmail': 'Outgoing email',
    'ticketDetail.incomingEmail': 'Incoming email',
    'ticketDetail.unknownEmailDirection': 'Direction unknown',
    'ticketDetail.directMessage': 'Direct message',
    'ticketDetail.editCommentTitle': 'Edit comment',
    'ticketDetail.updateError': 'Error updating comment',
    'ticketDetail.confirmDeleteComment': 'Do you really want to delete this comment?',
    'ticketDetail.deleting': 'Deleting... ',
    'ticketDetail.deleteError': 'Error deleting comment',
    'ticketDetail.projectGeneratedTitle': 'Operation-generated title',
    'ticketDetail.fileTooLarge': 'File "{{name}}" is too large. Maximum is 10MB.',
    'ticketDetail.selectFilesLabel': 'Select files (max. 10MB per file)',
    'ticketDetail.selectedFiles': 'Selected files:',
    'ticketDetail.optionalFilesDescription': 'Optional description for the files...',
    'ticketDetail.uploading': 'Uploading...',
    'ticketDetail.uploadFilesCount': 'Upload {{count}} file(s)',
    'ticketDetail.pasteFromClipboard': 'Paste from clipboard',
    'ticketDetail.clipboardNotSupported': 'Clipboard reading is not supported by this browser.',
    'ticketDetail.clipboardEmpty': 'Clipboard has no supported content.',
    'ticketDetail.clipboardReadError': 'Error reading from clipboard.',
    'ticketDetail.pasteHint': 'Tip: You can paste content directly with Ctrl+V.',
    'ticketDetail.transferAssignee': 'Target person',
    'ticketDetail.selectTargetPersonError': 'Please select a target person.',
    'common.pleaseSelect': 'Please select',
    'email.read': 'Read',
    'email.unread': 'Unread',
    'email.markAsUnread': 'Mark as unread',
    'email.markAsRead': 'Mark as read',
    'email.detailTitle': 'Email details',

    'common.sessionExpired': 'Session expired. Please sign in again.',
    'calendar.errorLoad': 'Error loading events. Please try again later.',
    'projects.errorLoadUsers': 'Error loading users'
    , 'common.all': 'All'
    , 'common.none': 'None'
    , 'common.paused': '⚠️ Paused'
    , 'common.errors': '⚠️ {{count}} errors'
    , 'common.loadFailed': 'Could not be loaded'
    , 'common.retry': 'Try again'
    , 'teams.list.loading': 'Loading Teams messages…'
    , 'teams.list.loadFailed': 'Teams messages could not be loaded'
    , 'common.noEntries': 'No entries yet'
    , 'common.linkedToTicket': 'Linked to a ticket'
    , 'ticketDetail.totalSize': 'Total size:'
    // #329: Verwaltung — KI-Reiter, Vorlagen, Kalender-Marker, Protokoll
    , 'admin.templates.saveFailed': 'Could not save the reply templates.'
    , 'admin.templates.autoReplyPlaceholder': 'We have created a new ticket with the number [ticketnr] and will get back to you as soon as possible.'
    , 'admin.templates.discardChanges': 'Discard changes'
    , 'admin.markers.deleteConfirm': 'Are you sure you want to delete this calendar marker?'
    , 'admin.markers.deleteFailed': 'Could not delete the calendar marker.'
    , 'admin.markers.createFailed': 'Could not create the calendar marker.'
    , 'admin.markers.updateFailed': 'Could not update the calendar marker.'
    , 'admin.logs.trigger': 'Trigger'
    , 'admin.logs.empty': 'No entries'
    , 'admin.ai.saveFailed': 'Could not save the AI configuration.'
    , 'admin.ai.sectionDescription': 'Configure the connection to an AI provider (OpenAI or OpenRouter). The OpenAI base URL is prefilled.'
    , 'admin.ai.defaultHint': 'Preset as requested; can be adjusted here.'
    , 'admin.ai.summaryPromptHint': 'Prompt for the ticket summary in the detail view.'
    , 'admin.ai.model': 'Model'
    , 'admin.ai.choosePlease': 'Please choose…'
    , 'admin.ai.modelsHint': 'Models are loaded via the connection check.'
    , 'admin.ai.mailModeOff': 'Off — take the mail text as before'
    , 'admin.ai.mailModeShadow': 'Shadow — suggestion as an internal comment'
    , 'admin.ai.mailModeLive': 'Live — set AI description and priority'
    , 'admin.ai.mailModeHint': 'Processing of incoming support@ mails. \u201cShadow\u201d changes nothing on the ticket — the suggestion is filed as an internal comment for review.'
    , 'admin.ai.transcribeModel': 'Transcription model (voicemail)'
    , 'admin.ai.transcribeHint': 'Audio-capable model for voicemail transcription. Empty = default.'
    , 'admin.ai.embeddingModel': 'Embedding model'
    , 'admin.ai.embeddingProviderHint': 'Separate provider for /embeddings — needed when the chat runs via OpenRouter. Empty = chat provider.'
    , 'admin.ai.dimensionsPlaceholder': 'Model default'
    , 'admin.ai.dimensionsHint': 'e.g. 1024 saves two thirds of the storage with text-embedding-3-large. After a change: run the backfill!'
    , 'admin.ai.backfillRunning': 'Backfill running…'
    , 'admin.ai.backfillStart': 'Start backfill'
    , 'admin.ai.lastCheck': 'Last check:'
    , 'admin.tabs.features': 'Features'
    , 'admin.ai.switchesInFeatures': 'The features are switched on and off in the “Features” tab.'
    , 'admin.ai.disabledInFeatures': 'Switched off in the “Features” tab. The settings can still be prepared.'
    , 'admin.ai.allDisabledInFeatures': 'All AI features are switched off in the “Features” tab.'
    , 'admin.features.title': 'Features'
    , 'admin.features.description': 'All features that can be switched on and off. Each switch saves immediately. The settings stay in their tabs and are greyed out there while the feature is off.'
    , 'admin.features.loadError': 'The switches could not be loaded. Please try “Refresh”.'
    , 'admin.features.saved': 'Saved.'
    , 'admin.features.saveError': 'The switch could not be saved.'
    , 'admin.features.aiNotReady': 'No AI provider is set up yet (“AI Integration” tab: base URL, API key, model). Until then the AI features have no effect, even if they are switched on here.'
    , 'admin.features.inAiTab': 'Settings in the “AI Integration” tab'
    , 'admin.features.inCustomersTab': 'Managed in the “Customers” tab'
    , 'admin.features.searchAi': 'AI search'
    , 'admin.features.searchAiDesc': 'AI answer in the global search with follow-up questions, and “Similar tickets” in the detail view.'
    , 'admin.features.summaryAi': 'AI summary'
    , 'admin.features.summaryAiDesc': '“Summarize” and “Summarize description” buttons on the ticket.'
    , 'admin.features.mailAi': 'AI ticket creation from emails'
    , 'admin.features.voicemailAi': 'AI voicemail tickets'
    , 'admin.features.customers': 'Customer master data'
    , 'admin.features.darstellung': 'Appearance (default)'
    , 'admin.features.darstellungDesc': 'Look for everyone who has not chosen their own appearance under the gear icon.'
    , 'admin.server.darstellungSofort': 'The selection is saved immediately.'
    , 'darstellung.modern': 'Modern'
    , 'darstellung.klassisch': 'Classic'
    , 'einstellungen.titel': 'Settings'
    , 'einstellungen.darstellung': 'Appearance'
    , 'einstellungen.darstellungVorgabe': 'Installation default'
    , 'einstellungen.darstellungZurzeit': 'currently {name}'
    , 'einstellungen.sprache': 'Language'
    , 'einstellungen.apiKeysText': 'Keys for programs and agents that access the ticket system with your permissions.'
    , 'einstellungen.apiKeysVerwalten': 'Manage API keys'
    , 'einstellungen.speichernFehler': 'The setting could not be saved.'
    , 'einstellungen.kopfleiste': 'Header'
    , 'einstellungen.reiter': 'Tabs'
    , 'einstellungen.reiterFest': 'Start page, always visible'
    , 'einstellungen.reiterAnzeige': 'Tab display'
    , 'einstellungen.symbolUndText': 'Icon and text'
    , 'einstellungen.nurSymbol': 'Icon only'
    , 'einstellungen.kontaktsuche': 'Contact search in the header'
    , 'einstellungen.dashboard': 'Dashboard'
    , 'einstellungen.nachOben': 'Move {{name}} up'
    , 'einstellungen.nachUnten': 'Move {{name}} down'
    , 'einstellungen.letzterBereich': 'At least one section stays visible'
    , 'dashboard.pool': 'Pool'
    , 'dashboard.showBereich': 'Show {{name}}'
    , 'dashboard.alleAusgeblendet': 'All dashboard sections are hidden.'
    , 'dashboard.einstellungenOeffnen': 'Open settings'
    , 'admin.features.lineLabel': 'State as one line (for the deploy log):'
    , 'admin.ai.title': 'AI Integration'
    , 'admin.ai.groupProvider': 'Provider connection'
    , 'admin.ai.groupProviderHint': 'Applies to all AI features below.'
    , 'admin.ai.groupSearch': 'AI search'
    , 'admin.ai.groupIndex': 'Search index (embeddings)'
    , 'admin.ai.groupIndexHint': 'Basis for the “Hybrid” and “Semantic” search modes.'
    , 'admin.ai.groupSummary': 'AI summary'
    , 'admin.ai.groupVoicemail': 'Voicemail'
    , 'admin.ai.baseUrl': 'API base URL'
    , 'admin.ai.baseUrlExamples': 'Examples: https://api.openai.com/v1, https://openrouter.ai/api/v1'
    , 'admin.ai.apiKey': 'API key'
    , 'admin.ai.apiKeyStored': 'An API key is stored.'
    , 'admin.ai.showKey': 'Show'
    , 'admin.ai.hideKey': 'Hide'
    , 'admin.ai.debug': 'Debug'
    , 'admin.ai.testing': 'Testing…'
    , 'admin.ai.testConnection': 'Test connection & load models'
    , 'admin.ai.retrievalMode': 'Search mode (AI search)'
    , 'admin.ai.retrievalLexical': 'Lexical — as before (optimizer + text search)'
    , 'admin.ai.retrievalHybrid': 'Hybrid — text search + semantic (recommended after backfill)'
    , 'admin.ai.retrievalVector': 'Semantic — embedding index only'
    , 'admin.ai.retrievalHint': 'Enable Hybrid/Semantic only after a successful backfill (search index below).'
    , 'admin.ai.searchPrompt': 'Search prompt (default in English)'
    , 'admin.ai.perQueryFragmentCap': 'Fragments per search pair'
    , 'admin.ai.globalFragmentCap': 'Global fragment limit'
    , 'admin.ai.contextCharCap': 'Context character limit'
    , 'admin.ai.fullTicketsCount': 'Number of full tickets'
    , 'admin.ai.defaultValue': 'Default: {{wert}}'
    , 'admin.ai.embeddingModelHint': 'Empty = semantic search off. Recommended: text-embedding-3-large.'
    , 'admin.ai.embeddingBaseUrl': 'Embedding base URL (optional)'
    , 'admin.ai.embeddingApiKey': 'Embedding API key (optional)'
    , 'admin.ai.embeddingApiKeyPlaceholder': 'sk-… (empty = chat key)'
    , 'admin.ai.embeddingApiKeyStored': 'A separate embedding key is stored.'
    , 'admin.ai.dimensions': 'Dimensions (optional)'
    , 'admin.ai.indexStatus': 'Index status'
    , 'admin.ai.loadStatus': 'Load status'
    , 'admin.ai.testEmbedding': 'Test embedding'
    , 'admin.ai.chunksEmbedded': '{{eingebettet}}/{{gesamt}} chunks embedded'
    , 'admin.ai.chunksPending': '({{anzahl}} pending)'
    , 'admin.ai.backfillProgress': 'Backfill: {{phase}}, {{quellen}} sources, {{eingebettet}} embedded'
    , 'admin.ai.backfillLastError': 'last error:'
    , 'admin.ai.summaryPrompt': 'Summary prompt (default in English)'
    , 'admin.ai.voicemailToggleHint': 'Transcribe Placetel voicemail messages and create a prepared ticket. When inactive, a normal mail ticket is created.'
    , 'admin.ai.transcribePlaceholder': 'google/gemini-3.5-flash-lite (default)'
    , 'admin.ai.saveAllHint': 'Saves all sections at once.'
    // #329: Detailansicht einer Teams-Nachricht — vor Sprint 10 ohne jede Uebersetzung
    , 'teams.detail.title': 'Teams message — details'
    , 'teams.detail.from': 'From:'
    , 'teams.detail.chat': 'Chat:'
    , 'teams.detail.sent': 'Sent:'
    , 'teams.detail.edited': 'Edited:'
    , 'teams.detail.replyTo': 'In reply to:'
    , 'teams.detail.messageRef': 'Message #{{id}}'
    , 'teams.detail.contentHeading': 'Message content'
    , 'teams.detail.frameTitle': 'Teams message content'
    , 'teams.detail.subjectFallback': 'Teams message'
    , 'teams.detail.noContent': 'No content available'
    , 'teams.detail.unknownFormat': 'Unknown message format'
    , 'teams.detail.unknownSender': 'Unknown sender'
    , 'teams.chatType.oneOnOne': 'Direct message'
    , 'teams.chatType.group': 'Group chat'
    , 'teams.chatType.unknown': 'Unknown'
    , 'teams.importance.high': 'High'
    , 'teams.importance.urgent': 'Urgent'
    , 'teams.importance.normal': 'Normal'
    , 'dashboard.loading': 'Loading data...'
    , 'dashboard.todayEvents': 'Today events'
    , 'mobile.settings': 'Settings'
    , 'mobile.language': 'Language'
    , 'mobile.sendFeedback': 'Send feedback'
    , 'mobile.confirmTakeOver': 'Assign this ticket to you?'
    , 'mobile.eventNotes': 'Notes'
    , 'mobile.aiQuestion': 'Question for the AI'
    , 'mobile.newSearch': 'New search'
    , 'mobile.aiSearching': 'Searching…'
    , 'mobile.aiFoundTickets': 'Tickets found'
    , 'mobile.aiSearchError': 'The search did not work. Please try again.'
    , 'mobile.aiSearchDisabled': 'AI search is turned off on this installation.'
    , 'mobile.editComment': 'Edit comment'
    , 'mobile.deleteImage': 'Delete image'
    , 'mobile.imageDeleted': 'Image deleted.'
    , 'mobile.image': 'Image'
    , 'mobile.clearSearch': 'Clear search'
    , 'mobile.searchMine': 'Search my tickets'
    , 'mobile.searchPool': 'Search pool tickets'
    , 'mobile.tabToday': 'Today'
    , 'mobile.tabMine': 'Mine'
    , 'mobile.tabPool': 'Pool'
    , 'mobile.tabsLabel': 'Sections'
    , 'mobile.refresh': 'Refresh'
    , 'mobile.ticket': 'Ticket'
    , 'mobile.back': 'Back'
    , 'mobile.archived': 'Archived'
    , 'mobile.poolTicket': 'Pool ticket, not assigned'
    , 'mobile.actions': 'Actions'
    , 'mobile.takePhoto': 'Photo'
    , 'mobile.chooseFiles': 'Files'
    , 'mobile.uploading': 'Uploading…'
    , 'mobile.takeOver': 'Take over'
    , 'mobile.newComment': 'Comment'
    , 'mobile.commentPlaceholder': 'What did you do or find?'
    , 'mobile.send': 'Send'
    , 'mobile.sending': 'Sending…'
    , 'mobile.showMore': 'Show more'
    , 'mobile.showLess': 'Show less'
    , 'mobile.history': 'History'
    , 'mobile.historyEmpty': 'No entries yet.'
    , 'mobile.ticketForbidden': 'You cannot see this ticket.'
    , 'mobile.ticketLoadError': 'The ticket could not be loaded.'
    , 'mobile.commentSaved': 'Comment saved.'
    , 'mobile.commentError': 'The comment could not be saved.'
    , 'mobile.uploadTooMany': 'At most {{count}} files at once.'
    , 'ticketCard.hasAttachments': 'Has attachments'
    , 'mobile.openEmail': 'Open email: {{subject}}'
    , 'mobile.openAndReply': 'Open and reply'
    , 'email.reply.popupBlocked': 'The draft is ready in Outlook but could not be opened here.'
    , 'ticketDetail.mailAttachments': 'Email attachments'
    , 'viewer.title': 'Attachment'
    , 'viewer.page': 'Page {{n}} of {{total}}'
    , 'viewer.zoomIn': 'Zoom in'
    , 'viewer.zoomOut': 'Zoom out'
    , 'viewer.zoomReset': 'Fit to screen'
    , 'viewer.delete': 'Delete attachment'
    , 'viewer.loadError': 'The file could not be loaded.'
    , 'viewer.noPreview': 'There is no preview for this file.'
    , 'viewer.prev': 'Previous attachment'
    , 'viewer.next': 'Next attachment'
    , 'ticketDetail.view': 'View'
    , 'mobile.uploadTooLarge': '“{{name}}” is larger than 10 MB.'
    , 'mobile.uploadDoneOne': 'File uploaded.'
    , 'mobile.uploadDoneMany': '{{count}} files uploaded.'
    , 'mobile.uploadError': 'Upload failed.'
    , 'mobile.takenOver': 'The ticket is now assigned to you.'
    , 'mobile.attachmentOpenError': 'The file could not be opened.'
    , 'mobile.installTitle': 'Install as an app?'
    , 'mobile.installAndroidText': 'Tickets then opens like an app from your home screen.'
    , 'mobile.installIosBefore': 'Tap'
    , 'mobile.installIosShare': 'Share'
    , 'mobile.installIosAfter': 'and then “Add to Home Screen”.'
    , 'mobile.installButton': 'Install'
    , 'mobile.installLater': 'Later'
    , 'mobile.installGotIt': 'Got it'
    , 'dashboard.noUpcomingEvents': 'No upcoming events'
    , 'dashboard.myTickets': 'My tickets'
    , 'dashboard.tickets': 'Tickets'
    , 'dashboard.emails': 'Emails'
    , 'dashboard.teams': 'Teams'
    , 'dashboard.hiddenSectionsLabel': 'Hidden sections:'
    , 'dashboard.showEmails': 'Show emails'
    , 'dashboard.showTeams': 'Show teams'
    , 'dashboard.hideEmails': 'Hide emails'
    , 'dashboard.hideTeams': 'Hide teams'
    , 'dashboard.configureTicketFilter': 'Configure ticket filter'
    , 'dashboard.configureEmailFilter': 'Configure email filter'
    , 'dashboard.configureEmailSection': 'Configure email section'
    , 'filters.additionalMailboxes': 'Additional mailboxes'
    , 'filters.additionalMailboxPlaceholder': 'Enter email address...'
    , 'filters.addMailbox': 'Add mailbox'
    , 'filters.removeMailbox': 'Remove mailbox'
    , 'filters.additionalMailboxesHelp': 'Add mailboxes you have full access to. Emails will be included in the section.'
    , 'filters.additionalMailboxesActive': '{{count}} additional mailbox(es)'
    , 'dashboard.configureTeamsFilter': 'Configure Teams filter'
    , 'dashboard.freeTime': 'Free time'
    , 'dashboard.searchTicketsPlaceholder': 'Search tickets...'
    , 'dashboard.searchMailsPlaceholder': 'Search emails...'
    , 'dashboard.searching': 'Searching…'
    , 'dashboard.retry': 'Retry'
    , 'dashboard.connecting': 'Connecting...'
    , 'dashboard.serviceIssues': 'Service connectivity issues'
    , 'dashboard.autoUpdatePausedFor': 'Auto refresh paused for:'
    , 'dashboard.sessionMayHaveExpired': 'Your session may have expired.'
    , 'dashboard.createTicketHeader': 'Create new ticket'
    , 'dashboard.dragDropHint': 'Drag & drop files here'
    , 'dashboard.attachmentsSuffix': 'attachments'
    , 'dashboard.field.title': 'Title'
    , 'dashboard.field.description': 'Description'
    , 'dashboard.field.priority': 'Priority'
    , 'dashboard.field.assignedTo': 'Assigned to'
    , 'dashboard.field.category': 'Category'
    , 'dashboard.placeholder.title': 'Enter ticket title...'
    , 'dashboard.placeholder.description': 'Enter ticket description...'
    , 'dashboard.placeholder.category': 'e.g. Support, Bug, Feature'
    , 'dashboard.unassigned': 'Unassigned'
    , 'dashboard.cancel': 'Cancel'
    , 'dashboard.createTicket': 'Create ticket'
    , 'dashboard.enterTitle': 'Please enter a title.'
    , 'dashboard.createTicketError': 'Error creating ticket.'
    , 'dashboard.saveEmailFilterError': 'Error saving filter settings.'
    , 'dashboard.saveTeamsFilterError': 'Error saving Teams filter settings.'
    , 'dashboard.saveTicketFilterError': 'Error saving ticket filter settings.'
    , 'dashboard.errorLoadData': 'Error loading data. Please try again later.'
    , 'dashboard.refreshError': 'Error refreshing data'
    , 'calendar.loading': 'Loading events...'
    , 'calendar.previousWeek': 'Previous week'
    , 'calendar.today': 'Today'
    , 'calendar.nextWeek': 'Next week'
    , 'calendar.backToWeekTooltip': 'Back to week view'
    , 'calendar.show14DaysTooltip': 'Show 14 days'
    , 'calendar.week': 'Week'
    , 'calendar.14days': '14 days'
    , 'calendar.updateError': 'Error updating events'
    , 'calendar.planNewTicketError': 'Error planning newly created ticket'
    , 'calendar.createTicketError': 'Error creating ticket.'
    , 'calendar.removePlanningError': 'Error removing ticket from planning'
    , 'calendar.changeDurationError': 'Error changing ticket duration'
    , 'calendar.placeTicketError': 'Error placing ticket'
    , 'calendar.newTicket': 'Create new ticket'
    , 'global.loading': 'Loading calendar...'
    , 'global.errorLoad': 'Error loading calendar data. Please try again later.'
    , 'global.refreshError': 'Error refreshing global calendar'
    , 'global.permissionDenied': '⚠️ No permission: You cannot create calendar entries for {{userEmail}}. Extended calendar permissions required.'
    , 'global.userNotFound': '⚠️ User {{userEmail}} not found or calendar unavailable.'
    , 'global.moveTicketError': 'Error moving ticket: {{details}}'
    , 'global.planNewTicketError': 'Error planning newly created ticket'
    , 'global.createTicketError': 'Error creating ticket.'
    , 'global.removePlanningError': 'Error removing ticket from planning'
    , 'global.changeDurationError': 'Error changing ticket duration'
    , 'global.placeTicketError': 'Error placing ticket'
    , 'global.users': 'Users'
    , 'global.onlyTodayTooltip': 'Show only current day'
    , 'global.weekTooltip': 'Show current week (Mon–Fri)'
    , 'global.weekendTooltip': 'Show current week including weekend (Mon–Sun)'
    , 'common.noTicketsFound': 'No tickets found'
    , 'common.noOpenTickets': 'No open tickets'
    , 'common.tryOtherSearchTerms': 'Try other search terms'
    , 'ticket.status.open': 'Open'
    , 'ticket.status.in_progress': 'In progress'
    , 'ticket.status.waiting': 'Waiting'
    , 'ticket.status.assigned': 'Assigned'
    , 'ticket.status.completed': 'Completed'
    , 'ticket.priority.critical': 'Critical'
    , 'ticket.priority.high': 'High'
    , 'ticket.priority.normal': 'Normal'
    , 'ticket.priority.low': 'Low'
    , 'ticket.overdueTitle': 'Ticket is overdue'
    , 'ticketSidebar.toPlan': 'Tickets to schedule'
    , 'ticketSidebar.mine': 'Mine'
    , 'ticketSidebar.mineAndOpen': 'Mine+Open'
    , 'ticketSidebar.onlyMineTooltip': 'Show only tickets assigned to me'
    , 'ticketSidebar.mineAndOpenTooltip': 'Show my assigned and open tickets'
    , 'ticketSidebar.searchPlaceholder': 'Search tickets...'
    , 'ticketSidebar.seqBadge': 'Number within project'
    , 'ticketSidebar.loadingTickets': 'Loading tickets...'
    , 'ticketSidebar.noPlanTickets': 'No tickets to schedule'
    , 'ticketSidebar.tryAnotherSearch': 'Try another search term'
    , 'ticketSidebar.expand': 'Expand sidebar'
    , 'ticketSidebar.collapse': 'Collapse sidebar'
    , 'ticketSidebar.newTicket': 'Create new ticket'
    , 'ticketView.byUserHeader': 'Tickets - Overview by users'
    , 'ticketView.includeCompleted': 'Include completed'
    , 'global.noUsersFound': 'No users found'
    , 'global.checkConsoleOrContactAdmin': 'Please check the browser console for details or contact the administrator.'
    , 'global.time': 'Time'
    , 'global.pleaseWait': 'Please wait:'
    , 'global.showEarlyAppointments': 'Show early appointments'
    , 'global.expandTo1am': 'Extend to 1 am'
    , 'global.showLateAppointments': 'Show late appointments'
    , 'global.expandTo11pm': 'Extend to 11 pm'
    , 'global.noSubject': '(No subject)'
    , 'common.newActivity': 'New activity'
    , 'global.view.dayLabel': 'DAY'
    , 'global.view.weekLabel': 'WEEK'
    , 'global.view.weekendLabel': 'WEEK+WE'
    , 'global.openShort': 'Open'
    , 'global.removeFromPlanning': 'Remove from planning'
    , 'global.ticketLabel': 'Ticket:'
    , 'global.tooltipResizeHint': 'Tip: Drag the edges to change the duration'
    , 'global.movingTicket': 'Moving ticket...'
    , 'global.removingFromPlanning': 'Removing ticket from planning...'
    , 'global.changingTicketDuration': 'Changing ticket duration...'
    , 'global.at': 'at'
    , 'projects.newProject': 'Create new operation'
    , 'projects.addProjectShort': '+ Operation'
    , 'projects.dueTemplatesCount': '{{count}} library items due'
    , 'projects.selectProject': 'Select operation'
    , 'projects.edit': 'Edit'
    , 'projects.planning': 'Scheduling'
    , 'projects.planningNeeded': 'Scheduling needed'
    , 'projects.subprojects': 'Sub-operations'
    , 'projects.lastPlannedAt': 'Last planned for'
    , 'projects.neverPlanned': 'Never planned yet'
    , 'projects.lastPlannedTooltip': 'planned on'
    , 'projects.lastPlannedBy': 'by'
    , 'projects.searchSubprojects': 'Search sub-operations...'
    , 'projects.timeblocks': 'Time blocks'
    , 'projects.searchTimeblocks': 'Search time blocks...'
    , 'projects.completed': 'Completed'
    , 'projects.prevMonth': 'Previous month'
    , 'projects.thisMonth': 'This month'
    , 'projects.nextMonth': 'Next month'
    , 'projects.users': 'Users'
    , 'projects.selectUsers': 'Select users'
    , 'projects.selectAll': 'Select all'
    , 'projects.deselectAll': 'Deselect all'
    , 'projects.weekend': 'Weekend'
    , 'projects.dayHoursTooltip': 'Day {{day}}: {{hours}} h'
    , 'projects.planPrompt.title': 'Schedule time blocks now?'
    , 'projects.planPrompt.message': 'Would you like to schedule the time blocks for "{{name}}" now in the calendar view?'
    , 'projects.planPrompt.goToPlanning': 'Yes, go to scheduling'
    , 'common.no': 'No'
    , 'common.active': 'Active'
    , 'common.inactive': 'Inactive'
    , 'admin.title': 'Administration'
    , 'admin.refresh': 'Refresh'
    , 'admin.loading': 'Loading admin data...'
    , 'admin.tabs.users': 'User management'
    , 'admin.tabs.server': 'Server settings'
    , 'admin.tabs.mailbox': 'Mailbox monitoring'
    , 'admin.tabs.markers': 'Calendar markers'
    , 'admin.tabs.notifications': 'Notifications'
    , 'admin.tabs.customers': 'Customers'
    , 'admin.customers.intro': 'Customer master data for ticket assignment. Incoming mails are matched to a customer via their email domain; the short name is shown in tickets.'
    , 'admin.customers.name': 'Customer name'
    , 'admin.customers.namePlaceholder': 'e.g. Krebs Consulting & IT-Services Co KG'
    , 'admin.customers.shortName': 'Short name (shown in tickets)'
    , 'admin.customers.shortNamePlaceholder': 'e.g. Krebs IT'
    , 'admin.customers.domains': 'Email domains (comma-separated)'
    , 'admin.customers.domainsPlaceholder': 'krebsit.de, krebs-itservices.de'
    , 'admin.customers.phone1': 'Phone 1'
    , 'admin.customers.phone2': 'Phone 2'
    , 'admin.customers.phone3': 'Phone 3'
    , 'admin.customers.notes': 'Notes'
    , 'admin.customers.active': 'Active'
    , 'admin.customers.save': 'Save'
    , 'admin.customers.add': 'Add customer'
    , 'admin.customers.cancelEdit': 'Cancel'
    , 'admin.customers.shortNameCol': 'Short name'
    , 'admin.customers.domainsCol': 'Domains'
    , 'admin.customers.phoneCol': 'Phone'
    , 'admin.customers.activeCol': 'Active'
    , 'admin.customers.edit': 'Edit'
    , 'admin.customers.none': 'No customers yet.'
    , 'admin.customers.loadError': 'Error loading customers.'
    , 'admin.customers.saveError': 'Error saving customer.'
    , 'admin.customers.searchPlaceholder': 'Search: name, short name, domain, phone, contact…'
    , 'admin.customers.searchLabel': 'Search customers'
    , 'admin.customers.archiveAction': 'Archive'
    , 'admin.customers.reactivateAction': 'Reactivate'
    , 'admin.customers.archived': 'Customer archived.'
    , 'admin.customers.reactivated': 'Customer reactivated.'
    , 'admin.customers.deleteTitle': 'Delete customer'
    , 'admin.customers.deleteMessage': 'Permanently delete customer "{{name}}"? This is only possible while no tickets, projects, contacts or domains reference it.'
    , 'admin.customers.deleted': 'Customer deleted.'
    , 'admin.customers.deleteRefs': 'Customer is still in use ({{refs}}) — please archive it instead.'
    , 'admin.customers.deleteError': 'Error deleting customer.'
    , 'admin.customers.refsTickets': 'tickets'
    , 'admin.customers.refsArchived': 'archived tickets'
    , 'admin.customers.refsProjects': 'projects'
    , 'admin.customers.refsContacts': 'contacts'
    , 'admin.customers.refsDomains': 'domains'
    , 'admin.customers.similarTitle': 'Possible duplicate'
    , 'admin.customers.similarMessage': 'Similar customers found: {{names}}. Create anyway?'
    , 'admin.customers.similarConfirm': 'Create anyway'
    , 'admin.customers.saved': 'Customer saved.'
    , 'admin.contacts.title': 'Contacts'
    , 'admin.contacts.countCol': 'Contacts'
    , 'admin.contacts.manage': 'Manage contacts'
    , 'admin.contacts.name': 'Name'
    , 'admin.contacts.phoneMobile': 'Mobile'
    , 'admin.contacts.phoneLandline': 'Landline'
    , 'admin.contacts.email': 'Email'
    , 'admin.contacts.notes': 'Notes'
    , 'admin.contacts.active': 'Active'
    , 'admin.contacts.add': 'Add'
    , 'admin.contacts.save': 'Save'
    , 'admin.contacts.cancelEdit': 'Cancel'
    , 'admin.contacts.none': 'No contacts yet.'
    , 'admin.contacts.saved': 'Contact saved.'
    , 'admin.contacts.saveError': 'Error saving contact.'
    , 'admin.contacts.archived': 'Contact archived.'
    , 'admin.contacts.reactivated': 'Contact reactivated.'
    , 'admin.contacts.deleteTitle': 'Delete contact'
    , 'admin.contacts.deleteMessage': 'Permanently delete contact "{{name}}"? This is only possible while no tickets reference them.'
    , 'admin.contacts.deleted': 'Contact deleted.'
    , 'admin.contacts.deleteRefs': 'Contact is still in use ({{refs}}) — please deactivate them instead.'
    , 'admin.contacts.deleteError': 'Error deleting contact.'
    , 'customerField.contact': 'Contact person'
    , 'customerField.contactSelect': 'Select contact…'
    , 'customerField.contactNone': '— no contact —'
    , 'customerField.contactNoCustomer': 'Select a customer first'
    , 'customerField.createNewContact': 'Create new contact'
    , 'customerField.createContactTitle': 'Create new contact'
    , 'customerField.createContactHint': 'Email and notes can be added later in customer management.'
    , 'customerField.createContactError': 'Contact could not be created.'
    , 'customerField.numberCopied': 'Number copied.'
    , 'customerField.copyNumber': 'Copy number'
    , 'customerField.callTitle': 'Call — right-click copies the number'
    , 'ticketDetail.customer': 'Customer'
    , 'admin.tabs.apiKeys': 'API keys'
    , 'admin.apiKeys.intro': 'Bearer keys for machine access (AI agents, integrations). A key acts with the permissions of its assigned user and is shown only once.'
    , 'admin.apiKeys.reachNotice': 'A key can read everything its assigned user can read — including ticket-linked e-mail and Teams content. A lifetime is mandatory: expired keys are not extended but reissued (rotation).'
    , 'admin.apiKeys.createdFor': 'New key for'
    , 'admin.apiKeys.copy': 'Copy'
    , 'admin.apiKeys.showOnce': 'This key is shown only once — store it safely now!'
    , 'admin.apiKeys.name': 'Name'
    , 'admin.apiKeys.namePlaceholder': 'e.g. Placetel agent'
    , 'admin.apiKeys.lifetime': 'Lifetime'
    , 'admin.apiKeys.days': 'days'
    , 'admin.apiKeys.user': 'Acts as user'
    , 'admin.apiKeys.userSelf': 'Myself'
    , 'admin.apiKeys.creating': 'Creating…'
    , 'admin.apiKeys.create': 'Create key'
    , 'admin.apiKeys.prefix': 'Key'
    , 'admin.apiKeys.expires': 'Expires'
    , 'admin.apiKeys.lastUsed': 'Last used'
    , 'admin.apiKeys.status': 'Status'
    , 'admin.apiKeys.statusActive': 'active'
    , 'admin.apiKeys.statusExpired': 'expired'
    , 'admin.apiKeys.statusRevoked': 'revoked'
    , 'admin.apiKeys.revokedBy': 'Revoked by'
    , 'admin.apiKeys.revoke': 'Revoke'
    , 'admin.apiKeys.revokeConfirm': 'Really revoke this API key? Applications using it will lose access immediately.'
    , 'admin.apiKeys.revoked': 'API key revoked.'
    , 'admin.apiKeys.none': 'No API keys created yet.'
    , 'admin.apiKeys.loadError': 'Error loading API keys.'
    , 'admin.apiKeys.createError': 'Error creating API key.'
    , 'admin.apiKeys.revokeError': 'Error revoking API key.'
    , 'admin.notifications.heading': 'Notifications'
    , 'admin.notifications.desc': 'Control a global notice bar at the top of the website. It appears in orange with the following text.'
    , 'admin.notifications.barLabel': 'Global notice bar'
    , 'admin.notifications.enabled': 'Enabled'
    , 'admin.notifications.message': 'Message'
    , 'admin.notifications.placeholder': 'Text for the notice bar...'
    , 'admin.notifications.save': 'Save'
    , 'common.cancel': 'Cancel'
    , 'common.close': 'Close'
    , 'common.ok': 'OK'
    , 'confirm.title': 'Confirmation'
    , 'terminAbsage.title': 'Appointment with attendees'
    , 'terminAbsage.message': 'This ticket has an appointment with attendees: {{liste}}. If you remove the assignment, the appointment will be deleted and the attendees will receive a cancellation.'
    , 'terminAbsage.confirm': 'Remove anyway'
    , 'feedback.buttonTitle': 'Give feedback (report a bug or wish)'
    , 'feedback.title': 'Give feedback'
    , 'feedback.type': 'Type of feedback'
    , 'feedback.typeBug': 'Bug'
    , 'feedback.typeWish': 'Wish'
    , 'feedback.message': 'Description'
    , 'feedback.messagePlaceholder': 'What happened or what would you like? The more specific, the better.'
    , 'feedback.screenshot': 'Screenshot (optional)'
    , 'feedback.contextHint': 'Sent automatically: current view ({{url}}), browser and timestamp.'
    , 'feedback.send': 'Send'
    , 'feedback.sending': 'Sending…'
    , 'feedback.thanks': 'Thanks! Your feedback has been received.'
    , 'feedback.error': 'Feedback could not be sent.'
    , 'admin.tabs.feedback': 'Feedback'
    , 'admin.feedback.title': 'Feedback'
    , 'admin.feedback.intro': 'Feedback from the header button. After transferring to an issue, set the status to "uebernommen" and enter the issue number.'
    , 'admin.feedback.filter': 'Status filter'
    , 'admin.feedback.empty': 'No entries for this filter.'
    , 'admin.feedback.date': 'Date'
    , 'admin.feedback.type': 'Type'
    , 'admin.feedback.reporter': 'Reporter'
    , 'admin.feedback.message': 'Message'
    , 'admin.feedback.screenshot': 'Screenshot'
    , 'admin.feedback.status': 'Status'
    , 'admin.feedback.issue': 'Issue #'
    , 'admin.feedback.view': 'view'
    , 'admin.feedback.notePlaceholder': 'Triage note…'
    , 'admin.feedback.loadError': 'Error loading feedback.'
    , 'admin.feedback.updated': 'Feedback updated.'
    , 'admin.feedback.updateError': 'Error updating feedback.'
    , 'admin.feedback.actions': 'Actions'
    , 'admin.feedback.deleteTitle': 'Delete feedback?'
    , 'admin.feedback.deleteConfirm': 'Permanently delete this entry? This cannot be undone.'
    , 'admin.feedback.deleted': 'Feedback deleted.'
    , 'admin.feedback.deleteError': 'Error deleting feedback.'
    , 'ticketDetail.customerNone': '— no customer —'
    , 'ticketDetail.customerSelect': 'Select customer…'
    , 'ticketDetail.customerEdit': 'Assign customer'
    , 'customerField.createNew': 'Create new customer'
    , 'customerField.createTitle': 'Create new customer'
    , 'customerField.createConfirm': 'Create'
    , 'customerField.createError': 'Customer could not be created.'
    , 'customerField.createHint': 'Domains, phone numbers and other details can be added later in the customer administration.'
    , 'ui.noResults': 'No matches'
    , 'admin.server.customersFeatureDesc': 'Completely hides the customer field on tickets, the "Customers" admin tab and the automatic customer assignment on incoming mail — for installations without customer context. Existing data is kept.'
    , 'common.save': 'Save'
    , 'common.saving': 'Saving...'
    , 'common.never': 'Never'
    , 'admin.users.email': 'Email'
    , 'admin.users.name': 'Name'
    , 'admin.users.lastLogin': 'Last login'
    , 'admin.users.admin': 'Admin'
    , 'admin.users.manager': 'Manager'
    , 'admin.users.ticketworker': 'Ticket worker'
    , 'admin.users.readonly': 'Readonly'
    , 'admin.users.licensed': 'Licensed'
    , 'admin.users.active': 'Active'
    , 'admin.users.adminTooltip': 'Access to the admin area (purely technical: users, mailboxes, AI, API keys). Grants NO ticket rights — add Ticketworker/Manager for those.'
    , 'admin.users.managerTooltip': 'Like Ticketworker, plus: edit, close and merge tickets of other users, delete tickets, edit all comments, "All tickets"/"All calendars", plan for others, customer management.'
    , 'admin.users.ticketworkerTooltip': 'May edit and close own tickets (assigned or self-created), claim pool tickets, hand tickets over and plan themselves. Without this: read-only.'
    , 'admin.users.readonlyTooltip': 'Read-only account: overrides ALL other roles — may see, may not change (even as manager). Giving feedback and personal view settings remain possible.'
    , 'admin.users.licensedTooltip': 'License seat: appears in the ticket assignment dropdown. No other effects.'
    , 'admin.users.activeTooltip': 'May sign in. Deactivated = sign-in and all API access blocked.'
    , 'admin.users.none': 'No users found.'
    , 'admin.users.systemAdminTooltip': 'System administrator rights cannot be removed'
    , 'admin.users.systemAdminBadge': 'System administrator (not removable)'
    , 'admin.error.permission': 'No permission for the admin area.'
    , 'admin.error.load': 'Error loading admin data.'
    , 'admin.users.roleUpdated': 'User role updated successfully.'
    , 'admin.users.roleUpdateError': 'Error updating user role.'
    , 'admin.server.allowedEmails': 'Allowed email addresses'
    , 'admin.server.desc': 'Restrict access to specific email addresses or domains. Leave empty for all O365 users.'
    , 'admin.server.allowedPlaceholder': 'Examples:\nuser1@example.com, user2@example.com\n*@example.com\nadmin@company.com, *@company.com'
    , 'admin.server.show': 'Show'
    , 'admin.server.hide': 'Hide'
    , 'admin.server.formatting': 'Formatting:'
    , 'admin.server.formatEmail': 'user@example.com - Exact email address'
    , 'admin.server.formatDomain': '*@example.com - All addresses for the domain'
    , 'admin.server.formatMultiple': 'Separate multiple addresses with commas'
    , 'admin.server.allowedSaved': 'Allowed email addresses saved successfully.'
    , 'admin.server.allowedSaveError': 'Error saving allowed email addresses.'
    , 'admin.server.allowedLoadError': 'The allowed email addresses could not be loaded. Saving is blocked so the existing list is not overwritten. Please reload the page.'
    , 'admin.server.unsaved': 'Unsaved changes'
    , 'admin.mailbox.heading': 'Mailbox monitoring'
    , 'admin.mailbox.desc': 'Configure email mailboxes for automatic ticket creation and assignment.'
    , 'admin.mailbox.noneTitle': 'No mailboxes configured yet.'
    , 'admin.mailbox.noneDesc': 'Click "Add mailbox" to enable automatic email processing.'
    , 'admin.mailbox.col.address': 'Mailbox'
    , 'admin.mailbox.col.status': 'Status'
    , 'admin.mailbox.col.interval': 'Interval'
    , 'admin.mailbox.col.successFolder': 'Success folder'
    , 'admin.mailbox.col.errorFolder': 'Error folder'
    , 'admin.mailbox.col.lastProcessed': 'Last processed'
    , 'admin.mailbox.col.actions': 'Actions'
    , 'admin.mailbox.processNowTooltip': 'Process now'
    , 'admin.mailbox.deleteTooltip': 'Delete mailbox monitoring'
    , 'admin.mailbox.addButton': 'Add mailbox'
    , 'admin.mailbox.addHeading': 'Add new mailbox'
    , 'admin.mailbox.addrLabel': 'Mailbox address *'
    , 'admin.mailbox.intervalLabel': 'Check interval (seconds)'
    , 'admin.mailbox.successFolderLabel': 'Success folder'
    , 'admin.mailbox.errorFolderLabel': 'Error folder'
    , 'admin.mailbox.addSubmit': 'Add'
    , 'admin.mailbox.added': 'Mailbox monitoring added successfully.'
    , 'admin.mailbox.addError': 'Error adding mailbox monitoring.'
    , 'admin.mailbox.updated': 'Mailbox settings updated successfully.'
    , 'admin.mailbox.updateError': 'Error updating mailbox settings.'
    , 'admin.mailbox.confirmDelete': 'Are you sure you want to delete this mailbox monitoring?'
    , 'admin.mailbox.deleted': 'Mailbox monitoring deleted successfully.'
    , 'admin.mailbox.deleteError': 'Error deleting mailbox monitoring.'
    , 'admin.mailbox.processStarted': 'Manual processing started'
    , 'admin.mailbox.processError': 'Error during manual processing'
    , 'admin.markers.heading': 'Calendar marker configuration'
    , 'admin.markers.desc': 'Manage available calendar markers for tickets. These markers can be used by all users.'
    , 'admin.markers.current': 'Current markers'
    , 'admin.markers.none': 'No calendar markers configured yet.'
    , 'admin.markers.col.value': 'Value'
    , 'admin.markers.col.label': 'Label'
    , 'admin.markers.col.icon': 'Icon'
    , 'admin.markers.col.color': 'Color'
    , 'admin.markers.col.status': 'Status'
    , 'admin.markers.col.sort': 'Sort order'
    , 'admin.markers.col.actions': 'Actions'
    , 'admin.markers.addButton': 'Add marker'
    , 'admin.markers.addHeading': 'Add new calendar marker'
    , 'admin.markers.valueLabel': 'Value (unique)'
    , 'admin.markers.valuePlaceholder': 'e.g. external, internal'
    , 'admin.markers.labelLabel': 'Label'
    , 'admin.markers.labelPlaceholder': 'e.g. External, Internal'
    , 'admin.markers.iconLabel': 'Symbol (emoji)'
    , 'admin.markers.colorLabel': 'Color'
    , 'admin.markers.sortLabel': 'Sort order'
    , 'admin.markers.create': 'Create marker'
    , 'admin.markers.cancel': 'Cancel'
    , 'admin.markers.editHeading': 'Edit marker'
    , 'admin.markers.valueReadonly': 'Value (read-only)'
    , 'common.edit': 'Edit'
    , 'common.delete': 'Delete'
    , 'common.hide': 'Hide'
    , 'common.show': 'Show'
    , 'common.unknown': 'Unknown'
    , 'common.notAvailable': 'Not available'
    , 'common.opening': 'Opening…'
    , 'common.creating': 'Creating...'
    , 'common.add': 'Add'
    , 'common.adding': 'Adding...'
    , 'common.back': 'Back'
    , 'common.unassigned': 'Unassigned'
    , 'email.detail.title': 'Email details'
    , 'email.detail.content': 'Content'
    , 'email.detail.imagesLoading': ' (Loading images...)'
    , 'email.detail.from': 'From:'
    , 'email.detail.to': 'To:'
    , 'email.detail.received': 'Received:'
    , 'email.subject.none': '(No subject)'
    , 'email.flagged': 'Flagged'
    , 'email.importance.high': 'High'
    , 'email.importance.normal': 'Normal'
    , 'email.importance.low': 'Low'
    , 'email.body.empty': 'No content available'
    , 'email.body.frameTitle': 'Email content'
    , 'email.body.processing': 'Processing content...'
    , 'email.body.imageLoadFailed': 'Image could not be loaded'
    , 'email.body.unknownFormat': 'Unknown email format'
    , 'email.body.imageNotAvailable': '[Image not available]'
    , 'email.attachments.title': 'Attachments'
    , 'email.attachments.unknownSize': 'Unknown size'
    , 'email.attachments.inlineImages': 'embedded images'
    , 'email.addToTicket.title': 'Add email to ticket'
    , 'email.addToTicket.modalTitle': 'Add email to ticket'
    , 'email.addToTicket.button': 'Add to a ticket'
    , 'email.reply.title': 'Reply to email'
    , 'email.reply.button': 'Reply'
    , 'email.reply.createTitle': 'Create email reply'
    , 'email.reply.ticketDecision': 'This email is not linked to a ticket yet. Would you like to create a new ticket or reply without a ticket?'
    , 'email.reply.withoutTicket': 'Reply without ticket'
    , 'email.reply.createAndRespond': 'Create ticket & reply'
    , 'email.reply.sendFromFallback': 'Keine Senden-Als Rechte an {{mailbox}}, sende aus eigenem Konto.'
    , 'email.preview.title': 'Email preview:'
    , 'email.preview.from': 'From:'
    , 'email.preview.subject': 'Subject:'
    , 'email.preview.preview': 'Preview:'
    , 'email.createFromEmail.title': 'Create new ticket from email'
    , 'teams.addToTicket.modalTitle': 'Add Teams message to ticket'
    , 'ticket.create.new': 'Create new ticket:'
    , 'ticket.create.submit': 'Create ticket'
    , 'ticket.create.button': 'Create new ticket'
    , 'ticket.addToExisting': 'Add to existing ticket:'
    , 'ticket.filter.placeholder': 'Search by ticket number or title...'
    , 'ticket.filter.placeholderCustomer': 'Search by ticket number, title, customer or contact...'
    , 'contactSearch.placeholder': 'Contact search…'
    , 'contactSearch.label': 'Contact search: customers and contacts'
    , 'contactSearch.title': 'Contact search'
    , 'contactSearch.results': 'Contact search results'
    , 'contactSearch.minChars': 'Type at least two characters.'
    , 'contactSearch.loading': 'Loading contacts…'
    , 'contactSearch.none': 'No matches.'
    , 'contactSearch.moreContacts': '+{{count}} more contacts'
    , 'contactSearch.moreCustomers': '{{count}} more matches — refine your search'
    , 'contactSearch.openAdmin': 'Open in customer management'
    , 'ticket.loading': 'Loading tickets...'
    , 'ticket.noneOpen': 'No open tickets found.'
    , 'ticket.noneMatchFilter': 'No tickets found matching the filter.'
    , 'form.title': 'Title'
    , 'form.description': 'Description'
    , 'form.priority': 'Priority'
    , 'form.assignedTo': 'Assigned to'
    , 'form.category': 'Category'
    , 'priority.low': 'Low'
    , 'priority.normal': 'Normal'
    , 'priority.high': 'High'
    , 'priority.critical': 'Critical'
    , 'welcome.title': 'Welcome to KayCee Ticket'
    , 'welcome.lead': 'Securely access your Microsoft 365 emails and calendars. This interface provides a modern, user-friendly UI for your Office 365 data.'
    , 'welcome.button.setupStart': '⚙️ Start setup'
    , 'welcome.button.signInM365': 'Sign in with Microsoft 365'
    , 'welcome.loadingStatus': 'Loading status...'
    , 'welcome.feature.email.title': 'Email access'
    , 'welcome.feature.email.desc': 'Read your emails directly via the Microsoft Graph API'
    , 'welcome.feature.calendar.title': 'Calendar integration'
    , 'welcome.feature.calendar.desc': 'Overview of your upcoming appointments and events'
    , 'welcome.feature.oauth.title': 'Secure OAuth authentication'
    , 'welcome.feature.oauth.desc': "Your data remains secure through Microsoft's OAuth 2.0"
    , 'welcome.feature.ui.title': 'Modern user interface'
    , 'welcome.feature.ui.desc': 'Responsive design for desktop and mobile'
    , 'welcome.setupNotice.title': '⚙️ Initial setup required'
    , 'welcome.setupNotice.desc': 'This application must first be configured with your Microsoft 365 tenant. Click "Start setup" for a guided walkthrough.'
    , 'setup.success': 'Setup completed successfully! You can now sign in.'
    , 'setup.errorUnknown': 'Unknown error during setup test'
    , 'setup.testFailed': 'Setup test failed: {{message}}'
    , 'email.list.noEmails': 'No emails found'
    , 'email.list.noneMatchFilters': 'No emails match the active filters'
    , 'email.list.adjustFilters': 'Remove filters or adjust the filter criteria'
    , 'teamsView.loadError': 'Error loading Teams messages'
    , 'teamsView.noContent': 'Message without content'
    , 'teamsView.unknownSender': 'Unknown sender'
    , 'teamsView.loading': 'Loading Teams messages...'
    , 'teamsView.title': 'Teams messages'
    , 'teamsView.empty': 'No Teams messages'
    , 'teamsView.emptyHint': 'No recent Teams messages were found.'
    , 'teamsView.count': '{{count}} messages (last 30)'
    , 'teamsView.justNow': 'just now'
    , 'teamsView.inChat': 'in {{topic}}'
    , 'email.errors.draftCreate': 'Error creating the email draft.'
    , 'email.errors.noneSelected': 'Error: no email selected'
    , 'email.errors.replyCreate': 'Error creating the email reply: {{details}}'
    , 'email.reply.createErrorGeneric': 'Error creating the email reply.'
    , 'email.errors.addToTicket': 'Error adding the email to the ticket: {{details}}'
    , 'ticket.createErrorDetails': 'Error creating the ticket: {{details}}'
    , 'email.filter.invalidAddress': 'Please enter a valid email address.'
    , 'teams.errors.noneSelected': 'Error: no Teams message selected'
    , 'teams.errors.addToTicket': 'Error adding the Teams message to the ticket: {{details}}'
    , 'eventToTicket.invalidEvent': 'Error: invalid appointment.'
    , 'eventToTicket.createPlanError': 'Error creating/scheduling the ticket.'
    , 'projectWizard.saveSubprojectsError': 'Error saving sub-operations or time blocks. Details in the console.'
    , 'setup.failed': 'Setup failed: {{details}}'
    , 'setup.configError': 'Configuration error: {{details}}'
    , 'common.unknownError': 'Unknown error'
    , 'app.sessionExpiredSso': 'Your session has expired. You will be redirected to sign in.'
    , 'app.sessionExpiredManual': 'Your session has expired. Please sign in again.'
    , 'app.loginNow': 'Sign in now'
    , 'setup.subtitle': 'Configure your Microsoft 365 integration'
    , 'setup.clickOn': 'Click'
    , 'setup.goTo': 'Go to'
    , 'setup.choose': 'Select'
    , 'setup.important': 'Important:'
    , 'setup.new': 'NEW:'
    , 'setup.nextStep': 'Continue to step {{n}}'
    , 'common.optional': 'optional'
    , 'common.errorPrefix': 'Error:'
    , 'setup.step1.title': 'Step 1: Create the Azure app registration'
    , 'setup.step1.intro': 'Create a new app registration in Azure:'
    , 'setup.step1.goToPortal': 'Go to the'
    , 'setup.step1.fillIn': 'Fill in the following information:'
    , 'setup.step2.title': 'Step 2: Create a client secret'
    , 'setup.step2.intro': 'Create a client secret for your app:'
    , 'setup.step2.description': 'Enter a description (e.g. "O365 Interface Secret")'
    , 'setup.step2.expiry': 'Choose an expiry (recommended: 24 months)'
    , 'setup.step2.copyNow': 'Copy the secret immediately - it is shown only once!'
    , 'setup.step3.title': 'Step 3: Configure API permissions'
    , 'setup.step3.intro': 'Add the required API permissions:'
    , 'setup.step3.addFollowing': 'Add the following permissions:'
    , 'setup.perm.userRead': 'Read user profile'
    , 'setup.perm.mailRead': 'Read emails'
    , 'setup.perm.mailReadWrite': 'Edit emails (read/unread state)'
    , 'setup.perm.mailSend': 'Send emails'
    , 'setup.perm.calRead': 'Read calendars'
    , 'setup.perm.calReadWrite': 'Create/edit calendar events (own calendar)'
    , 'setup.perm.calShared': 'Calendar events in shared/delegated calendars'
    , 'setup.step3.forAllPermissions': 'for all permissions'
    , 'setup.step3.adminConsentRequired': 'Admin consent is required for proper operation!'
    , 'setup.step3.newFeatures': 'New features available:'
    , 'setup.step3.featureMarkRead': 'Mark emails as read/unread'
    , 'setup.step3.featureSend': 'Send emails directly from the application'
    , 'setup.step4.title': 'Step 4: Enter the configuration'
    , 'setup.step4.clientIdHelp': 'You will find it on the Overview page of your app'
    , 'setup.step4.clientIdPlaceholder': 'e.g. 12345678-1234-1234-1234-123456789012'
    , 'setup.step4.secretHelp': 'The secret you just created'
    , 'setup.step4.secretPlaceholder': 'Your client secret'
    , 'setup.step4.tenantHelp': 'Leave empty for multi-tenant or enter a specific tenant ID'
    , 'setup.step4.tenantPlaceholder': 'common (for multi-tenant)'
    , 'setup.step4.sessionHelp': 'Generated automatically or enter your own'
    , 'setup.step4.sessionPlaceholder': 'Generated automatically'
    , 'setup.step4.generate': 'Generate'
    , 'setup.step4.allowedEmails': 'Allowed email addresses'
    , 'setup.step4.allowedEmailsHelp': 'Restrict access to specific email addresses or domains. Leave empty for all O365 users.'
    , 'setup.step4.allowedEmailsPlaceholder': 'Examples:\nuser1@example.com, user2@example.com\n*@example.com\nadmin@company.de, *@company.de'
    , 'setup.step4.formatting': 'Formatting:'
    , 'setup.step4.fmtExact': 'Exact email address'
    , 'setup.step4.fmtDomain': 'All addresses of the domain'
    , 'setup.step4.fmtComma': 'Separate multiple addresses with commas'
    , 'setup.step4.saving': 'Configuring...'
    , 'setup.step4.save': 'Save configuration'
    , 'setup.step5.title': 'Setup complete!'
    , 'setup.step5.saved': 'The configuration was saved successfully!'
    , 'setup.step5.testing': 'Testing the configuration...'
    , 'setup.step5.testOk': 'Test successful!'
    , 'setup.step5.worksFine': 'The configuration works correctly.'
    , 'setup.step5.finish': 'Finish setup'
    , 'setup.step5.testFailed': 'Test failed'
    , 'setup.step5.retest': 'Test again'
    , 'setup.step5.autoTest': 'The configuration is being tested automatically...'
    , 'setup.step5.manualTest': 'Test manually'
    , 'setup.info.permissions': 'Required permissions:'
    , 'setup.info.userRead': 'To read user information'
    , 'setup.info.mailRead': 'To read emails'
    , 'setup.info.mailReadWrite': 'To edit emails (read/unread)'
    , 'setup.info.mailSend': 'To send emails'
    , 'setup.info.calRead': 'To read calendar events'
    , 'setup.info.calReadWrite': 'To create/edit calendar events (own calendar)'
    , 'setup.info.calShared': 'To create events in shared calendars'
    , 'setup.info.access': 'Access control:'
    , 'setup.info.accessRestrict': 'Access can be restricted to specific email addresses'
    , 'setup.info.accessWildcard': 'Wildcard support for whole domains (*@domain.com)'
    , 'setup.info.accessFlexible': 'Flexible configuration via the setup process'
    , 'setup.info.accessEmpty': 'Leave empty for all O365 users'
    , 'common.accessDenied': 'Access denied'
    , 'app.offlineQueueSentOne': 'Your comment written offline has been sent.'
    , 'app.offlineQueueSentMany': '{{count}} comments written offline have been sent.'
    , 'app.offlineQueueDropped': 'A comment written offline could not be sent and was discarded.'
    , 'ticketDetail.commentQueued': 'No connection — the comment is saved and will be sent as soon as you are back online.'
  }
  , es: {
    'dayCalendar.title': 'Día de eventos',
    'language.de': 'Alemán',
    'language.en': 'Inglés',
    'language.es': 'Español',
    'projectWizard.step1.title': 'Crear nuevo proyecto',
    'projectWizard.step1.editTitle': 'Editar proyecto',
    'projectWizard.step2.title': 'Definir sub-operaciones',
    'projectWizard.step3.title': 'Definir bloques de tiempo',
    'projectWizard.labels.projectName': 'Nombre del proyecto',
    'projectWizard.labels.projectDescription': 'Descripción del proyecto',
    'projectWizard.labels.projectResponsible': 'Responsable del proyecto',
    'projectWizard.labels.subprojects': 'Sub-operaciones',
    'projectWizard.placeholders.projectName': 'Nombre del proyecto',
    'projectWizard.placeholders.description': 'Descripción',
    'projectWizard.placeholders.assignedTo': 'Asignado a...',
    'projectWizard.options.none': 'Ninguno',
    'projectWizard.options.no': 'No',
    'projectWizard.options.with': 'Sí',
    'projectWizard.buttons.next': 'Siguiente',
    'projectWizard.buttons.back': 'Atrás',
    'projectWizard.buttons.finish': 'Finalizar',
    'projectWizard.buttons.create': 'Crear',
    'projectWizard.buttons.saveChanges': 'Guardar cambios',
    'projectWizard.buttons.addSubproject': 'Añadir sub-operación',
    'projectWizard.buttons.configureTimeblocks': 'Configurar bloques de tiempo',
    'projectWizard.buttons.deleteSubproject': 'Eliminar sub-operación',
    'projectWizard.buttons.deleteProject': 'Eliminar proyecto',
    'projectWizard.buttons.addTimeblock': 'Bloque de tiempo',
    'projectWizard.buttons.addTimeblockFor': 'Bloque de tiempo para {{name}}',
    'projectWizard.buttons.addAnotherTimeblock': 'Otro bloque de tiempo',
    'projectWizard.subproject.title': 'Título de la sub-operación',
    'projectWizard.subproject.namePlaceholder': 'Nombre de la sub-operación',
    'projectWizard.subproject.description': 'Descripción de la sub-operación',
    'projectWizard.subproject.responsibleRequired': 'Responsable (obligatorio)',
    'projectWizard.timeblock.namePlaceholder': 'NOMBRE DEL BLOQUE',
    'projectWizard.timeblock.titleLabel': 'Título del bloque de tiempo',
    'projectWizard.timeblock.titlePlaceholder': 'Título del bloque de tiempo',
    'projectWizard.timeblock.descriptionLabel': 'Descripción del bloque de tiempo',
    'projectWizard.timeblock.delete': 'Eliminar bloque de tiempo',
    'projectWizard.hints.titleSavedAs': 'El título se guardará como "{{project}} - NOMBRE DEL BLOQUE".',
    'projectWizard.validation.selectAssigneeForAllBlocks': 'Seleccione un responsable para todos los bloques definidos.',
    'projectWizard.confirm.deleteSubprojectAndTickets': '¿Eliminar permanentemente esta sub-operación y todos sus tickets?',
    'projectWizard.confirm.deleteProject': '¿Eliminar permanentemente el proyecto incluyendo sub-operaciones y tickets?',
    'projectWizard.errors.deleteSubproject': 'No se pudo eliminar la sub-operación.',
    'projectWizard.errors.deleteSubprojectPartial': 'Sub-operación no eliminada: no se pudieron eliminar {count} ticket(s).',
    'projectWizard.confirm.deleteTimeblock': '¿Eliminar este bloque de tiempo?',
    'projectWizard.info.subprojectsToggleLocked': 'El conmutador de sub-operaciones está bloqueado para proyectos existentes.',
    'projectWizard.subTimeblocks.heading': 'Bloques de tiempo para {{name}}',
    'projectWizard.templates.title': 'Biblioteca',
    'projectWizard.templates.loading': 'Cargando biblioteca...',
    'projectWizard.templates.none': 'La biblioteca está vacía',
    'projectWizard.templates.searchPlaceholder': 'Buscar en la biblioteca...',
    'projectWizard.turnus.count': 'x veces',
    'projectWizard.turnus.every': 'cada x',
    'projectWizard.turnus.days': 'días',
    'projectWizard.turnus.weeks': 'semanas',
    'projectWizard.turnus.months': 'meses',
    'projectWizard.turnus.years': 'años',
    'projectWizard.turnus.hint': 'Opcional: p. ej., "3 veces cada 2 semanas".',
    'projectWizard.turnus.heading': 'Recurrencia',
    'projectWizard.templates.apply': 'Aplicar',
    'projectWizard.templates.delete': 'Eliminar',
    'projectWizard.templates.confirmDelete': '¿Realmente desea eliminar esta plantilla?',
    'projectWizard.templates.deleteError': 'Error al eliminar la plantilla. Ver consola para detalles.',
    'projectWizard.templates.hasActiveProjects': 'Esta plantilla no puede ser eliminada porque {{count}} proyectos de recurrencia activos están vinculados a ella.',
    'projectWizard.templates.skipTurnus': 'Omitir este ciclo',
    'projectWizard.templates.skippedOnce': 'Ciclo omitido una vez',
    'projectWizard.templates.fulfilledThisPeriod': 'Ya programado según recurrencia',
    'projectWizard.templates.turnusSkippedOnce': 'Recurrencia actual omitida',
    'projectWizard.templates.skipTurnusConfirm': '¿Omitir la recurrencia una vez?',
    'projectWizard.templates.skipTurnusError': 'Error al omitir la recurrencia.',
    'projectWizard.templates.created': 'La plantilla ha sido creada',
    'projectWizard.templates.deleted': 'Plantilla eliminada',
    'projectWizard.templates.undo': 'Deshacer',
    'projectWizard.templates.undoing': 'Restaurando...',
    'projectWizard.templates.restored': 'Plantilla restaurada',
    'projectWizard.templates.restoreError': 'Error al restaurar la plantilla.',
    'projectWizard.templates.updated': 'Plantilla actualizada',
    'projectWizard.templates.dueThisPeriod': 'Aún por planificar en este período',
    'projectWizard.buttons.addToTemplates': 'Añadir a la biblioteca',
    'projectWizard.buttons.updateTemplate': 'Actualizar elemento de biblioteca',
    'header.calendar': 'Calendario',
    'header.dashboard': 'Panel',
    'header.allTickets': 'Todos los tickets',
    'header.allCalendars': 'Todos los calendarios',
    'header.projects': 'Proyectos',
    'header.searchDev': 'Búsqueda (en desarrollo)',
    'search.title': 'Búsqueda con IA',
    'search.semanticBadge': 'semántico',
    // Frontend Phase 4 (Welle 4): frühere Baseline-Keys (i18n_known_missing)
    'admin.notifications.saved': 'Notificación guardada.',
    'admin.notifications.saveError': 'Error al guardar la notificación.',
    'admin.releaseNote.saved': 'Nota de versión guardada.',
    'admin.releaseNote.saveError': 'Error al guardar la nota de versión.',
    'admin.releaseNote.heading': 'Aviso al iniciar sesión (única vez)',
    'admin.releaseNote.desc': 'Controla únicamente el aviso que cada usuario ve una vez como superposición tras iniciar sesión. El historial de versiones del modal «¿Qué hay de nuevo?» proviene de la importación del CHANGELOG en el despliegue y no se mantiene aquí.',
    'admin.releaseNote.versionsHeading': 'Historial de versiones («¿Qué hay de nuevo?»)',
    'admin.releaseNote.versionsHint': 'Se importa del CHANGELOG en el despliegue; aquí solo se muestra a modo de referencia — no se mantiene en el área de administración.',
    'admin.releaseNote.enabled': 'Activado',
    'admin.releaseNote.title': 'Título',
    'admin.releaseNote.titlePlaceholder': 'p. ej. Update v2.5.0',
    'admin.releaseNote.content': 'Contenido',
    'admin.releaseNote.contentPlaceholder': 'Nuevas funciones, cambios, correcciones...',
    'calendar.convertToTicket': 'Convertir en ticket',
    'calendar.confirmDeleteEvent': '¿Realmente desea eliminar esta cita?',
    'calendar.deletingEvent': 'Eliminando cita...',
    'calendar.deleteError': 'Error al eliminar la cita.',
    'calendar.deleteEvent': 'Eliminar entrada',
    'calendar.dropChoice.title': '¿Mover la cita o crear una nueva?',
    'calendar.dropChoice.text': 'Ha movido una cita existente. ¿Cómo desea continuar?',
    'calendar.dropChoice.move': 'Mover',
    'calendar.dropChoice.createAnother': 'Crear otra',
    'calendar.updating': 'Actualizando...',
    'calendar.creating': 'Creando...',
    'calendar.createError': 'Error al crear.',
    'common.yes': 'Sí',
    'common.clear': 'Vaciar',
    'dashboard.showTodayCalendar': 'Mostrar vista de calendario',
    'dashboard.hideTodayCalendar': 'Ocultar vista de calendario',
    'dashboard.expandToCalendar': 'Cambiar a la vista de calendario',
    'dashboard.collapseCalendar': 'Cambiar a la lista de citas de hoy',
    'dashboard.todayLabel': 'Hoy',
    'dashboard.releaseNote.defaultTitle': 'Nuevas funciones y cambios',
    'email.minimize.title': 'Minimizar',
    'email.maximize.title': 'Maximizar',
    'event.addToTicket.modalTitle': 'Añadir cita al ticket',
    'eventDetail.busy': 'Ocupado',
    'eventDetail.tentative': 'Provisional',
    'eventDetail.free': 'Libre',
    'eventDetail.oof': 'Ausente',
    'eventDetail.appointment': 'Cita',
    'eventDetail.noSubject': '(Sin asunto)',
    'eventDetail.header': 'Cita',
    'eventDetail.status': 'Estado',
    'eventDetail.date': 'Fecha',
    'eventDetail.period': 'Período',
    'eventDetail.location': 'Lugar',
    'eventDetail.organizer': 'Organizador',
    'eventDetail.attendees': 'Participantes',
    'eventDetail.joinMeeting': 'Unirse a la reunión',
    'eventDetail.teamsMarker': 'Reunión de Teams',
    'global.searchInCalendar': 'Buscar en el calendario...',
    'global.prevResult': 'Resultado anterior',
    'global.nextResult': 'Resultado siguiente',
    'global.creating': 'Creando...',
    'header.aiSearch': 'Búsqueda con IA',
    'header.loadingStatus': 'Estado de carga',
    'projects.heatmapLoading': 'Actualizando el mapa de calor...',
    'projects.searchProjects': 'Buscar proyectos...',
    'projects.turnusConfigured': 'Recurrencia configurada',
    'projects.completePrompt.defaultProjectName': 'Nuevo proyecto',
    'projects.completePrompt.defaultBlockBase': 'Ticket',
    'projects.completePrompt.convertTitle': '¿Necesita una cita de seguimiento?',
    'projects.completePrompt.convertMessage': '¿Necesita una cita de seguimiento? ¿O este tema está realmente terminado?',
    'projects.completePrompt.title': 'Proyecto completo',
    'projects.completePrompt.message': 'Se completó el último bloque de tiempo del proyecto. ¿Desea marcar el proyecto (y sus subproyectos) también como completado y archivarlo?',
    'projects.completePrompt.project': 'Proyecto',
    'projects.completePrompt.subprojects': 'Subproyectos',
    'projects.completePrompt.keepOpen': 'No, todavía necesito tiempo',
    'projects.completePrompt.confirmConvert': 'He terminado, cerrar',
    'projects.completePrompt.confirm': 'Sí, archivar el proyecto',
    'projectWizard.confirm.deleteSubproject': '¿Eliminar este subproyecto?',
    'projectWizard.templates.createError': 'Error al guardar la plantilla. Detalles en la consola.',
    'projectWizard.info.turnusLocked': 'La recurrencia no se puede cambiar en "Editar proyecto".',
    'projectWizard.templates.choiceTitle': '¿Actualizar el elemento de la biblioteca o crear uno nuevo?',
    'projectWizard.templates.choiceText': 'Ha aplicado una plantilla. ¿Cómo desea continuar?',
    'projectWizard.templates.updateExisting': 'Actualizar elemento existente',
    'projectWizard.templates.createNew': 'Crear nueva plantilla',
    'projectWizard.timeblock.checklist': 'Lista de verificación',
    'search.send': 'Enviar',
    'search.ai.answer': 'Sugerencia / respuesta',
    'search.ai.followup.placeholder': 'Hacer una pregunta de seguimiento…',
    'search.ai.debug': 'Depuración (solo visible aquí)',
    'search.ai.input': 'Término de búsqueda',
    'search.ai.usedQueries': 'Pares de búsqueda utilizados',
    'ticket.create.data': 'Datos del nuevo ticket:',
    'ticket.merge.error': 'Error al fusionar los tickets.',
    'ticketDetail.aiSummary': 'Resumen con IA',
    'ticketDetail.aiSummaryError': 'Error al crear el resumen con IA',
    'ticketDetail.aiDisabled': 'Las funciones de IA están desactivadas.',
    'ticketDetail.descSummarize': 'Resumir la descripción con IA',
    'ticketDetail.descSummarizeTitle': 'Resumir descripción',
    'ticketDetail.descSummarizeHint': 'Revise la propuesta y ajústela si es necesario. Al aplicarla, la descripción anterior se guarda automáticamente como comentario interno.',
    'ticketDetail.descSummarizeLoading': 'La IA está resumiendo la descripción…',
    'ticketDetail.descSummarizeApply': 'Aplicar',
    'ticketDetail.descSummarizeApplying': 'Aplicando…',
    'ticketDetail.descSummarizeApplied': 'Descripción aplicada — el original queda como comentario interno en el historial.',
    'ticketDetail.descSummarizeError': 'Error al resumir la descripción.',
    'ticketDetail.descSummarizeApplyError': 'Error al aplicar la descripción.',
    'ticketDetail.confirmDeleteAttachment': '¿Realmente desea eliminar este adjunto?',
    'ticketDetail.deleteAttachment': 'Eliminar adjunto',
    'ticketDetail.deleteAttachmentError': 'Error al eliminar el adjunto',
    'ticketDetail.loadingAttachments': 'Cargando adjuntos...',
    'ticketDetail.file': 'archivo',
    'ticketDetail.files': 'archivos',
    'ticketDetail.prevImage': 'Imagen anterior',
    'ticketDetail.nextImage': 'Imagen siguiente',
    'ticketDetail.backToHistory': 'Volver al historial',
    'ticketDetail.meeting': 'Reunión',
    'ticketDetail.joinMeeting': 'Unirse a la reunión',
    'ticketDetail.markWaiting': 'Marcar como en espera',
    'ticketDetail.waitingError': 'Error al poner en espera',
    'ticketDetail.reopenTitle': 'Reabrir ticket',
    'ticketDetail.reopenBlocked': 'Reapertura bloqueada: el proyecto aún tiene tickets o bloques de tiempo abiertos.',
    'ticketDetail.reopenError': 'Error al reabrir el ticket',
    'ticketDetail.notFound': 'Ticket no encontrado.',
    'ticketDetail.completeDate': 'Fecha',
    'ticketDetail.completeStart': 'Hora de inicio',
    'ticketDetail.completeEnd': 'Hora de fin',
    'ticketDetail.completeHint': 'Indique fecha, hora de inicio y de fin. Si ya había tiempo planificado, los campos vienen rellenados.',
    'ticketDetail.completeWithoutTimes': 'Sin horas',
    'ticketDetail.completeMissingFields': 'Indique fecha, hora de inicio y hora de fin.',
    'ticketDetail.completeInvalidFields': 'Fecha u hora no válidas.',
    'ticketDetail.completeEndBeforeStart': 'La hora de fin debe ser posterior a la de inicio.',
    'ticketDetail.completePlanningError': 'Error al actualizar/crear la entrada del calendario.',
    'ticketDetail.completeUnplanError': 'Error al eliminar la entrada del calendario.',
    'ticketDetail.transferPlanningError': 'Error al actualizar/crear la entrada del calendario antes del traspaso.',
    'ticketDetail.transferHint': 'Si cambia las horas, su entrada del calendario se actualiza/crea antes del traspaso.',
    'ticketDetail.sourceAssignee': 'Persona de origen',
    'ticketDetail.usedPastTimeQuestion': '¿Se utilizó el tiempo pasado planificado?',
    'ticketDetail.usedPastTimeHint': 'Si responde no, las entradas de calendario pasadas de este ticket se eliminan de su calendario.',
    'ticketSidebar.pool': 'Pool',
    'ticketSidebar.poolTooltip': 'Mostrar solo tickets del pool',
    'search.placeholder': 'Frase completa para búsqueda con IA, palabras clave para búsqueda clásica',
    'search.aiDisabled': 'La búsqueda con IA está desactivada. Un administrador puede activarla en el panel de administración (pestaña «AI Integration»).',
    'search.sections.withTicket': 'Contenido encontrado mediante búsqueda clásica por palabras clave',
    'search.sections.emailsOnly': 'Correos encontrados sin referencia a ticket',
    'search.sections.teamsOnly': 'Mensajes de Teams encontrados sin referencia a ticket',
    'search.loading': 'Buscando…',
    'search.noResults': 'Sin resultados',
    'search.noAnswer': 'No se recibió respuesta.',
    'search.noContext': 'Este historial no admite preguntas de seguimiento.',
    'search.followupFailed': 'La pregunta de seguimiento falló.',
    'search.newSearch': 'Nueva búsqueda',
    'search.history': 'Historial',
    'search.historyEmpty': 'Aún no hay conversaciones guardadas.',
    'search.historyMessages': '{{count}} mensajes',
    'search.historyDelete': 'Eliminar conversación',
    'search.historyDeleteConfirm': '¿Eliminar esta conversación?',
    'search.deleteFailed': 'Error al eliminar.',
    'search.loadFailed': 'No se pudo cargar la conversación.',
    'search.saveConversation': 'Guardar conversación',
    'search.savedToast': 'Conversación guardada.',
    'search.saveFailed': 'Error al guardar.',
    'search.attachToTicket': 'Adjuntar al ticket',
    'search.attachAction': 'Adjuntar',
    'search.attachSelectTicket': 'Seleccionar ticket',
    'search.attachHint': 'La conversación se guardará como comentario interno en el ticket seleccionado.',
    'search.attachedToast': 'Guardado como comentario interno en el ticket.',
    'search.attachFailed': 'Error al adjuntar (¿permiso insuficiente?).',
    'search.attachCommentTitle': 'Búsqueda IA – conversación',
    'search.roleQuestion': 'Pregunta',
    'search.roleAnswer': 'Respuesta',
    'search.hits': 'Resultados',
    'search.emptyHint': 'Haz una pregunta: la IA busca en tickets, comentarios y correos.',
    'ticketDetail.aiSimilar': 'Incidentes similares (búsqueda IA)',
    'aiSimilar.title': 'Incidentes similares',
    'aiSimilar.similarTickets': 'Tickets encontrados',
    'aiSimilar.adopt': 'Añadir al ticket',
    'aiSimilar.adopted': 'Guardado como comentario interno.',
    'aiSimilar.adoptFailed': 'Error al añadir.',
    'aiSimilar.commentTitle': 'Búsqueda IA: incidentes similares',
    'aiSimilar.failed': 'La búsqueda IA falló.',
    'header.notifications': 'Notificaciones',
    'header.activities': 'Actividades',
    'header.noNewActivities': 'No hay actividades nuevas',
    'header.activityNewTicket': 'Ticket nuevo',
    'header.activityComment': 'Comentario nuevo',
    'header.activityEmail': 'Correo recibido',
    'header.activityAttachment': 'Adjunto nuevo',
    'header.activityGeneric': 'Actividad nueva',
    'header.assignedToYou': 'Asignado a ti',
    'header.unassigned': 'Sin asignar',
    'header.user': 'Usuario',
    'header.admin': 'Administración',
    'header.enable': 'Activar',
    'header.enableNotifications': 'Activar notificaciones de escritorio',
    'header.enableNotificationsShort': 'Activar notificaciones',
    'header.notificationsBlocked': '¿Notificaciones permitidas en el navegador? (actualmente bloqueadas)',
    'header.logout': 'Cerrar sesión',
    'header.options': 'Opciones',
    'header.customers': 'Gestión de clientes',
    'customersPage.title': 'Gestión de clientes',
    'customersPage.disabled': 'La función de clientes está desactivada en este sistema.',
    'customersPage.forbidden': 'La gestión de clientes está reservada a los managers.',
    'header.whatsNew': '¿Qué hay de nuevo?',
    'header.menu': 'Menú',
    'ticketCard.justNow': 'Ahora mismo',
    'ticketCard.minutesAgo': 'hace {{count}} min',
    'ticketCard.hoursAgo': 'hace {{count}} h',
    'ticketCard.daysAgo': 'hace {{count}} días',
    'ticketCard.newActivity': 'Nueva actividad',
    'ticketCard.projectTicket': 'Ticket de expediente',
    'contextMenu.open': 'Abrir',
    'contextMenu.findInCalendar': 'Buscar en el calendario',
    'contextMenu.mergeInto': 'Añadir a otro ticket',
    'contextMenu.assignToMe': 'Asignármelo',
    'contextMenu.unassign': 'Quitar asignación',
    'contextMenu.assignError': 'Error al asignar el ticket.',
    'contextMenu.unassignError': 'Error al quitar la asignación.',
    'contextMenu.moveNextWeek': 'Mover a la próxima semana',
    'contextMenu.markBooked': 'Marcar como reservado',
    'contextMenu.unmarkBooked': 'Ya no reservado',
    'contextMenu.bookedSet': 'Marcado como reservado.',
    'contextMenu.bookedRemoved': 'Marca «Reservado» eliminada.',
    'contextMenu.bookedError': 'No se pudo guardar «Reservado».',
    'contextMenu.movingNextWeek': 'Moviendo a la próxima semana...',
    'contextMenu.movedNextWeek': 'Movido a {{date}}',
    'contextMenu.moveNextWeekError': 'Error al mover a la próxima semana.',
    'app.updateAvailable': 'Hay una nueva versión de la aplicación disponible.',
    'app.reloadNow': 'Actualizar ahora',
    'app.offline': 'Sin conexión — los datos mostrados pueden estar desactualizados.',
    'app.degradedStale': 'Sin conexión con el servidor — se muestran los datos del {time}.',
    'app.degradedOffline': 'Sin conexión con el servidor — algunas áreas no se pudieron cargar.',
    'header.poolMonitor': 'Monitor del pool',
    'pool.title': 'Monitor del pool',
    'pool.loading': 'Cargando el pool…',
    'pool.empty': 'No hay tickets abiertos en el pool. 🎉',
    'pool.lastUpdated': 'Actualizado',
    'pool.justNow': 'Ahora mismo',
    'pool.minutesAgo': 'hace {{count}} min',
    'pool.hoursAgo': 'hace {{count}} h',
    'pool.daysAgo': 'hace {{count}} días',
    'dashboard.expandPool': 'Abrir el pool como vista de monitor',
    'dashboard.field.attachments': 'Adjuntos',
    'dashboard.addAttachment': 'Añadir archivo/foto',
    'dashboard.removeAttachment': 'Quitar adjunto',
    'dashboard.attachmentUploadError': 'El ticket se creó, pero no se pudieron subir los adjuntos.',
    'ticketDetail.ticketUpdateError': 'Error al actualizar el ticket.',
    'header.myApiKeys': 'Mis claves API',
    'personalKeys.title': 'Mis claves API',
    'personalKeys.intro': 'Claves Bearer para tus propios scripts y agentes. Una clave tiene exactamente tus permisos de usuario y se muestra solo una vez.',
    'personalKeys.namePlaceholder': 'p. ej. script de calendario',
    'personalKeys.none': 'Aún no hay claves API personales.',
    'personalKeys.loadError': 'Error al cargar las claves API.',
    'personalKeys.createError': 'Error al crear la clave API.',
    'releaseNotes.historyTitle': '¿Qué hay de nuevo?',
    'releaseNotes.versionLabel': 'Versión',
    'releaseNotes.current': 'actual',
    'releaseNotes.loadError': 'No se pudo cargar el historial de versiones.',
    'releaseNotes.empty': 'Aún no hay notas de versión.',
    'header.login': 'Iniciar sesión',
    'header.setup': 'Configuración',
    'header.projectPlanningDisabled': 'Deshabilitado en la planificación del proyecto',
    'header.backToProject': 'Volver al proyecto',
    'header.projectPlanningFor': 'Planificación para el proyecto',
    'header.notice': 'Aviso:',
    'header.dropHereNewTicket': 'Suelta aquí para nuevo ticket',
    'header.unreadTitle': '{{count}} tickets/actividades sin leer',
    'header.notificationsEnabled': 'Notificaciones activadas',
    'header.notificationsEnabledBody': 'Ahora recibirás notificaciones de escritorio.',
    'common.loading': 'Cargando...',
    'ticketSidebar.toPlan': 'Tickets por planificar',
    'ticketSidebar.mine': 'Mis',
    'ticketSidebar.mineAndOpen': 'Míos+Abiertos',
    'ticketSidebar.onlyMineTooltip': 'Mostrar solo tickets asignados a mí',
    'ticketSidebar.mineAndOpenTooltip': 'Mostrar mis asignados y abiertos',
    'ticketSidebar.searchPlaceholder': 'Buscar tickets...',
    'ticketSidebar.seqBadge': 'Número en el proyecto',
    'ticketSidebar.loadingTickets': 'Cargando tickets...',
    'ticketSidebar.noPlanTickets': 'No hay tickets por planificar',
    'ticketSidebar.tryAnotherSearch': 'Prueba otra búsqueda',
    'ticketSidebar.expand': 'Expandir barra lateral',
    'ticketSidebar.collapse': 'Contraer barra lateral',
    'ticketSidebar.newTicket': 'Crear nuevo ticket',
    'ticketView.byUserHeader': 'Tickets - Vista por usuarios',
    'ticketView.includeCompleted': 'Incluir completados',

    // Filtros
    'filters.keywordFilter': 'Filtro de palabras clave',
    'filters.keywordFilterPlaceholder': 'Introduce una palabra por línea...\nEj.:\nError\nFunción\nSoporte',
    'filters.keywordFilterHelp': 'Se filtrarán los tickets que contengan estas palabras clave en el título, la descripción o la categoría. Introduce una palabra por línea.',
    'filters.activeFilters': 'Filtros activos:',
    'filters.keywordsCount': '{{count}} palabra(s) filtrada(s)',
    'filters.noneActive': 'No hay filtros activos',
    'filters.hideReadEmails': 'Ocultar correos leídos',
    'filters.hideReadEmailsHelp': 'Si está activado, los correos ya leídos no se mostrarán en la lista.',
    'filters.onlyLastHours': 'Mostrar solo correos de las últimas horas',
    'filters.numHours': 'Número de horas:',
    'filters.onlyLastHoursHelp': 'Muestra solo correos de las últimas {{hours}} horas.',
    'filters.keywordEmailHelp': 'Se filtrarán los correos que contengan estas palabras clave en el remitente o el asunto. Introduce una palabra por línea.',
    'filters.hideReadEmailsActive': 'Se ocultan los correos leídos',
    'filters.onlyLastHoursActive': 'Solo correos de las últimas {{hours}} horas',

    // Dashboard
    'dashboard.loading': 'Cargando datos...',
    'dashboard.todayEvents': 'Eventos de hoy',
    'mobile.settings': 'Ajustes',
    'mobile.language': 'Idioma',
    'mobile.sendFeedback': 'Enviar comentarios',
    'mobile.confirmTakeOver': '¿Asignarte este ticket?',
    'mobile.eventNotes': 'Notas',
    'mobile.aiQuestion': 'Pregunta para la IA',
    'mobile.newSearch': 'Nueva búsqueda',
    'mobile.aiSearching': 'Buscando…',
    'mobile.aiFoundTickets': 'Tickets encontrados',
    'mobile.aiSearchError': 'La búsqueda no funcionó. Inténtalo de nuevo.',
    'mobile.aiSearchDisabled': 'La búsqueda con IA está desactivada en esta instalación.',
    'mobile.editComment': 'Editar comentario',
    'mobile.deleteImage': 'Eliminar imagen',
    'mobile.imageDeleted': 'Imagen eliminada.',
    'mobile.image': 'Imagen',
    'mobile.clearSearch': 'Borrar búsqueda',
    'mobile.searchMine': 'Buscar en mis tickets',
    'mobile.searchPool': 'Buscar en tickets del pool',
    'mobile.tabToday': 'Hoy',
    'mobile.tabMine': 'Míos',
    'mobile.tabPool': 'Pool',
    'mobile.tabsLabel': 'Secciones',
    'mobile.refresh': 'Actualizar',
    'mobile.ticket': 'Ticket',
    'mobile.back': 'Atrás',
    'mobile.archived': 'Archivado',
    'mobile.poolTicket': 'Ticket del pool, sin asignar',
    'mobile.actions': 'Acciones',
    'mobile.takePhoto': 'Foto',
    'mobile.chooseFiles': 'Archivos',
    'mobile.uploading': 'Subiendo…',
    'mobile.takeOver': 'Asumir',
    'mobile.newComment': 'Comentario',
    'mobile.commentPlaceholder': '¿Qué has hecho o detectado?',
    'mobile.send': 'Enviar',
    'mobile.sending': 'Enviando…',
    'mobile.showMore': 'Mostrar más',
    'mobile.showLess': 'Mostrar menos',
    'mobile.history': 'Historial',
    'mobile.historyEmpty': 'Aún no hay entradas.',
    'mobile.ticketForbidden': 'No puedes ver este ticket.',
    'mobile.ticketLoadError': 'No se pudo cargar el ticket.',
    'mobile.commentSaved': 'Comentario guardado.',
    'mobile.commentError': 'No se pudo guardar el comentario.',
    'mobile.uploadTooMany': 'Como máximo {{count}} archivos a la vez.',
    'ticketCard.hasAttachments': 'Tiene adjuntos',
    'mobile.openEmail': 'Abrir correo: {{subject}}',
    'mobile.openAndReply': 'Abrir y responder',
    'email.reply.popupBlocked': 'El borrador está listo en Outlook, pero no se pudo abrir aquí.',
    'ticketDetail.mailAttachments': 'Adjuntos del correo',
    'viewer.title': 'Adjunto',
    'viewer.page': 'Página {{n}} de {{total}}',
    'viewer.zoomIn': 'Ampliar',
    'viewer.zoomOut': 'Reducir',
    'viewer.zoomReset': 'Ajustar',
    'viewer.delete': 'Eliminar adjunto',
    'viewer.loadError': 'No se pudo cargar el archivo.',
    'viewer.noPreview': 'No hay vista previa para este archivo.',
    'viewer.prev': 'Adjunto anterior',
    'viewer.next': 'Adjunto siguiente',
    'ticketDetail.view': 'Ver',
    'mobile.uploadTooLarge': '«{{name}}» supera los 10 MB.',
    'mobile.uploadDoneOne': 'Archivo subido.',
    'mobile.uploadDoneMany': '{{count}} archivos subidos.',
    'mobile.uploadError': 'La subida ha fallado.',
    'mobile.takenOver': 'El ticket ahora está asignado a ti.',
    'mobile.attachmentOpenError': 'No se pudo abrir el archivo.',
    'mobile.installTitle': '¿Instalar como app?',
    'mobile.installAndroidText': 'Tickets se abrirá como una app desde la pantalla de inicio.',
    'mobile.installIosBefore': 'Toca',
    'mobile.installIosShare': 'Compartir',
    'mobile.installIosAfter': 'y luego «Añadir a pantalla de inicio».',
    'mobile.installButton': 'Instalar',
    'mobile.installLater': 'Más tarde',
    'mobile.installGotIt': 'Entendido',
    'dashboard.noUpcomingEvents': 'No hay eventos próximos',
    'dashboard.myTickets': 'Mis tickets',
    'dashboard.tickets': 'Tickets',
    'dashboard.emails': 'Correos',
    'dashboard.teams': 'Teams',
    'dashboard.hiddenSectionsLabel': 'Secciones ocultas:',
    'dashboard.showEmails': 'Mostrar correos',
    'dashboard.showTeams': 'Mostrar Teams',
    'dashboard.hideEmails': 'Ocultar correos',
    'dashboard.hideTeams': 'Ocultar Teams',
    'dashboard.configureTicketFilter': 'Configurar filtro de tickets',
    'dashboard.configureEmailFilter': 'Configurar filtro de correos',
    'dashboard.configureEmailSection': 'Configurar sección de correo',
    'filters.additionalMailboxes': 'Buzones adicionales',
    'filters.additionalMailboxPlaceholder': 'Introduzca dirección de correo...',
    'filters.addMailbox': 'Añadir buzón',
    'filters.removeMailbox': 'Eliminar buzón',
    'filters.additionalMailboxesHelp': 'Añada buzones a los que tenga acceso total. Sus correos se incluirán en la sección.',
    'filters.additionalMailboxesActive': '{{count}} buzón(es) adicional(es)',
    'dashboard.configureTeamsFilter': 'Configurar filtro de Teams',
    'dashboard.freeTime': 'Tiempo libre',
    'dashboard.searchTicketsPlaceholder': 'Buscar tickets...',
    'dashboard.searchMailsPlaceholder': 'Buscar correos...',
    'dashboard.searching': 'Buscando…',
    'dashboard.retry': 'Reintentar',
    'dashboard.connecting': 'Conectando...',
    'dashboard.serviceIssues': 'Problemas de conectividad del servicio',
    'dashboard.autoUpdatePausedFor': 'Actualización automática en pausa para:',
    'dashboard.sessionMayHaveExpired': 'Su sesión puede haber expirado.',
    'dashboard.createTicketHeader': 'Crear nuevo ticket',
    'dashboard.dragDropHint': 'Arrastra y suelta archivos aquí',
    'dashboard.attachmentsSuffix': 'adjuntos',
    'dashboard.field.title': 'Título',
    'dashboard.field.description': 'Descripción',
    'dashboard.field.priority': 'Prioridad',
    'dashboard.field.assignedTo': 'Asignado a',
    'dashboard.field.category': 'Categoría',
    'dashboard.placeholder.title': 'Introduzca el título del ticket...',
    'dashboard.placeholder.description': 'Introduzca la descripción del ticket...',
    'dashboard.placeholder.category': 'p. ej. Soporte, Error, Función',
    'dashboard.unassigned': 'Sin asignar',
    'dashboard.cancel': 'Cancelar',
    'dashboard.createTicket': 'Crear ticket',
    'dashboard.enterTitle': 'Por favor, introduzca un título.',
    'dashboard.createTicketError': 'Error al crear el ticket.',
    'dashboard.saveEmailFilterError': 'Error al guardar la configuración del filtro de correos.',
    'dashboard.saveTeamsFilterError': 'Error al guardar la configuración del filtro de Teams.',
    'dashboard.saveTicketFilterError': 'Error al guardar la configuración del filtro de tickets.',
    'dashboard.errorLoadData': 'Error al cargar los datos. Inténtelo de nuevo más tarde.',
    'dashboard.refreshError': 'Error al actualizar los datos',

    // Calendar
    'calendar.loading': 'Cargando eventos...',
    'calendar.previousWeek': 'Semana anterior',
    'calendar.today': 'Hoy',
    'calendar.nextWeek': 'Próxima semana',
    'calendar.backToWeekTooltip': 'Volver a la vista semanal',
    'calendar.show14DaysTooltip': 'Mostrar 14 días',
    'calendar.week': 'Semana',
    'calendar.14days': '14 días',
    'calendar.updateError': 'Error al actualizar los eventos',
    'calendar.planNewTicketError': 'Error al planificar el ticket recién creado',
    'calendar.createTicketError': 'Error al crear el ticket.',
    'calendar.removePlanningError': 'Error al eliminar el ticket de la planificación',
    'calendar.changeDurationError': 'Error al cambiar la duración del ticket',
    'calendar.placeTicketError': 'Error al colocar el ticket',
    'calendar.newTicket': 'Crear nuevo ticket',

    // Global
    'global.loading': 'Cargando calendario...',
    'global.errorLoad': 'Error al cargar los datos del calendario. Inténtelo de nuevo más tarde.',
    'global.refreshError': 'Error al actualizar el calendario global',
    'global.permissionDenied': '⚠️ Sin permiso: No puede crear entradas de calendario para {{userEmail}}. Se requieren permisos ampliados.',
    'global.userNotFound': '⚠️ Usuario {{userEmail}} no encontrado o calendario no disponible.',
    'global.moveTicketError': 'Error al mover el ticket: {{details}}',
    'global.planNewTicketError': 'Error al planificar el ticket recién creado',
    'global.createTicketError': 'Error al crear el ticket.',
    'global.removePlanningError': 'Error al eliminar el ticket de la planificación',
    'global.changeDurationError': 'Error al cambiar la duración del ticket',
    'global.placeTicketError': 'Error al colocar el ticket',
    'global.users': 'Usuarios',
    'global.onlyTodayTooltip': 'Mostrar solo el día actual',
    'global.weekTooltip': 'Mostrar semana actual (Lun–Vie)',
    'global.weekendTooltip': 'Mostrar semana actual incluido fin de semana (Lun–Dom)',
    'global.noUsersFound': 'No se encontraron usuarios',
    'global.checkConsoleOrContactAdmin': 'Consulte la consola para más detalles o contacte al administrador.',
    'global.time': 'Hora',
    'global.pleaseWait': 'Por favor espera:',
    'global.showEarlyAppointments': 'Mostrar citas tempranas',
    'global.expandTo1am': 'Extender hasta la 1 h',
    'global.showLateAppointments': 'Mostrar citas tardías',
    'global.expandTo11pm': 'Extender hasta las 23 h',
    'global.noSubject': '(Sin asunto)',
    'global.view.dayLabel': 'DÍA',
    'global.view.weekLabel': 'SEMANA',
    'global.view.weekendLabel': 'SEMANA+FS',
    'global.openShort': 'Abiertos',
    'global.removeFromPlanning': 'Eliminar de la planificación',
    'global.ticketLabel': 'Ticket:',
    'global.tooltipResizeHint': 'Consejo: Arrastre los bordes para cambiar la duración',
    'global.movingTicket': 'Moviendo ticket...',
    'global.removingFromPlanning': 'Eliminando ticket de la planificación...',
    'global.changingTicketDuration': 'Cambiando la duración del ticket...',
    'global.at': 'a las',

    // Email (faltend gegenüber en)
    'email.read': 'Leído',
    'email.unread': 'No leído',
    'email.markAsUnread': 'Marcar como no leído',
    'email.markAsRead': 'Marcar como leído',
    'email.detailTitle': 'Detalles del correo',
    'email.detail.content': 'Contenido',
    'email.detail.imagesLoading': ' (Cargando imágenes...)',
    'email.detail.from': 'De:',
    'email.detail.to': 'Para:',
    'email.detail.received': 'Recibido:',
    'email.subject.none': '(Sin asunto)',
    'email.flagged': 'Marcado',
    'email.importance.high': 'Alta',
    'email.importance.normal': 'Normal',
    'email.importance.low': 'Baja',
    'email.body.empty': 'No hay contenido disponible',
    'email.body.frameTitle': 'Contenido del correo',
    'email.body.processing': 'Procesando contenido...',
    'email.body.imageLoadFailed': 'No se pudo cargar la imagen',
    'email.body.unknownFormat': 'Formato de correo desconocido',
    'email.body.imageNotAvailable': '[Imagen no disponible]',
    'email.attachments.title': 'Adjuntos',
    'email.attachments.unknownSize': 'Tamaño desconocido',
    'email.attachments.inlineImages': 'imágenes incrustadas',
    'email.addToTicket.title': 'Añadir correo al ticket',
    'email.addToTicket.modalTitle': 'Añadir correo al ticket',
    'email.addToTicket.button': 'Añadir a un ticket',
    'email.reply.title': 'Responder al correo',
    'email.reply.button': 'Responder',
    'email.reply.createTitle': 'Crear respuesta de correo',
    'email.reply.ticketDecision': 'Este correo no está vinculado aún a un ticket. ¿Crear un nuevo ticket o responder sin ticket?',
    'email.reply.withoutTicket': 'Responder sin ticket',
    'email.reply.createAndRespond': 'Crear ticket y responder',
    'email.reply.sendFromFallback': 'Keine Senden-Als Rechte an {{mailbox}}, sende aus eigenem Konto.',
    'email.preview.title': 'Vista previa del correo:',
    'email.preview.from': 'De:',
    'email.preview.subject': 'Asunto:',
    'email.preview.preview': 'Vista previa:',
    'email.createFromEmail.title': 'Crear nuevo ticket desde correo',

    // Ticket Create/Filter
    'ticket.create.new': 'Crear nuevo ticket:',
    'ticket.create.submit': 'Crear ticket',
    'ticket.create.button': 'Crear nuevo ticket',
    'ticket.addToExisting': 'Añadir a ticket existente:',
    'ticket.filter.placeholder': 'Buscar por número de ticket o título...',
    'ticket.filter.placeholderCustomer': 'Buscar por número de ticket, título, cliente o persona de contacto...',
    'contactSearch.placeholder': 'Buscar contacto…',
    'contactSearch.label': 'Buscar contacto: clientes y personas de contacto',
    'contactSearch.title': 'Buscar contacto',
    'contactSearch.results': 'Resultados de la búsqueda de contactos',
    'contactSearch.minChars': 'Introduzca al menos dos caracteres.',
    'contactSearch.loading': 'Cargando contactos…',
    'contactSearch.none': 'Sin resultados.',
    'contactSearch.moreContacts': '+{{count}} personas de contacto más',
    'contactSearch.moreCustomers': '{{count}} resultados más — afine la búsqueda',
    'contactSearch.openAdmin': 'Abrir en la gestión de clientes',
    'ticket.loading': 'Cargando tickets...',
    'ticket.noneOpen': 'No hay tickets abiertos.',
    'ticket.noneMatchFilter': 'No se encontraron tickets que coincidan con el filtro.',

    // Formularios / Prioridad
    'form.title': 'Título',
    'form.description': 'Descripción',
    'form.priority': 'Prioridad',
    'form.assignedTo': 'Asignado a',
    'form.category': 'Categoría',
    'priority.low': 'Baja',
    'priority.normal': 'Normal',
    'priority.high': 'Alta',
    'priority.critical': 'Crítica',

    // TicketDetail (komplett)
    'ticketDetail.block': 'Bloque de tiempo',
    'ticketDetail.ticket': 'Ticket',
    'ticketDetail.toProject': 'Ir a la operación',
    'ticketDetail.projectLoadError': 'No se pudo cargar la operación.',
    'ticketDetail.edit': 'Editar',
    'ticketDetail.transfer': 'Transferir',
    'ticketDetail.transferTitle': 'Transferir ticket',
    'ticketDetail.complete': 'Completar',
    'ticketDetail.completeTitle': 'Completar ticket',
    'ticketDetail.history': 'Historial',
    'ticketDetail.attach': 'Adjuntar archivos',
    'ticketDetail.newEmail': 'Nueva conversación de correo',
    'ticketDetail.newEmailError': 'No se pudo crear el borrador del correo. Inténtelo de nuevo.',
    'ticketDetail.detachEmailTitle': 'Desvincular correo del ticket',
    'ticketDetail.detachEmailConfirm': '¿Desvincular el correo «{{subject}}» de este ticket? En Outlook no cambia; solo se elimina el vínculo con el ticket.',
    'ticketDetail.detachEmail': 'Desvincular del ticket',
    'ticketDetail.detachEmailDone': 'Correo desvinculado del ticket.',
    'ticketDetail.detachEmailGone': 'Este correo ya no está vinculado a este ticket.',
    'ticketDetail.detachEmailAutomatic': 'Este correo se asignó automáticamente y no se puede desvincular.',
    'ticketDetail.detachEmailForbidden': 'No tiene permiso para modificar este ticket.',
    'ticketDetail.detachEmailError': 'No se pudo desvincular el correo.',
    'ticketDetail.addComment': 'Añadir comentario',
    'ticketDetail.loadingHistory': 'Cargando historial...',
    'ticketDetail.noHistory': 'Sin historial',
    'ticketDetail.download': 'Descargar',
    'ticketDetail.cancel': 'Cancelar',
    'ticketDetail.adding': 'Añadiendo...',
    'ticketDetail.enterComment': 'Introducir nuevo comentario...',
    'ticketDetail.optionalComment': 'Introducir comentario opcional...',
    'ticketDetail.status': 'Estado',
    'ticketDetail.priority': 'Prioridad',
    'ticketDetail.plannedFor': 'Planificado para',
    'ticketDetail.created': 'Creado',
    'ticketDetail.createdBy': 'Creado por',
    'ticketDetail.closedBy': 'Cerrado por',
    'ticketDetail.completeForeignHint': 'Este ticket está asignado a {{assignee}} — aun así se cerrará en tu nombre.',
    'ticketDetail.period': 'Periodo',
    'ticketDetail.filesDragOverlay': 'Arrastra archivos aquí para adjuntarlos',
    'ticketDetail.loadingPreview': '🔄 Cargando vista previa...',
    'ticketDetail.subject': 'Asunto',
    'ticketDetail.from': 'De',
    'ticketDetail.to': 'Para',
    'ticketDetail.received': 'Recibido',
    'ticketDetail.teamsMessage': 'Mensaje de Teams',
    'ticketDetail.chat': 'Chat',
    'ticketDetail.sent': 'Enviado',
    'ticketDetail.description': 'Descripción',
    'ticketDetail.system': 'Sistema',
    'ticketDetail.unknown': 'Desconocido',
    'ticketDetail.emailAdded': 'Correo añadido',
    'ticketDetail.teamsMessageAdded': 'Mensaje de Teams añadido',
    'ticketDetail.fileAdded': 'Archivo añadido',
    'ticketDetail.comment': 'Comentario',
    'ticketDetail.editTicketTitle': 'Editar ticket',
    'ticketDetail.outgoingEmail': 'Correo saliente',
    'ticketDetail.incomingEmail': 'Correo entrante',
    'ticketDetail.unknownEmailDirection': 'Dirección desconocida',
    'ticketDetail.directMessage': 'Mensaje directo',
    'ticketDetail.editCommentTitle': 'Editar comentario',
    'ticketDetail.updateError': 'Error al actualizar el comentario',
    'ticketDetail.confirmDeleteComment': '¿Desea realmente eliminar este comentario?',
    'ticketDetail.deleting': 'Eliminando... ',
    'ticketDetail.deleteError': 'Error al eliminar el comentario',
    'ticketDetail.projectGeneratedTitle': 'Título generado por la operación',
    'ticketDetail.fileTooLarge': 'El archivo "{{name}}" es demasiado grande. Máximo 10MB.',
    'ticketDetail.selectFilesLabel': 'Seleccionar archivos (máx. 10MB por archivo)',
    'ticketDetail.selectedFiles': 'Archivos seleccionados:',
    'ticketDetail.optionalFilesDescription': 'Descripción opcional para los archivos...',
    'ticketDetail.uploading': 'Subiendo...',
    'ticketDetail.uploadFilesCount': 'Subir {{count}} archivo(s)',
    'ticketDetail.pasteFromClipboard': 'Pegar desde el portapapeles',
    'ticketDetail.clipboardNotSupported': 'Este navegador no admite la lectura del portapapeles.',
    'ticketDetail.clipboardEmpty': 'El portapapeles no contiene contenido compatible.',
    'ticketDetail.clipboardReadError': 'Error al leer del portapapeles.',
    'ticketDetail.pasteHint': 'Consejo: puedes pegar contenido directamente con Ctrl+V.',
    'ticketDetail.transferAssignee': 'Persona de destino',
    'ticketDetail.selectTargetPersonError': 'Por favor, seleccione una persona de destino.',

    // Estados de ticket
    'ticket.status.open': 'Abierto',
    'ticket.status.in_progress': 'En curso',
    'ticket.status.waiting': 'En espera',
    'ticket.status.assigned': 'Asignado',
    'ticket.status.completed': 'Completado',
    'ticket.overdueTitle': 'El ticket está vencido',

    // Proyectos
    'projects.newProject': 'Crear nueva operación',
    'projects.addProjectShort': '+ Operación',
    'projects.dueTemplatesCount': '{{count}} elementos de la biblioteca pendientes',
    'projects.selectProject': 'Seleccionar operación',
    'projects.edit': 'Editar',
    'projects.planning': 'Planificación',
    'projects.planningNeeded': 'Planificación necesaria',
    'projects.subprojects': 'Sub-operaciones',
    'projects.lastPlannedAt': 'Última planificación para',
    'projects.neverPlanned': 'Aún sin planificar',
    'projects.lastPlannedTooltip': 'planificado el',
    'projects.lastPlannedBy': 'por',
    'projects.searchSubprojects': 'Buscar sub-operaciones...',
    'projects.timeblocks': 'Bloques de tiempo',
    'projects.searchTimeblocks': 'Buscar bloques de tiempo...',
    'projects.completed': 'Completados',
    'projects.prevMonth': 'Mes anterior',
    'projects.thisMonth': 'Este mes',
    'projects.nextMonth': 'Próximo mes',
    'projects.users': 'Usuarios',
    'projects.selectUsers': 'Seleccionar usuarios',
    'projects.selectAll': 'Seleccionar todo',
    'projects.deselectAll': 'Deseleccionar todo',
    'projects.weekend': 'Fin de semana',
    'projects.dayHoursTooltip': 'Día {{day}}: {{hours}} h',
    'projects.planPrompt.title': '¿Planificar los bloques de tiempo ahora?',
    'projects.planPrompt.message': '¿Desea planificar ahora los bloques de tiempo para "{{name}}" en la vista de calendario?',
    'projects.planPrompt.goToPlanning': 'Sí, ir a la planificación',
    'common.no': 'No',

    // Administración
    'admin.title': 'Administración',
    'admin.refresh': 'Actualizar',
    'admin.loading': 'Cargando datos de administración...',
    'admin.tabs.users': 'Gestión de usuarios',
    'admin.tabs.server': 'Configuración del servidor',
    'admin.tabs.mailbox': 'Supervisión de buzón',
    'admin.tabs.markers': 'Marcadores de calendario',
    'admin.tabs.notifications': 'Notificaciones',
    'admin.tabs.customers': 'Clientes',
    'admin.customers.intro': 'Base de clientes para asignar tickets. Los correos entrantes se asignan automáticamente al cliente por su dominio de correo; la forma corta se muestra en los tickets.',
    'admin.customers.name': 'Nombre del cliente',
    'admin.customers.namePlaceholder': 'p. ej. Krebs Consulting & IT-Services Co KG',
    'admin.customers.shortName': 'Forma corta (visible en tickets)',
    'admin.customers.shortNamePlaceholder': 'p. ej. Krebs IT',
    'admin.customers.domains': 'Dominios de correo (separados por comas)',
    'admin.customers.domainsPlaceholder': 'krebsit.de, krebs-itservices.de',
    'admin.customers.phone1': 'Teléfono 1',
    'admin.customers.phone2': 'Teléfono 2',
    'admin.customers.phone3': 'Teléfono 3',
    'admin.customers.notes': 'Notas',
    'admin.customers.active': 'Activo',
    'admin.customers.save': 'Guardar',
    'admin.customers.add': 'Crear cliente',
    'admin.customers.cancelEdit': 'Cancelar',
    'admin.customers.shortNameCol': 'Forma corta',
    'admin.customers.domainsCol': 'Dominios',
    'admin.customers.phoneCol': 'Teléfono',
    'admin.customers.activeCol': 'Activo',
    'admin.customers.edit': 'Editar',
    'admin.customers.none': 'Aún no hay clientes.',
    'admin.customers.loadError': 'Error al cargar los clientes.',
    'admin.customers.saveError': 'Error al guardar el cliente.',
    'admin.customers.searchPlaceholder': 'Buscar: nombre, forma corta, dominio, teléfono, contacto…',
    'admin.customers.searchLabel': 'Buscar clientes',
    'admin.customers.archiveAction': 'Archivar',
    'admin.customers.reactivateAction': 'Reactivar',
    'admin.customers.archived': 'Cliente archivado.',
    'admin.customers.reactivated': 'Cliente reactivado.',
    'admin.customers.deleteTitle': 'Eliminar cliente',
    'admin.customers.deleteMessage': '¿Eliminar definitivamente el cliente «{{name}}»? Solo es posible mientras ningún ticket, proyecto, contacto o dominio haga referencia a él.',
    'admin.customers.deleted': 'Cliente eliminado.',
    'admin.customers.deleteRefs': 'El cliente todavía está en uso ({{refs}}) — archívelo en su lugar.',
    'admin.customers.deleteError': 'Error al eliminar el cliente.',
    'admin.customers.refsTickets': 'tickets',
    'admin.customers.refsArchived': 'tickets archivados',
    'admin.customers.refsProjects': 'proyectos',
    'admin.customers.refsContacts': 'contactos',
    'admin.customers.refsDomains': 'dominios',
    'admin.customers.similarTitle': 'Posible duplicado',
    'admin.customers.similarMessage': 'Se encontraron clientes similares: {{names}}. ¿Crear de todos modos?',
    'admin.customers.similarConfirm': 'Crear de todos modos',
    'admin.customers.saved': 'Cliente guardado.',
    'admin.contacts.title': 'Personas de contacto',
    'admin.contacts.countCol': 'Contactos',
    'admin.contacts.manage': 'Gestionar contactos',
    'admin.contacts.name': 'Nombre',
    'admin.contacts.phoneMobile': 'Móvil',
    'admin.contacts.phoneLandline': 'Fijo',
    'admin.contacts.email': 'Correo',
    'admin.contacts.notes': 'Notas',
    'admin.contacts.active': 'Activo',
    'admin.contacts.add': 'Añadir',
    'admin.contacts.save': 'Guardar',
    'admin.contacts.cancelEdit': 'Cancelar',
    'admin.contacts.none': 'Aún no hay personas de contacto.',
    'admin.contacts.saved': 'Contacto guardado.',
    'admin.contacts.saveError': 'Error al guardar el contacto.',
    'admin.contacts.archived': 'Contacto archivado.',
    'admin.contacts.reactivated': 'Contacto reactivado.',
    'admin.contacts.deleteTitle': 'Eliminar contacto',
    'admin.contacts.deleteMessage': '¿Eliminar definitivamente el contacto «{{name}}»? Solo es posible mientras ningún ticket haga referencia a él.',
    'admin.contacts.deleted': 'Contacto eliminado.',
    'admin.contacts.deleteRefs': 'El contacto todavía está en uso ({{refs}}) — desactívelo en su lugar.',
    'admin.contacts.deleteError': 'Error al eliminar el contacto.',
    'customerField.contact': 'Persona de contacto',
    'customerField.contactSelect': 'Seleccionar contacto…',
    'customerField.contactNone': '— sin contacto —',
    'customerField.contactNoCustomer': 'Primero elija un cliente',
    'customerField.createNewContact': 'Crear nuevo contacto',
    'customerField.createContactTitle': 'Crear nuevo contacto',
    'customerField.createContactHint': 'El correo y las notas se pueden añadir después en la gestión de clientes.',
    'customerField.createContactError': 'No se pudo crear el contacto.',
    'customerField.numberCopied': 'Número copiado.',
    'customerField.copyNumber': 'Copiar número',
    'customerField.callTitle': 'Llamar — clic derecho copia el número',
    'ticketDetail.customer': 'Cliente',
    'admin.tabs.apiKeys': 'Claves API',
    'admin.apiKeys.intro': 'Claves Bearer para accesos de máquinas (agentes de IA, integraciones). La clave actúa con los permisos del usuario asignado y se muestra solo una vez.',
    'admin.apiKeys.reachNotice': 'Una clave puede leer todo lo que puede leer el usuario asignado — incluidos los contenidos de correo y Teams vinculados a tickets. La vigencia es obligatoria: las claves caducadas no se prorrogan, se emiten de nuevo (rotación).',
    'admin.apiKeys.createdFor': 'Nueva clave para',
    'admin.apiKeys.copy': 'Copiar',
    'admin.apiKeys.showOnce': 'La clave se muestra solo esta vez — ¡guárdala ahora en un lugar seguro!',
    'admin.apiKeys.name': 'Nombre',
    'admin.apiKeys.namePlaceholder': 'p. ej. agente Placetel',
    'admin.apiKeys.lifetime': 'Vigencia',
    'admin.apiKeys.days': 'días',
    'admin.apiKeys.user': 'Actúa como usuario',
    'admin.apiKeys.userSelf': 'Yo mismo',
    'admin.apiKeys.creating': 'Creando…',
    'admin.apiKeys.create': 'Crear clave',
    'admin.apiKeys.prefix': 'Clave',
    'admin.apiKeys.expires': 'Caduca',
    'admin.apiKeys.lastUsed': 'Último uso',
    'admin.apiKeys.status': 'Estado',
    'admin.apiKeys.statusActive': 'activa',
    'admin.apiKeys.statusExpired': 'caducada',
    'admin.apiKeys.statusRevoked': 'revocada',
    'admin.apiKeys.revokedBy': 'Revocada por',
    'admin.apiKeys.revoke': 'Revocar',
    'admin.apiKeys.revokeConfirm': '¿Revocar esta clave API? Las aplicaciones que la usan perderán el acceso inmediatamente.',
    'admin.apiKeys.revoked': 'Clave API revocada.',
    'admin.apiKeys.none': 'Aún no hay claves API.',
    'admin.apiKeys.loadError': 'Error al cargar las claves API.',
    'admin.apiKeys.createError': 'Error al crear la clave API.',
    'admin.apiKeys.revokeError': 'Error al revocar la clave API.',
    'admin.notifications.heading': 'Notificaciones',
    'admin.notifications.desc': 'Controle una barra de aviso global en la parte superior del sitio web. Aparece en naranja con el siguiente texto.',
    'admin.notifications.barLabel': 'Barra de aviso global',
    'admin.notifications.enabled': 'Activado',
    'admin.notifications.message': 'Mensaje',
    'admin.notifications.placeholder': 'Texto para la barra de aviso...',
    'admin.notifications.save': 'Guardar',
    'admin.users.email': 'Correo electrónico',
    'admin.users.name': 'Nombre',
    'admin.users.lastLogin': 'Último acceso',
    'admin.users.admin': 'Admin',
    'admin.users.manager': 'Manager',
    'admin.users.ticketworker': 'Agente de tickets',
    'admin.users.readonly': 'Solo lectura',
    'admin.users.licensed': 'Con licencia',
    'admin.users.active': 'Activo',
    'admin.users.adminTooltip': 'Acceso al área de administración (puramente técnico: usuarios, buzones, IA, claves API). NO otorga derechos sobre tickets — para eso añadir Ticketworker/Manager.',
    'admin.users.managerTooltip': 'Como Ticketworker y además: editar, cerrar y fusionar tickets ajenos, eliminar tickets, editar todos los comentarios, "Todos los tickets"/"Todos los calendarios", planificar para otros, gestión de clientes.',
    'admin.users.ticketworkerTooltip': 'Puede editar y cerrar sus propios tickets (asignados o creados), tomar tickets del pool, traspasar tickets y planificarse a sí mismo. Sin esta marca: solo lectura.',
    'admin.users.readonlyTooltip': 'Cuenta de solo lectura: prevalece sobre TODOS los demás roles — puede ver, no puede cambiar (incluso como manager). Dar feedback y los ajustes de vista personales siguen siendo posibles.',
    'admin.users.licensedTooltip': 'Plaza de licencia: aparece en la selección de asignación de tickets. Sin otros efectos.',
    'admin.users.activeTooltip': 'Puede iniciar sesión. Desactivado = inicio de sesión y todo acceso API bloqueados.',
    'admin.users.none': 'No se encontraron usuarios.',
    'admin.users.systemAdminTooltip': 'Los derechos de administrador del sistema no se pueden eliminar',
    'admin.users.systemAdminBadge': 'Administrador del sistema (no removible)',
    'admin.error.permission': 'Sin permiso para el área de administración.',
    'admin.error.load': 'Error al cargar los datos de administración.',
    'admin.users.roleUpdated': 'Rol de usuario actualizado correctamente.',
    'admin.users.roleUpdateError': 'Error al actualizar el rol del usuario.',
    'admin.server.allowedEmails': 'Direcciones de correo permitidas',
    'admin.server.desc': 'Restrinja el acceso a direcciones de correo o dominios específicos. Deje vacío para todos los usuarios de O365.',
    'admin.server.allowedPlaceholder': 'Ejemplos:\nusuario1@ejemplo.com, usuario2@ejemplo.com\n*@ejemplo.com\nadmin@empresa.com, *@empresa.com',
    'admin.server.show': 'Mostrar',
    'admin.server.hide': 'Ocultar',
    'admin.server.formatting': 'Formato:',
    'admin.server.formatEmail': 'usuario@ejemplo.com - Dirección exacta',
    'admin.server.formatDomain': '*@ejemplo.com - Todas las direcciones del dominio',
    'admin.server.formatMultiple': 'Separe varias direcciones con comas',
    'admin.server.allowedSaved': 'Direcciones permitidas guardadas correctamente.',
    'admin.server.allowedSaveError': 'Error al guardar las direcciones permitidas.',
    'admin.server.allowedLoadError': 'No se pudieron cargar las direcciones permitidas. Guardar está bloqueado para no sobrescribir la lista existente. Vuelva a cargar la página.',
    'admin.server.unsaved': 'Cambios no guardados',
    'admin.mailbox.heading': 'Supervisión de buzón',
    'admin.mailbox.desc': 'Configure buzones para la creación y asignación automática de tickets.',
    'admin.mailbox.noneTitle': 'Aún no hay buzones configurados.',
    'admin.mailbox.noneDesc': 'Haga clic en "Añadir buzón" para habilitar el procesamiento automático de correos.',
    'admin.mailbox.col.address': 'Buzón',
    'admin.mailbox.col.status': 'Estado',
    'admin.mailbox.col.interval': 'Intervalo',
    'admin.mailbox.col.successFolder': 'Carpeta de éxito',
    'admin.mailbox.col.errorFolder': 'Carpeta de errores',
    'admin.mailbox.col.lastProcessed': 'Último procesamiento',
    'admin.mailbox.col.actions': 'Acciones',
    'admin.mailbox.processNowTooltip': 'Procesar ahora',
    'admin.mailbox.deleteTooltip': 'Eliminar supervisión del buzón',
    'admin.mailbox.addButton': 'Añadir buzón',
    'admin.mailbox.addHeading': 'Añadir nuevo buzón',
    'admin.mailbox.addrLabel': 'Dirección del buzón *',
    'admin.mailbox.intervalLabel': 'Intervalo de comprobación (segundos)',
    'admin.mailbox.successFolderLabel': 'Carpeta de éxito',
    'admin.mailbox.errorFolderLabel': 'Carpeta de errores',
    'admin.mailbox.addSubmit': 'Añadir',
    'admin.mailbox.added': 'Supervisión del buzón añadida correctamente.',
    'admin.mailbox.addError': 'Error al añadir la supervisión del buzón.',
    'admin.mailbox.updated': 'Ajustes del buzón actualizados correctamente.',
    'admin.mailbox.updateError': 'Error al actualizar los ajustes del buzón.',
    'admin.mailbox.confirmDelete': '¿Desea eliminar esta supervisión del buzón?',
    'admin.mailbox.deleted': 'Supervisión del buzón eliminada correctamente.',
    'admin.mailbox.deleteError': 'Error al eliminar la supervisión del buzón.',
    'admin.mailbox.processStarted': 'Procesamiento manual iniciado',
    'admin.mailbox.processError': 'Error durante el procesamiento manual',
    'admin.markers.heading': 'Configuración de marcadores de calendario',
    'admin.markers.desc': 'Gestione los marcadores de calendario disponibles para tickets. Estos marcadores pueden ser usados por todos los usuarios.',
    'admin.markers.current': 'Marcadores actuales',
    'admin.markers.none': 'Aún no hay marcadores de calendario configurados.',
    'admin.markers.col.value': 'Valor',
    'admin.markers.col.label': 'Etiqueta',
    'admin.markers.col.icon': 'Icono',
    'admin.markers.col.color': 'Color',
    'admin.markers.col.status': 'Estado',
    'admin.markers.col.sort': 'Orden',
    'admin.markers.col.actions': 'Acciones',
    'admin.markers.addButton': 'Añadir marcador',
    'admin.markers.addHeading': 'Añadir nuevo marcador de calendario',
    'admin.markers.valueLabel': 'Valor (único)',
    'admin.markers.valuePlaceholder': 'p. ej. externo, interno',
    'admin.markers.labelLabel': 'Etiqueta',
    'admin.markers.labelPlaceholder': 'p. ej. Externo, Interno',
    'admin.markers.iconLabel': 'Símbolo (emoji)',
    'admin.markers.colorLabel': 'Color',
    'admin.markers.sortLabel': 'Orden',
    'admin.markers.create': 'Crear marcador',
    'admin.markers.cancel': 'Cancelar',
    'admin.markers.editHeading': 'Editar marcador',
    'admin.markers.valueReadonly': 'Valor (solo lectura)',

    // Comunes faltantes
    'app.loading': 'Cargando la aplicación...',
    'common.pleaseSelect': 'Por favor seleccione',

    'mindmap.completed': 'Completado',
    'mindmap.subprojects': 'Sub-operaciones',
    'mindmap.timeblocks': 'Time blocks',
    'mindmap.project': 'Operación',
    'common.sessionExpired': 'Sesión caducada. Por favor, inicie sesión de nuevo.',
    'calendar.errorLoad': 'Error al cargar los eventos. Inténtelo de nuevo más tarde.',
    'projects.errorLoadUsers': 'Error al cargar los usuarios',
    'common.all': 'Todos',
    'common.none': 'Ninguno',
    'common.paused': '⚠️ En pausa',
    'common.errors': '⚠️ {{count}} errores',
    'common.loadFailed': 'No se pudo cargar',
    'common.retry': 'Intentar de nuevo',
    'teams.list.loading': 'Cargando mensajes de Teams…',
    'teams.list.loadFailed': 'No se pudieron cargar los mensajes de Teams',
    'common.noEntries': 'Aún no hay entradas',
    'common.linkedToTicket': 'Vinculado a un ticket',
    'ticketDetail.totalSize': 'Tamaño total:',
    // #329: Verwaltung — KI-Reiter, Vorlagen, Kalender-Marker, Protokoll
    'admin.templates.saveFailed': 'No se pudieron guardar las plantillas de respuesta.',
    'admin.templates.autoReplyPlaceholder': 'Hemos creado un nuevo ticket con el número [ticketnr] y le responderemos lo antes posible.',
    'admin.templates.discardChanges': 'Descartar cambios',
    'admin.markers.deleteConfirm': '¿Seguro que quiere eliminar este marcador de calendario?',
    'admin.markers.deleteFailed': 'No se pudo eliminar el marcador de calendario.',
    'admin.markers.createFailed': 'No se pudo crear el marcador de calendario.',
    'admin.markers.updateFailed': 'No se pudo actualizar el marcador de calendario.',
    'admin.logs.trigger': 'Origen',
    'admin.logs.empty': 'Sin entradas',
    'admin.ai.saveFailed': 'No se pudo guardar la configuración de IA.',
    'admin.ai.sectionDescription': 'Configure la conexión con un proveedor de IA (OpenAI u OpenRouter). La URL base de OpenAI viene predefinida.',
    'admin.ai.defaultHint': 'Valor predeterminado según lo solicitado; puede ajustarse aquí.',
    'admin.ai.summaryPromptHint': 'Instrucción para el resumen del ticket en la vista de detalle.',
    'admin.ai.model': 'Modelo',
    'admin.ai.choosePlease': 'Elija…',
    'admin.ai.modelsHint': 'Los modelos se cargan mediante la comprobación de conexión.',
    'admin.ai.mailModeOff': 'Desactivado: usar el texto del correo como hasta ahora',
    'admin.ai.mailModeShadow': 'Sombra: sugerencia como comentario interno',
    'admin.ai.mailModeLive': 'Activo: fijar descripción y prioridad por IA',
    'admin.ai.mailModeHint': 'Procesamiento de los correos entrantes a support@. «Sombra» no cambia nada en el ticket: la sugerencia queda como comentario interno para su revisión.',
    'admin.ai.transcribeModel': 'Modelo de transcripción (buzón de voz)',
    'admin.ai.transcribeHint': 'Modelo con soporte de audio para transcribir buzón de voz. Vacío = predeterminado.',
    'admin.ai.embeddingModel': 'Modelo de embeddings',
    'admin.ai.embeddingProviderHint': 'Proveedor propio para /embeddings: necesario si el chat va por OpenRouter. Vacío = proveedor del chat.',
    'admin.ai.dimensionsPlaceholder': 'Predeterminado del modelo',
    'admin.ai.dimensionsHint': 'p. ej. 1024 ahorra dos tercios de almacenamiento con text-embedding-3-large. Tras un cambio: ¡ejecute el backfill!',
    'admin.ai.backfillRunning': 'Backfill en curso…',
    'admin.ai.backfillStart': 'Iniciar backfill',
    'admin.ai.lastCheck': 'Última comprobación:',
    'admin.tabs.features': 'Funciones',
    'admin.ai.switchesInFeatures': 'Las funciones se activan y desactivan en la pestaña «Funciones».',
    'admin.ai.disabledInFeatures': 'Desactivado en la pestaña «Funciones». Los ajustes se pueden preparar igualmente.',
    'admin.ai.allDisabledInFeatures': 'Todas las funciones de IA están desactivadas en la pestaña «Funciones».',
    'admin.features.title': 'Funciones',
    'admin.features.description': 'Todas las funciones que se pueden activar y desactivar. Cada interruptor guarda al instante. Los ajustes siguen en sus pestañas y aparecen atenuados mientras la función esté desactivada.',
    'admin.features.loadError': 'No se pudieron cargar los interruptores. Pruebe «Actualizar».',
    'admin.features.saved': 'Guardado.',
    'admin.features.saveError': 'No se pudo guardar el interruptor.',
    'admin.features.aiNotReady': 'Aún no hay ningún proveedor de IA configurado (pestaña «AI Integration»: URL base, clave de API, modelo). Hasta entonces las funciones de IA no tienen efecto, aunque estén activadas aquí.',
    'admin.features.inAiTab': 'Ajustes en la pestaña «AI Integration»',
    'admin.features.inCustomersTab': 'Gestión en la pestaña «Clientes»',
    'admin.features.searchAi': 'Búsqueda con IA',
    'admin.features.searchAiDesc': 'Respuesta de IA en la búsqueda global con preguntas de seguimiento y «Tickets similares» en la vista de detalle.',
    'admin.features.summaryAi': 'Resumen con IA',
    'admin.features.summaryAiDesc': 'Botones «Resumir» y «Resumir descripción» en el ticket.',
    'admin.features.mailAi': 'Creación de tickets con IA a partir de correos',
    'admin.features.voicemailAi': 'Tickets de buzón de voz con IA',
    'admin.features.customers': 'Maestro de clientes',
    'admin.features.darstellung': 'Apariencia (predeterminada)',
    'admin.features.darstellungDesc': 'Aspecto para todas las personas que no han elegido una apariencia propia en el engranaje.',
    'admin.server.darstellungSofort': 'La selección se guarda de inmediato.',
    'darstellung.modern': 'Moderno',
    'darstellung.klassisch': 'Clásico',
    'einstellungen.titel': 'Ajustes',
    'einstellungen.darstellung': 'Apariencia',
    'einstellungen.darstellungVorgabe': 'Predeterminada de la instalación',
    'einstellungen.darstellungZurzeit': 'actualmente {name}',
    'einstellungen.sprache': 'Idioma',
    'einstellungen.apiKeysText': 'Claves para programas y agentes que acceden al sistema de tickets con sus permisos.',
    'einstellungen.apiKeysVerwalten': 'Gestionar claves API',
    'einstellungen.speichernFehler': 'No se pudo guardar el ajuste.',
    'einstellungen.kopfleiste': 'Barra superior',
    'einstellungen.reiter': 'Pestañas',
    'einstellungen.reiterFest': 'Página de inicio, siempre visible',
    'einstellungen.reiterAnzeige': 'Visualización de las pestañas',
    'einstellungen.symbolUndText': 'Icono y texto',
    'einstellungen.nurSymbol': 'Solo icono',
    'einstellungen.kontaktsuche': 'Búsqueda de contactos en la barra superior',
    'einstellungen.dashboard': 'Panel',
    'einstellungen.nachOben': 'Subir {{name}}',
    'einstellungen.nachUnten': 'Bajar {{name}}',
    'einstellungen.letzterBereich': 'Al menos una sección permanece visible',
    'dashboard.pool': 'Pool',
    'dashboard.showBereich': 'Mostrar {{name}}',
    'dashboard.alleAusgeblendet': 'Todas las secciones del panel están ocultas.',
    'dashboard.einstellungenOeffnen': 'Abrir ajustes',
    'admin.features.lineLabel': 'Estado en una línea (para el registro de despliegue):',
    'admin.ai.title': 'Integración de IA',
    'admin.ai.groupProvider': 'Conexión con el proveedor',
    'admin.ai.groupProviderHint': 'Se aplica a todas las funciones de IA de abajo.',
    'admin.ai.groupSearch': 'Búsqueda con IA',
    'admin.ai.groupIndex': 'Índice de búsqueda (embeddings)',
    'admin.ai.groupIndexHint': 'Base de los modos de búsqueda «Híbrido» y «Semántico».',
    'admin.ai.groupSummary': 'Resumen con IA',
    'admin.ai.groupVoicemail': 'Buzón de voz',
    'admin.ai.baseUrl': 'URL base de la API',
    'admin.ai.baseUrlExamples': 'Ejemplos: https://api.openai.com/v1, https://openrouter.ai/api/v1',
    'admin.ai.apiKey': 'Clave de API',
    'admin.ai.apiKeyStored': 'Hay una clave de API guardada.',
    'admin.ai.showKey': 'Mostrar',
    'admin.ai.hideKey': 'Ocultar',
    'admin.ai.debug': 'Depuración',
    'admin.ai.testing': 'Probando…',
    'admin.ai.testConnection': 'Probar conexión y cargar modelos',
    'admin.ai.retrievalMode': 'Modo de búsqueda (búsqueda con IA)',
    'admin.ai.retrievalLexical': 'Léxico: como hasta ahora (optimizador + búsqueda de texto)',
    'admin.ai.retrievalHybrid': 'Híbrido: búsqueda de texto + semántica (recomendado tras el backfill)',
    'admin.ai.retrievalVector': 'Semántico: solo índice de embeddings',
    'admin.ai.retrievalHint': 'Active Híbrido/Semántico solo tras un backfill correcto (índice de búsqueda abajo).',
    'admin.ai.searchPrompt': 'Instrucción de búsqueda (predeterminada en inglés)',
    'admin.ai.perQueryFragmentCap': 'Fragmentos por par de búsqueda',
    'admin.ai.globalFragmentCap': 'Límite global de fragmentos',
    'admin.ai.contextCharCap': 'Límite de caracteres del contexto',
    'admin.ai.fullTicketsCount': 'Número de tickets completos',
    'admin.ai.defaultValue': 'Predeterminado: {{wert}}',
    'admin.ai.embeddingModelHint': 'Vacío = búsqueda semántica desactivada. Recomendado: text-embedding-3-large.',
    'admin.ai.embeddingBaseUrl': 'URL base de embeddings (opcional)',
    'admin.ai.embeddingApiKey': 'Clave de API de embeddings (opcional)',
    'admin.ai.embeddingApiKeyPlaceholder': 'sk-… (vacío = clave del chat)',
    'admin.ai.embeddingApiKeyStored': 'Hay una clave de embeddings propia guardada.',
    'admin.ai.dimensions': 'Dimensiones (opcional)',
    'admin.ai.indexStatus': 'Estado del índice',
    'admin.ai.loadStatus': 'Cargar estado',
    'admin.ai.testEmbedding': 'Probar embedding',
    'admin.ai.chunksEmbedded': '{{eingebettet}}/{{gesamt}} fragmentos con embedding',
    'admin.ai.chunksPending': '({{anzahl}} pendientes)',
    'admin.ai.backfillProgress': 'Backfill: {{phase}}, {{quellen}} fuentes, {{eingebettet}} con embedding',
    'admin.ai.backfillLastError': 'último error:',
    'admin.ai.summaryPrompt': 'Instrucción de resumen (predeterminada en inglés)',
    'admin.ai.voicemailToggleHint': 'Transcribir los mensajes del buzón de voz de Placetel y crear un ticket preparado. Si está inactivo, se crea un ticket de correo normal.',
    'admin.ai.transcribePlaceholder': 'google/gemini-3.5-flash-lite (predeterminado)',
    'admin.ai.saveAllHint': 'Guarda todas las secciones a la vez.',
    // #329: Detailansicht einer Teams-Nachricht — vor Sprint 10 ohne jede Uebersetzung
    'teams.detail.title': 'Mensaje de Teams — detalles',
    'teams.detail.from': 'De:',
    'teams.detail.chat': 'Chat:',
    'teams.detail.sent': 'Enviado:',
    'teams.detail.edited': 'Editado:',
    'teams.detail.replyTo': 'En respuesta a:',
    'teams.detail.messageRef': 'Mensaje n.º {{id}}',
    'teams.detail.contentHeading': 'Contenido del mensaje',
    'teams.detail.frameTitle': 'Contenido del mensaje de Teams',
    'teams.detail.subjectFallback': 'Mensaje de Teams',
    'teams.detail.noContent': 'Contenido no disponible',
    'teams.detail.unknownFormat': 'Formato de mensaje desconocido',
    'teams.detail.unknownSender': 'Remitente desconocido',
    'teams.chatType.oneOnOne': 'Mensaje directo',
    'teams.chatType.group': 'Chat de grupo',
    'teams.chatType.unknown': 'Desconocido',
    'teams.importance.high': 'Alta',
    'teams.importance.urgent': 'Urgente',
    'teams.importance.normal': 'Normal',
    'common.noTicketsFound': 'No se encontraron tickets',
    'common.noOpenTickets': 'No hay tickets abiertos',
    'common.tryOtherSearchTerms': 'Pruebe otros términos de búsqueda',
    'ticket.priority.critical': 'Crítica',
    'ticket.priority.high': 'Alta',
    'ticket.priority.normal': 'Normal',
    'ticket.priority.low': 'Baja',
    'common.newActivity': 'Nueva actividad',
    'common.active': 'Activo',
    'common.inactive': 'Inactivo',
    'common.cancel': 'Cancelar',
    'common.close': 'Cerrar',
    'common.ok': 'OK',
    'confirm.title': 'Confirmación',
    'terminAbsage.title': 'Cita con participantes',
    'terminAbsage.message': 'Este ticket tiene una cita con participantes: {{liste}}. Si quita la asignación, la cita se eliminará y los participantes recibirán una cancelación.',
    'terminAbsage.confirm': 'Quitar de todos modos',
    'feedback.buttonTitle': 'Enviar comentarios (informar de un error o deseo)',
    'feedback.title': 'Enviar comentarios',
    'feedback.type': 'Tipo de comentario',
    'feedback.typeBug': 'Error',
    'feedback.typeWish': 'Deseo',
    'feedback.message': 'Descripción',
    'feedback.messagePlaceholder': '¿Qué ha pasado o qué te gustaría? Cuanto más concreto, mejor.',
    'feedback.screenshot': 'Captura de pantalla (opcional)',
    'feedback.contextHint': 'Se envía automáticamente: vista actual ({{url}}), navegador y fecha.',
    'feedback.send': 'Enviar',
    'feedback.sending': 'Enviando…',
    'feedback.thanks': '¡Gracias! Tu comentario ha llegado.',
    'feedback.error': 'No se pudo enviar el comentario.',
    'admin.tabs.feedback': 'Comentarios',
    'admin.feedback.title': 'Comentarios',
    'admin.feedback.intro': 'Comentarios del botón de feedback en la cabecera. Tras transferirlos a un issue, poner el estado en "uebernommen" e introducir el número de issue.',
    'admin.feedback.filter': 'Filtro de estado',
    'admin.feedback.empty': 'No hay entradas para este filtro.',
    'admin.feedback.date': 'Fecha',
    'admin.feedback.type': 'Tipo',
    'admin.feedback.reporter': 'Autor',
    'admin.feedback.message': 'Mensaje',
    'admin.feedback.screenshot': 'Captura',
    'admin.feedback.status': 'Estado',
    'admin.feedback.issue': 'Issue #',
    'admin.feedback.view': 'ver',
    'admin.feedback.notePlaceholder': 'Nota de triaje…',
    'admin.feedback.loadError': 'Error al cargar los comentarios.',
    'admin.feedback.updated': 'Comentario actualizado.',
    'admin.feedback.updateError': 'Error al actualizar el comentario.',
    'admin.feedback.actions': 'Acciones',
    'admin.feedback.deleteTitle': '¿Eliminar comentario?',
    'admin.feedback.deleteConfirm': '¿Eliminar esta entrada definitivamente? Esto no se puede deshacer.',
    'admin.feedback.deleted': 'Comentario eliminado.',
    'admin.feedback.deleteError': 'Error al eliminar el comentario.',
    'ticketDetail.customerNone': '— sin cliente —',
    'ticketDetail.customerSelect': 'Seleccionar cliente…',
    'ticketDetail.customerEdit': 'Asignar cliente',
    'customerField.createNew': 'Crear nuevo cliente',
    'customerField.createTitle': 'Crear nuevo cliente',
    'customerField.createConfirm': 'Crear',
    'customerField.createError': 'No se pudo crear el cliente.',
    'customerField.createHint': 'Los dominios, números de teléfono y otros datos se pueden añadir más tarde en la administración de clientes.',
    'ui.noResults': 'Sin resultados',
    'admin.server.customersFeatureDesc': 'Oculta por completo el campo de cliente en los tickets, la pestaña de administración «Clientes» y la asignación automática de clientes en el correo entrante — para instalaciones sin contexto de clientes. Los datos existentes se conservan.',
    'common.save': 'Guardar',
    'common.saving': 'Guardando...',
    'common.never': 'Nunca',
    'common.edit': 'Editar',
    'common.delete': 'Eliminar',
    'common.hide': 'Ocultar',
    'common.show': 'Mostrar',
    'common.unknown': 'Desconocido',
    'common.notAvailable': 'No disponible',
    'common.opening': 'Abriendo…',
    'common.creating': 'Creando...',
    'common.add': 'Añadir',
    'common.adding': 'Añadiendo...',
    'common.back': 'Atrás',
    'common.unassigned': 'Sin asignar',

    // Email faltante
    'email.detail.title': 'Detalles del correo',
    'teams.addToTicket.modalTitle': 'Añadir mensaje de Teams al ticket'
    , 'welcome.title': 'Bienvenido a KayCee Ticket'
    , 'welcome.lead': 'Accede de forma segura a tus correos y calendarios de Microsoft 365. Esta interfaz te ofrece una experiencia moderna y fácil de usar para tus datos de Office 365.'
    , 'welcome.button.setupStart': '⚙️ Iniciar configuración'
    , 'welcome.button.signInM365': 'Iniciar sesión con Microsoft 365'
    , 'welcome.loadingStatus': 'Cargando estado...'
    , 'welcome.feature.email.title': 'Acceso al correo'
    , 'welcome.feature.email.desc': 'Lee tus correos directamente a través de la API de Microsoft Graph'
    , 'welcome.feature.calendar.title': 'Integración de calendario'
    , 'welcome.feature.calendar.desc': 'Vista general de tus próximas citas y eventos'
    , 'welcome.feature.oauth.title': 'Autenticación OAuth segura'
    , 'welcome.feature.oauth.desc': 'Tus datos permanecen seguros con OAuth 2.0 de Microsoft'
    , 'welcome.feature.ui.title': 'Interfaz de usuario moderna'
    , 'welcome.feature.ui.desc': 'Diseño responsive para escritorio y móvil'
    , 'welcome.setupNotice.title': '⚙️ Se requiere configuración inicial'
    , 'welcome.setupNotice.desc': 'Esta aplicación debe configurarse primero con tu tenant de Microsoft 365. Haz clic en "Iniciar configuración" para una guía interactiva.'
    , 'setup.success': 'La configuración se completó correctamente. Ahora puedes iniciar sesión.'
    , 'setup.errorUnknown': 'Error desconocido durante la prueba de configuración'
    , 'setup.testFailed': 'La prueba de configuración falló: {{message}}'
    , 'email.list.noEmails': 'No se encontraron correos'
    , 'email.list.noneMatchFilters': 'Ningún correo coincide con los filtros activos'
    , 'email.list.adjustFilters': 'Quite filtros o ajuste los criterios de filtrado'
    , 'teamsView.loadError': 'Error al cargar los mensajes de Teams'
    , 'teamsView.noContent': 'Mensaje sin contenido'
    , 'teamsView.unknownSender': 'Remitente desconocido'
    , 'teamsView.loading': 'Cargando mensajes de Teams...'
    , 'teamsView.title': 'Mensajes de Teams'
    , 'teamsView.empty': 'Sin mensajes de Teams'
    , 'teamsView.emptyHint': 'No se encontraron mensajes recientes de Teams.'
    , 'teamsView.count': '{{count}} mensajes (últimos 30)'
    , 'teamsView.justNow': 'ahora mismo'
    , 'teamsView.inChat': 'en {{topic}}'
    , 'email.errors.draftCreate': 'Error al crear el borrador de correo.'
    , 'email.errors.noneSelected': 'Error: ningún correo seleccionado'
    , 'email.errors.replyCreate': 'Error al crear la respuesta de correo: {{details}}'
    , 'email.reply.createErrorGeneric': 'Error al crear la respuesta de correo.'
    , 'email.errors.addToTicket': 'Error al añadir el correo al ticket: {{details}}'
    , 'ticket.createErrorDetails': 'Error al crear el ticket: {{details}}'
    , 'email.filter.invalidAddress': 'Introduzca una dirección de correo válida.'
    , 'teams.errors.noneSelected': 'Error: ningún mensaje de Teams seleccionado'
    , 'teams.errors.addToTicket': 'Error al añadir el mensaje de Teams al ticket: {{details}}'
    , 'eventToTicket.invalidEvent': 'Error: cita no válida.'
    , 'eventToTicket.createPlanError': 'Error al crear/planificar el ticket.'
    , 'projectWizard.saveSubprojectsError': 'Error al guardar las suboperaciones o bloques de tiempo. Detalles en la consola.'
    , 'setup.failed': 'Configuración fallida: {{details}}'
    , 'setup.configError': 'Error de configuración: {{details}}'
    , 'common.unknownError': 'Error desconocido'
    , 'app.sessionExpiredSso': 'Su sesión ha expirado. Se le redirigirá al inicio de sesión.'
    , 'app.sessionExpiredManual': 'Su sesión ha expirado. Vuelva a iniciar sesión.'
    , 'app.loginNow': 'Iniciar sesión ahora'
    , 'setup.subtitle': 'Configure su integración con Microsoft 365'
    , 'setup.clickOn': 'Haga clic en'
    , 'setup.goTo': 'Vaya a'
    , 'setup.choose': 'Seleccione'
    , 'setup.important': 'Importante:'
    , 'setup.new': 'NUEVO:'
    , 'setup.nextStep': 'Continuar al paso {{n}}'
    , 'common.optional': 'opcional'
    , 'common.errorPrefix': 'Error:'
    , 'setup.step1.title': 'Paso 1: Crear el registro de aplicación en Azure'
    , 'setup.step1.intro': 'Cree un nuevo registro de aplicación en Azure:'
    , 'setup.step1.goToPortal': 'Vaya al'
    , 'setup.step1.fillIn': 'Complete la siguiente información:'
    , 'setup.step2.title': 'Paso 2: Crear un secreto de cliente'
    , 'setup.step2.intro': 'Cree un secreto de cliente para su aplicación:'
    , 'setup.step2.description': 'Introduzca una descripción (p. ej. "O365 Interface Secret")'
    , 'setup.step2.expiry': 'Elija un periodo de validez (recomendado: 24 meses)'
    , 'setup.step2.copyNow': 'Copie el secreto de inmediato: solo se muestra una vez.'
    , 'setup.step3.title': 'Paso 3: Configurar permisos de API'
    , 'setup.step3.intro': 'Añada los permisos de API necesarios:'
    , 'setup.step3.addFollowing': 'Añada los siguientes permisos:'
    , 'setup.perm.userRead': 'Leer el perfil de usuario'
    , 'setup.perm.mailRead': 'Leer correos'
    , 'setup.perm.mailReadWrite': 'Editar correos (estado leído/no leído)'
    , 'setup.perm.mailSend': 'Enviar correos'
    , 'setup.perm.calRead': 'Leer calendarios'
    , 'setup.perm.calReadWrite': 'Crear/editar eventos de calendario (calendario propio)'
    , 'setup.perm.calShared': 'Eventos en calendarios compartidos/delegados'
    , 'setup.step3.forAllPermissions': 'para todos los permisos'
    , 'setup.step3.adminConsentRequired': 'El consentimiento del administrador es necesario para el funcionamiento correcto.'
    , 'setup.step3.newFeatures': 'Nuevas funciones disponibles:'
    , 'setup.step3.featureMarkRead': 'Marcar correos como leídos/no leídos'
    , 'setup.step3.featureSend': 'Enviar correos directamente desde la aplicación'
    , 'setup.step4.title': 'Paso 4: Introducir la configuración'
    , 'setup.step4.clientIdHelp': 'La encontrará en la página Overview de su aplicación'
    , 'setup.step4.clientIdPlaceholder': 'p. ej. 12345678-1234-1234-1234-123456789012'
    , 'setup.step4.secretHelp': 'El secreto que acaba de crear'
    , 'setup.step4.secretPlaceholder': 'Su secreto de cliente'
    , 'setup.step4.tenantHelp': 'Déjelo vacío para multi-tenant o introduzca un ID de tenant concreto'
    , 'setup.step4.tenantPlaceholder': 'common (para multi-tenant)'
    , 'setup.step4.sessionHelp': 'Generado automáticamente o introduzca el suyo'
    , 'setup.step4.sessionPlaceholder': 'Generado automáticamente'
    , 'setup.step4.generate': 'Generar'
    , 'setup.step4.allowedEmails': 'Direcciones de correo permitidas'
    , 'setup.step4.allowedEmailsHelp': 'Restrinja el acceso a direcciones o dominios concretos. Déjelo vacío para todos los usuarios de O365.'
    , 'setup.step4.allowedEmailsPlaceholder': 'Ejemplos:\nuser1@example.com, user2@example.com\n*@example.com\nadmin@company.de, *@company.de'
    , 'setup.step4.formatting': 'Formato:'
    , 'setup.step4.fmtExact': 'Dirección de correo exacta'
    , 'setup.step4.fmtDomain': 'Todas las direcciones del dominio'
    , 'setup.step4.fmtComma': 'Separe varias direcciones con comas'
    , 'setup.step4.saving': 'Configurando...'
    , 'setup.step4.save': 'Guardar configuración'
    , 'setup.step5.title': 'Configuración completada'
    , 'setup.step5.saved': 'La configuración se guardó correctamente.'
    , 'setup.step5.testing': 'Probando la configuración...'
    , 'setup.step5.testOk': 'Prueba correcta'
    , 'setup.step5.worksFine': 'La configuración funciona correctamente.'
    , 'setup.step5.finish': 'Finalizar configuración'
    , 'setup.step5.testFailed': 'Prueba fallida'
    , 'setup.step5.retest': 'Probar de nuevo'
    , 'setup.step5.autoTest': 'La configuración se prueba automáticamente...'
    , 'setup.step5.manualTest': 'Probar manualmente'
    , 'setup.info.permissions': 'Permisos necesarios:'
    , 'setup.info.userRead': 'Para leer la información del usuario'
    , 'setup.info.mailRead': 'Para leer correos'
    , 'setup.info.mailReadWrite': 'Para editar correos (leído/no leído)'
    , 'setup.info.mailSend': 'Para enviar correos'
    , 'setup.info.calRead': 'Para leer los eventos del calendario'
    , 'setup.info.calReadWrite': 'Para crear/editar eventos (calendario propio)'
    , 'setup.info.calShared': 'Para crear eventos en calendarios compartidos'
    , 'setup.info.access': 'Control de acceso:'
    , 'setup.info.accessRestrict': 'Se puede restringir a direcciones concretas'
    , 'setup.info.accessWildcard': 'Comodines para dominios completos (*@domain.com)'
    , 'setup.info.accessFlexible': 'Configuración flexible mediante el asistente'
    , 'setup.info.accessEmpty': 'Déjelo vacío para todos los usuarios de O365'
    , 'common.accessDenied': 'Acceso denegado'
    , 'app.offlineQueueSentOne': 'Su comentario escrito sin conexión se ha enviado.'
    , 'app.offlineQueueSentMany': 'Se han enviado {{count}} comentarios escritos sin conexión.'
    , 'app.offlineQueueDropped': 'Un comentario escrito sin conexión no se pudo enviar y se descartó.'
    , 'ticketDetail.commentQueued': 'Sin conexión — el comentario está guardado y se enviará en cuanto vuelva la conexión.'
  }
};

// Kein Auto-Backfill/Auto-Translate: ES-Keys werden manuell gepflegt.

export const I18nProvider = ({ children }) => {
  const [lang, setLang] = useState(() => getCookie(UI_LANGUAGE_COOKIE) || getBrowserLanguage());

  useEffect(() => {
    setCookie(UI_LANGUAGE_COOKIE, lang);
  }, [lang]);

  useEffect(() => {
    try {
      const htmlLang = (lang === 'en' || lang === 'de' || lang === 'es') ? lang : 'en';
      document.documentElement.setAttribute('lang', htmlLang);
    } catch (_) {}
  }, [lang]);

  const t = useCallback((key, fallback, vars) => {
    try {
      const dict = translations[lang] || translations.en;
      let text = dict[key];
      if (!text) {
        text = (translations.en && translations.en[key]) || fallback || key;
      }
      if (vars && typeof text === 'string') {
        Object.keys(vars).forEach(k => {
          text = text.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), String(vars[k]));
        });
      }
      return text;
    } catch (_) {
      return fallback || key;
    }
  }, [lang]);

  const setLanguage = useCallback((nextLang) => {
    setLang(nextLang === 'de' ? 'de' : (nextLang === 'es' ? 'es' : 'en'));
  }, []);

  const getLocale = useCallback(() => (lang === 'en' ? 'en-GB' : (lang === 'de' ? 'de-DE' : (lang === 'es' ? 'es-ES' : 'en-GB'))), [lang]);

  const value = useMemo(() => ({ lang, setLanguage, t, getLocale }), [lang, setLanguage, t, getLocale]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => useContext(I18nContext);


